// src/HexMap.ts
import {
  WebGLRenderer,
  Scene as ThreeScene,
  PerspectiveCamera,
  Color as Color2,
  AmbientLight,
  DirectionalLight,
  Mesh as Mesh3,
  MeshBasicMaterial as MeshBasicMaterial2,
  BufferGeometry as BufferGeometry2,
  BufferAttribute as BufferAttribute3,
  DoubleSide as DoubleSide2,
  MOUSE,
  TOUCH
} from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

// src/EventEmitter.ts
var EventEmitter = class {
  constructor() {
    this.listeners = {};
  }
  on(event, listener) {
    var _a;
    ((_a = this.listeners)[event] || (_a[event] = [])).push(listener);
    return this;
  }
  off(event, listener) {
    if (!this.listeners[event]) return this;
    if (!listener) {
      delete this.listeners[event];
      return this;
    }
    this.listeners[event] = this.listeners[event].filter((l) => l !== listener);
    return this;
  }
  //Removes every listener of every event - for objects being disposed.
  offAll() {
    this.listeners = {};
    return this;
  }
  emit(event, payload) {
    const list = this.listeners[event];
    if (!list || list.length === 0) return;
    for (const listener of list.slice()) {
      listener(payload);
    }
  }
};

// src/helpers/helpers.ts
function getRandomInt(min, max) {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1)) + min;
}
function pointy_hex_corner(center, size, i) {
  let angle_deg = 60 * i;
  let angle_rad = Math.PI / 180 * angle_deg;
  return {
    "x": Math.round(center.x + size * Math.cos(angle_rad)),
    "y": Math.round(center.y + size * Math.sin(angle_rad))
  };
}
function HEXPolygon(center = { x: 0, y: 0 }, size = 1) {
  let arrPoints = [];
  for (let i = 1; i <= 6; i++) {
    arrPoints.push(pointy_hex_corner(center, size, i));
  }
  return arrPoints;
}
function getHexCenter(x, y, size) {
  let space = 0;
  if (x % 2 == 0) {
    space = size * Math.sqrt(3) / 2;
  }
  return { x: x * size * 1.5, y: y * size * Math.sqrt(3) + space };
}
function worldToTile(x, z, size) {
  const q = x / (1.5 * size);
  const r = (z - size * Math.sqrt(3) / 2) / (size * Math.sqrt(3)) - q / 2;
  const cx = q, cz = r, cy = -cx - cz;
  let rx = Math.round(cx), ry = Math.round(cy), rz = Math.round(cz);
  const dx = Math.abs(rx - cx), dy = Math.abs(ry - cy), dz = Math.abs(rz - cz);
  if (dx > dy && dx > dz) rx = -ry - rz;
  else if (dy <= dz) rz = -rx - ry;
  return { x: rx, y: rz + Math.floor((rx + 1) / 2) };
}
function insideConvex(polygon, x, y) {
  let sign = 0;
  for (let i = 0; i < polygon.length; i++) {
    const [ax, ay] = polygon[i], [bx, by] = polygon[(i + 1) % polygon.length];
    const cross = (bx - ax) * (y - ay) - (by - ay) * (x - ax);
    if (cross === 0) return false;
    if (sign === 0) sign = Math.sign(cross);
    else if (Math.sign(cross) !== sign) return false;
  }
  return true;
}

// src/helpers/picking.ts
import { Vector3, Plane, Raycaster, Vector2 } from "three";
var GROUND_PLANE = new Plane(new Vector3(0, 1, 0), 0);
function screenToGround(clientX, clientY, canvas, camera) {
  const rect = canvas.getBoundingClientRect();
  const ndc = new Vector2(
    (clientX - rect.left) / rect.width * 2 - 1,
    -((clientY - rect.top) / rect.height) * 2 + 1
  );
  const raycaster = new Raycaster();
  raycaster.setFromCamera(ndc, camera);
  const point = new Vector3();
  return raycaster.ray.intersectPlane(GROUND_PLANE, point) ? point : null;
}
function pickTile(worldPoint, size, mapWidth, mapHeight) {
  const approxX = worldPoint.x / (size * 1.5);
  const approxY = worldPoint.z / (size * Math.sqrt(3));
  const x0 = Math.floor(approxX);
  const y0 = Math.floor(approxY);
  let best = null;
  let bestDist = Infinity;
  for (let dx = -1; dx <= 1; dx++) {
    for (let dy = -1; dy <= 1; dy++) {
      const x = x0 + dx;
      const y = y0 + dy;
      if (x < 0 || y < 0) continue;
      if (mapWidth !== void 0 && x >= mapWidth) continue;
      if (mapHeight !== void 0 && y >= mapHeight) continue;
      const center = getHexCenter(x, y, size);
      const dist = (center.x - worldPoint.x) ** 2 + (center.y - worldPoint.z) ** 2;
      if (dist < bestDist) {
        bestDist = dist;
        best = { x, y };
      }
    }
  }
  return best;
}

// src/helpers/neighbors.ts
var NEIGHBOR_DIRECTIONS = ["NE", "N", "NW", "SW", "S", "SE"];
function getNeighborCoords(x, y, direction) {
  const odd = x % 2 !== 0;
  switch (direction) {
    case "NE":
      return { x: x + 1, y: odd ? y - 1 : y };
    case "N":
      return { x, y: y - 1 };
    case "NW":
      return { x: x - 1, y: odd ? y - 1 : y };
    case "SW":
      return { x: x - 1, y: odd ? y : y + 1 };
    case "S":
      return { x, y: y + 1 };
    case "SE":
      return { x: x + 1, y: odd ? y : y + 1 };
  }
}
function getNeighbors(x, y) {
  return NEIGHBOR_DIRECTIONS.map((direction) => ({ direction, ...getNeighborCoords(x, y, direction) }));
}

// src/helpers/fog.ts
function tilesWithinRange(map, x, y, range) {
  if (range < 0 || !map.data[x]?.[y]) return [];
  const visited = /* @__PURE__ */ new Set([`${x},${y}`]);
  const result = [{ x, y }];
  let frontier = [{ x, y }];
  for (let step = 0; step < range; step++) {
    const next = [];
    for (const tile of frontier) {
      for (const n of getNeighbors(tile.x, tile.y)) {
        const key3 = `${n.x},${n.y}`;
        if (visited.has(key3)) continue;
        if (!map.data[n.x]?.[n.y]) continue;
        visited.add(key3);
        next.push({ x: n.x, y: n.y });
        result.push({ x: n.x, y: n.y });
      }
    }
    frontier = next;
  }
  return result;
}

// src/objects/FogOfWar.ts
var FogState = /* @__PURE__ */ ((FogState2) => {
  FogState2[FogState2["Unseen"] = 0] = "Unseen";
  FogState2[FogState2["Explored"] = 1] = "Explored";
  FogState2[FogState2["Visible"] = 2] = "Visible";
  return FogState2;
})(FogState || {});
var FogOfWar = class {
  constructor(map) {
    this.map = map;
    this.state = new Uint8Array(map.w * map.h);
  }
  index(x, y) {
    return x * this.map.h + y;
  }
  getState(x, y) {
    return this.state[this.index(x, y)];
  }
  //Every existing tile, at its current state - used once at startup to sync
  //a renderer whose own default (see HexMap.setTileFog()) doesn't necessarily
  //match this class's all-Unseen initial state.
  allTiles() {
    const tiles = [];
    for (let x = 0; x < this.map.w; x++) {
      for (let y = 0; y < this.map.h; y++) {
        if (!this.map.data[x]?.[y]) continue;
        tiles.push({ x, y, state: this.state[this.index(x, y)] });
      }
    }
    return tiles;
  }
  //Recomputes which tiles are currently visible from `viewers` (typically
  //every unit's {x, y, viewRange}) and updates state accordingly: tiles now
  //visible -> Visible; tiles that *were* Visible but no longer are ->
  //Explored (remembered, but dimmed); everything else is untouched (an
  //Unseen tile stays Unseen until it's actually been seen at least once).
  //Returns only the tiles whose state actually changed, so callers can push
  //a cheap incremental update to the renderer instead of touching every tile.
  recompute(viewers) {
    const nowVisible = /* @__PURE__ */ new Set();
    for (const viewer of viewers) {
      for (const tile of tilesWithinRange(this.map, viewer.x, viewer.y, viewer.viewRange)) {
        nowVisible.add(`${tile.x},${tile.y}`);
      }
    }
    const changes = [];
    for (let x = 0; x < this.map.w; x++) {
      for (let y = 0; y < this.map.h; y++) {
        if (!this.map.data[x]?.[y]) continue;
        const idx = this.index(x, y);
        const was = this.state[idx];
        const isVisibleNow = nowVisible.has(`${x},${y}`);
        const next = isVisibleNow ? 2 /* Visible */ : was === 2 /* Visible */ ? 1 /* Explored */ : was;
        if (next !== was) {
          this.state[idx] = next;
          changes.push({ x, y, state: next });
        }
      }
    }
    return changes;
  }
};

// src/objects/YieldOverlay.ts
import {
  CanvasTexture,
  InstancedBufferAttribute,
  InstancedBufferGeometry,
  BufferAttribute,
  LinearFilter,
  LinearMipmapLinearFilter,
  Mesh,
  ShaderMaterial
} from "three";
var CELL = 128;
var PER_ROW = 3;
var DEFAULT_COLOR = "#5b6470";
var YieldOverlay = class extends Mesh {
  constructor(options) {
    const geometry = new InstancedBufferGeometry();
    geometry.setAttribute("position", new BufferAttribute(new Float32Array([
      -0.5,
      -0.5,
      0,
      0.5,
      -0.5,
      0,
      0.5,
      0.5,
      0,
      -0.5,
      0.5,
      0
    ]), 3));
    geometry.setAttribute("uv", new BufferAttribute(new Float32Array([0, 0, 1, 0, 1, 1, 0, 1]), 2));
    geometry.setIndex([0, 1, 2, 0, 2, 3]);
    super(geometry, new ShaderMaterial({
      uniforms: {
        atlas: { value: null },
        iconSize: { value: options.iconSize }
      },
      vertexShader: `
                attribute vec3 iCenter;
                attribute vec2 iOffset;
                attribute vec4 iUv;
                uniform float iconSize;
                varying vec2 vUv;
                void main() {
                    // camera-facing: offset the quad in view space
                    vec4 mv = modelViewMatrix * vec4(iCenter, 1.0);
                    mv.xy += (position.xy + iOffset) * iconSize;
                    gl_Position = projectionMatrix * mv;
                    vUv = iUv.xy + uv * iUv.zw;
                }`,
      fragmentShader: `
                uniform sampler2D atlas;
                varying vec2 vUv;
                void main() {
                    vec4 c = texture2D(atlas, vUv);
                    if (c.a < 0.02) discard;
                    gl_FragColor = c;
                }`,
      transparent: true,
      depthTest: false,
      depthWrite: false
    }));
    this.icons = /* @__PURE__ */ new Map();
    this.order = [];
    this.cells = [];
    this.dirty = false;
    this.atlasKeys = [];
    this.options = options;
    this.frustumCulled = false;
    this.renderOrder = 20;
    this.visible = false;
  }
  //Registers the icons (loads the images). Replaces the previous set.
  async setIcons(defs) {
    const loaded = await Promise.all(defs.map(async (def) => {
      const image = new Image();
      image.src = def.icon;
      try {
        await image.decode();
        return { def, image };
      } catch {
        console.warn(`Resource icon "${def.icon}" (${def.id}) failed to load - drawing its initial instead.`);
        return { def, image: null };
      }
    }));
    this.icons = new Map(loaded.map((i) => [i.def.id, i]));
    this.order = defs.map((d) => d.id);
    this.atlasKeys = [];
    this.markDirty();
  }
  setYields(cells) {
    this.cells = cells;
    this.visible = true;
    this.markDirty();
  }
  hide() {
    this.cells = [];
    this.visible = false;
    this.geometry.instanceCount = 0;
  }
  get shown() {
    return this.visible;
  }
  set iconSize(value) {
    this.material.uniforms.iconSize.value = value;
    this.markDirty();
  }
  get iconSize() {
    return this.material.uniforms.iconSize.value;
  }
  markDirty() {
    this.dirty = true;
  }
  //Called every frame by HexMap; rebuilds only when something changed.
  update() {
    if (!this.dirty || !this.visible) return;
    this.dirty = false;
    this.rebuild();
  }
  rebuild() {
    const { size, heightAt, fogAt } = this.options;
    const iconSize = this.iconSize;
    const items = [];
    const keys = /* @__PURE__ */ new Set();
    for (const cell of this.cells) {
      if (fogAt(cell.x, cell.y) === 0 /* Unseen */) continue;
      const ids = this.order.filter((id) => cell.yields[id] > 0);
      if (ids.length === 0) continue;
      const center2 = getHexCenter(cell.x, cell.y, size);
      const cy = heightAt(center2.x, center2.y) + iconSize * 0.6;
      const rows = Math.ceil(ids.length / PER_ROW);
      ids.forEach((id, i) => {
        const row = Math.floor(i / PER_ROW);
        const inRow = Math.min(PER_ROW, ids.length - row * PER_ROW);
        const key3 = `${id}|${formatAmount(cell.yields[id])}`;
        keys.add(key3);
        items.push({
          cx: center2.x,
          cy,
          cz: center2.y,
          ox: (i % PER_ROW - (inRow - 1) / 2) * 1.05,
          oy: ((rows - 1) / 2 - row) * 1.05,
          key: key3
        });
      });
    }
    this.ensureAtlas([...keys]);
    const cols = Math.ceil(Math.sqrt(this.atlasKeys.length)) || 1;
    const slot = new Map(this.atlasKeys.map((k, i) => [k, i]));
    const center = new Float32Array(items.length * 3);
    const offset = new Float32Array(items.length * 2);
    const uv = new Float32Array(items.length * 4);
    items.forEach((it, i) => {
      center.set([it.cx, it.cy, it.cz], i * 3);
      offset.set([it.ox, it.oy], i * 2);
      const s = slot.get(it.key);
      uv.set([s % cols / cols, (Math.floor(s / cols) + 1) / cols, 1 / cols, -1 / cols], i * 4);
    });
    const g = this.geometry;
    g.setAttribute("iCenter", new InstancedBufferAttribute(center, 3));
    g.setAttribute("iOffset", new InstancedBufferAttribute(offset, 2));
    g.setAttribute("iUv", new InstancedBufferAttribute(uv, 4));
    g.instanceCount = items.length;
  }
  //Redraws the atlas only if the set of (resource, amount) pairs grew.
  ensureAtlas(keys) {
    const known = new Set(this.atlasKeys);
    if (keys.every((k) => known.has(k)) && this.atlasKeys.length > 0) return;
    this.atlasKeys = [.../* @__PURE__ */ new Set([...this.atlasKeys, ...keys])];
    const cols = Math.ceil(Math.sqrt(this.atlasKeys.length)) || 1;
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = cols * CELL;
    const ctx = canvas.getContext("2d");
    this.atlasKeys.forEach((key3, i) => {
      const [id, amount] = key3.split("|");
      this.drawIcon(ctx, i % cols * CELL, Math.floor(i / cols) * CELL, id, amount);
    });
    this.texture?.dispose();
    this.texture = new CanvasTexture(canvas);
    this.texture.minFilter = LinearMipmapLinearFilter;
    this.texture.magFilter = LinearFilter;
    this.texture.flipY = false;
    this.material.uniforms.atlas.value = this.texture;
  }
  drawIcon(ctx, x, y, id, amount) {
    const entry = this.icons.get(id);
    const cx = x + CELL / 2, cy = y + CELL / 2, r = CELL / 2 - 14;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = entry?.def.color ?? DEFAULT_COLOR;
    ctx.fill();
    ctx.lineWidth = 6;
    ctx.strokeStyle = "rgba(20, 16, 12, 0.85)";
    ctx.stroke();
    const glyph = r * 1.25;
    if (entry?.image) {
      ctx.drawImage(entry.image, cx - glyph / 2, cy - glyph / 2, glyph, glyph);
    } else {
      ctx.fillStyle = "#fff";
      ctx.font = `bold ${Math.round(r)}px sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText((id[0] ?? "?").toUpperCase(), cx, cy);
    }
    if (amount !== "1") {
      const br = 30, bx = x + CELL - br - 3, by = y + CELL - br - 3;
      ctx.beginPath();
      ctx.arc(bx, by, br, 0, Math.PI * 2);
      ctx.fillStyle = "#1c1915";
      ctx.fill();
      ctx.lineWidth = 4;
      ctx.strokeStyle = "rgba(255, 255, 255, 0.9)";
      ctx.stroke();
      ctx.fillStyle = "#fff";
      ctx.font = `bold ${amount.length > 2 ? 28 : 40}px sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(amount, bx, by + 2);
    }
  }
  dispose() {
    this.geometry.dispose();
    this.material.dispose();
    this.texture?.dispose();
    this.removeFromParent();
  }
};
function formatAmount(amount) {
  return Number.isInteger(amount) ? String(amount) : amount.toFixed(1);
}

// src/objects/MoveOverlay.ts
import {
  BufferAttribute as BufferAttribute2,
  BufferGeometry,
  CanvasTexture as CanvasTexture2,
  Color,
  DoubleSide,
  Group,
  Mesh as Mesh2,
  MeshBasicMaterial,
  SRGBColorSpace,
  Sprite,
  SpriteMaterial
} from "three";
var EDGE_STEPS = 8;
var FILL_STEPS = 4;
function corner(size, i) {
  const angle = Math.PI / 3 * (i % 6);
  return { x: size * Math.cos(angle), z: size * Math.sin(angle) };
}
function overlayMaterial(color, opacity) {
  return new MeshBasicMaterial({
    color,
    opacity,
    transparent: true,
    side: DoubleSide,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2
  });
}
function geometryOf(positions, indices) {
  const geometry = new BufferGeometry();
  geometry.setAttribute("position", new BufferAttribute2(new Float32Array(positions), 3));
  geometry.setIndex(indices);
  geometry.computeBoundingSphere();
  return geometry;
}
var MoveArea = class extends Group {
  constructor(size, height, color) {
    super();
    this.size = size;
    this.height = height;
    this.fillMaterial = overlayMaterial(color, 0.22);
    this.borderMaterial = overlayMaterial(color, 0.95);
    this.renderOrder = 9;
  }
  show(cells) {
    this.clear();
    if (cells.length === 0) return;
    const inside = new Set(cells.map((c) => `${c.x},${c.y}`));
    const fill = { positions: [], indices: [] };
    const border = { positions: [], indices: [] };
    for (const cell of cells) {
      const center = getHexCenter(cell.x, cell.y, this.size);
      this.addFill(fill, center.x, center.y);
      for (let side = 0; side < 6; side++) {
        const a = corner(this.size, side), b = corner(this.size, side + 1);
        const across = worldToTile(center.x + (a.x + b.x) * 0.75, center.y + (a.z + b.z) * 0.75, this.size);
        if (!inside.has(`${across.x},${across.y}`)) this.addBorder(border, center.x, center.y, side);
      }
    }
    const fillMesh = new Mesh2(geometryOf(fill.positions, fill.indices), this.fillMaterial);
    const borderMesh = new Mesh2(geometryOf(border.positions, border.indices), this.borderMaterial);
    fillMesh.renderOrder = borderMesh.renderOrder = 9;
    this.add(fillMesh, borderMesh);
  }
  hide() {
    this.clear();
  }
  clear() {
    for (const child of this.children) child.geometry.dispose();
    return super.clear();
  }
  dispose() {
    this.clear();
    this.fillMaterial.dispose();
    this.borderMaterial.dispose();
  }
  //The hex as 6 triangles, each split into FILL_STEPS^2 small ones.
  addFill(out, cx, cz) {
    const n = FILL_STEPS;
    for (let side = 0; side < 6; side++) {
      const a = corner(this.size, side), b = corner(this.size, side + 1);
      const base = out.positions.length / 3;
      const row = (i) => i * (i + 1) / 2;
      for (let i = 0; i <= n; i++) {
        for (let j = 0; j <= i; j++) {
          const x = cx + (a.x * (i - j) + b.x * j) / n, z = cz + (a.z * (i - j) + b.z * j) / n;
          out.positions.push(x, this.height(x, z), z);
        }
      }
      for (let i = 0; i < n; i++) {
        for (let j = 0; j <= i; j++) {
          const p = base + row(i) + j, q = base + row(i + 1) + j;
          out.indices.push(p, q, q + 1);
          if (j < i) out.indices.push(p, q + 1, p + 1);
        }
      }
    }
  }
  //A band along the inside of one side of the hex.
  addBorder(out, cx, cz, side) {
    const a = corner(this.size, side), b = corner(this.size, side + 1);
    const outer = 0.97, inner = 0.86;
    const base = out.positions.length / 3;
    for (let s = 0; s <= EDGE_STEPS; s++) {
      const t = s / EDGE_STEPS;
      const x = a.x * (1 - t) + b.x * t, z = a.z * (1 - t) + b.z * t;
      for (const k of [outer, inner]) {
        const wx = cx + x * k, wz = cz + z * k;
        out.positions.push(wx, this.height(wx, wz), wz);
      }
      if (s > 0) {
        const p = base + (s - 1) * 2;
        out.indices.push(p, p + 1, p + 2, p + 1, p + 3, p + 2);
      }
    }
  }
};
var RoutePreview = class extends Group {
  constructor(size, height, color) {
    super();
    this.size = size;
    this.height = height;
    this.color = new Color(color);
    this.material = overlayMaterial(color, 0.9);
    this.renderOrder = 11;
  }
  //turns[i] = turn in which the unit stands on path[i] (GameEngine.routeTurns).
  show(path, turns = []) {
    this.clear();
    if (path.length < 2) return;
    const points = [];
    for (let i = 0; i < path.length - 1; i++) {
      const from = getHexCenter(path[i].x, path[i].y, this.size);
      const to = getHexCenter(path[i + 1].x, path[i + 1].y, this.size);
      for (let s = i === 0 ? 0 : 1; s <= EDGE_STEPS; s++) {
        const t = s / EDGE_STEPS;
        points.push({ x: from.x + (to.x - from.x) * t, z: from.y + (to.y - from.y) * t });
      }
    }
    const band = new Mesh2(this.band(points, this.size * 0.07), this.material);
    band.renderOrder = 11;
    this.add(band);
    for (let i = 1; i < path.length; i++) {
      const last = i === path.length - 1;
      if (turns[i] === void 0 || !last && turns[i + 1] === turns[i]) continue;
      const center = getHexCenter(path[i].x, path[i].y, this.size);
      const badge = this.badge(String(turns[i] + 1));
      badge.position.set(center.x, this.height(center.x, center.y) + this.size * 0.25, center.y);
      this.add(badge);
    }
  }
  hide() {
    this.clear();
  }
  clear() {
    for (const child of this.children) {
      if (child instanceof Sprite) {
        child.material.map?.dispose();
        child.material.dispose();
      } else {
        child.geometry.dispose();
      }
    }
    return super.clear();
  }
  dispose() {
    this.clear();
    this.material.dispose();
  }
  //A flat band of the given width along the points, on the ground.
  band(points, width) {
    const positions = [], indices = [];
    for (let i = 0; i < points.length; i++) {
      const prev = points[Math.max(0, i - 1)], next = points[Math.min(points.length - 1, i + 1)];
      let dx = next.x - prev.x, dz = next.z - prev.z;
      const len = Math.hypot(dx, dz) || 1;
      dx /= len;
      dz /= len;
      const p = points[i];
      for (const side of [1, -1]) {
        const x = p.x - dz * width / 2 * side, z = p.z + dx * width / 2 * side;
        positions.push(x, this.height(x, z), z);
      }
      if (i > 0) {
        const v = (i - 1) * 2;
        indices.push(v, v + 1, v + 2, v + 1, v + 3, v + 2);
      }
    }
    return geometryOf(positions, indices);
  }
  badge(text) {
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = 64;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#" + this.color.getHexString();
    ctx.beginPath();
    ctx.arc(32, 32, 28, 0, Math.PI * 2);
    ctx.fill();
    ctx.lineWidth = 4;
    ctx.strokeStyle = "rgba(0,0,0,0.6)";
    ctx.stroke();
    ctx.fillStyle = "#000";
    ctx.font = "bold 34px sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(text, 32, 34);
    const texture = new CanvasTexture2(canvas);
    texture.colorSpace = SRGBColorSpace;
    const sprite = new Sprite(new SpriteMaterial({ map: texture, depthTest: false, depthWrite: false }));
    sprite.scale.setScalar(this.size * 0.45);
    sprite.renderOrder = 12;
    return sprite;
  }
};

// src/HexMap.ts
var DEFAULT_OPTIONS = {
  size: 40,
  gridVisible: true,
  selectorColor: 16776960,
  pointerColor: 15658734,
  moveAreaColor: 4172287,
  routeColor: 16777215,
  fogDarkenFactor: 0.45
};
var HexMap = class extends EventEmitter {
  constructor(options) {
    super();
    this.mouseDownAt = null;
    // screen coords, used to distinguish click vs. drag
    this.lastHover = null;
    this.lastSelected = null;
    //Authoritative per-tile fog states ("x,y" -> state), owned here rather
    //than only living inside each layer's instanced attributes: those are
    //rebuilt to all-Visible whenever a style rebuilds its layers (a density
    //slider...), and warFogVisible below needs the real states back
    //when fog is re-shown after being hidden.
    this.fogStates = /* @__PURE__ */ new Map();
    this.warFogShown = true;
    this.handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      this.renderer.setSize(width, height);
    };
    this.animate = (t) => {
      const dtS = this.lastFrameTime === void 0 ? 0 : (t - this.lastFrameTime) / 1e3;
      this.lastFrameTime = t;
      this._style.update(dtS);
      this.refreshVisibleMarkers();
      this.yields.update();
      this.emit("frame", { t, dt: dtS });
      this.renderer.render(this.scene, this.camera);
      window.requestAnimationFrame(this.animate);
    };
    //-------------------------------------------------------------------------
    //Picking (analytic, ground-plane based - see helpers/picking.ts)
    //-------------------------------------------------------------------------
    this.onMouseDown = (event) => {
      this.mouseDownAt = { x: event.clientX, y: event.clientY };
    };
    this.onPointerMove = (event) => {
      const ground = screenToGround(event.clientX, event.clientY, this.canvas, this.camera);
      if (!ground) return;
      const tileCoords = pickTile(ground, this.options.size, this.mapData?.w, this.mapData?.h);
      if (!tileCoords) return;
      if (this.lastHover && this.lastHover.x === tileCoords.x && this.lastHover.y === tileCoords.y) return;
      this.lastHover = tileCoords;
      const tile = this.getTile(tileCoords.x, tileCoords.y);
      if (!tile) return;
      this.placeMarker(this.pointer, tileCoords.x, tileCoords.y);
      this.emit("hover", { x: tileCoords.x, y: tileCoords.y, tile });
    };
    this.onMouseUp = (event) => {
      const downAt = this.mouseDownAt;
      this.mouseDownAt = null;
      if (!downAt) return;
      const dragDistance = Math.hypot(event.clientX - downAt.x, event.clientY - downAt.y);
      if (dragDistance > 4) return;
      const ground = screenToGround(event.clientX, event.clientY, this.canvas, this.camera);
      if (!ground) return;
      const tileCoords = pickTile(ground, this.options.size, this.mapData?.w, this.mapData?.h);
      if (!tileCoords) return;
      const tile = this.getTile(tileCoords.x, tileCoords.y);
      if (!tile) return;
      this.selectTile(tileCoords.x, tileCoords.y);
      this.emit("click", { x: tileCoords.x, y: tileCoords.y, tile });
    };
    const { style, ...rest } = options;
    if (!style) throw new Error("HexMap: options.style is required (e.g. new TexturedStyle())");
    this._style = style;
    this.options = {
      ...DEFAULT_OPTIONS,
      ...rest,
      yieldIconSize: options.yieldIconSize ?? (options.size ?? DEFAULT_OPTIONS.size) * 0.45
    };
    const el = document.querySelector(this.options.element);
    if (!(el instanceof HTMLCanvasElement)) {
      throw new Error(`HexMap: element "${this.options.element}" is not a <canvas>`);
    }
    this.canvas = el;
    this.setupScene();
    this.setupCamera();
    this.setupLights();
    this.setupControls();
    this.setupMarkers();
    this.setupYields();
    this.setupEvents();
    this.handleResize();
    this._style.attach({
      scene: this.scene,
      renderer: this.renderer,
      camera: this.camera,
      size: this.options.size,
      fogDarkenFactor: this.options.fogDarkenFactor,
      reapplyFog: () => this.reapplyFog(),
      groundChanged: () => this.yields.markDirty()
    });
    this._style.gridVisible = this.options.gridVisible;
    this.animate(0);
  }
  //-------------------------------------------------------------------------
  //Scene / renderer / camera / controls
  //-------------------------------------------------------------------------
  setupScene() {
    this.scene = new ThreeScene();
    this.scene.background = new Color2(13421772);
    this.renderer = new WebGLRenderer({ canvas: this.canvas, antialias: true });
  }
  setupCamera() {
    this.camera = new PerspectiveCamera(60, 1, 10, 2e3);
    this.camera.position.set(900, 500, 1e3);
    this.scene.add(this.camera);
  }
  setupLights() {
    const dirLight1 = new DirectionalLight(16777215);
    dirLight1.position.set(1, 1, 1);
    this.scene.add(dirLight1);
    const dirLight2 = new DirectionalLight(8840);
    dirLight2.position.set(-1, -1, -1);
    this.scene.add(dirLight2);
    this.scene.add(new AmbientLight(2236962));
  }
  setupControls() {
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.mouseButtons = { LEFT: MOUSE.PAN, MIDDLE: MOUSE.DOLLY, RIGHT: MOUSE.ROTATE };
    this.controls.touches = { ONE: TOUCH.PAN, TWO: TOUCH.DOLLY_ROTATE };
    this.controls.dampingFactor = 0.05;
    this.controls.screenSpacePanning = false;
    this.controls.minDistance = 100;
    this.controls.maxDistance = 800;
    this.controls.minAzimuthAngle = 80 * (Math.PI / 180);
    this.controls.maxAzimuthAngle = 100 * (Math.PI / 180);
    this.controls.minPolarAngle = 10 * (Math.PI / 180);
    this.controls.maxPolarAngle = 90 * (Math.PI / 180);
  }
  //The initial camera position/target (set in setupCamera(), before map data
  //is known) looks at world origin, which is only the map's (0,0) corner, not
  //its middle - most maps would load with the camera pointed off to one side
  //of the actual content. Re-centers the existing look-at *angle* (the
  //direction from target to camera, already tuned via min/maxAzimuth/PolarAngle)
  //on the map's real center instead, at a fixed, in-range viewing distance.
  frameMap(mapData) {
    const size = this.options.size;
    const corner00 = getHexCenter(0, 0, size);
    const cornerWH = getHexCenter(mapData.w - 1, mapData.h - 1, size);
    const centerX = (corner00.x + cornerWH.x) / 2;
    const centerZ = (corner00.y + cornerWH.y) / 2;
    const viewDistance = (this.controls.minDistance + this.controls.maxDistance) / 2;
    const direction = this.camera.position.clone().sub(this.controls.target).normalize();
    this.controls.target.set(centerX, 0, centerZ);
    this.camera.position.copy(this.controls.target).addScaledVector(direction, viewDistance);
    this.controls.update();
  }
  setupMarkers() {
    const size = this.options.size;
    const selectorGeom = this.createMarkerGeometry(size);
    this.selector = new Mesh3(selectorGeom, this.createMarkerMaterial(this.options.selectorColor));
    this.selector.renderOrder = 10;
    this.selector.visible = false;
    this.scene.add(this.selector);
    const pointerGeom = this.createMarkerGeometry(size);
    this.pointer = new Mesh3(pointerGeom, this.createMarkerMaterial(this.options.pointerColor));
    this.pointer.renderOrder = 10;
    this.pointer.visible = false;
    this.scene.add(this.pointer);
    const height = (x, z) => this.overlayHeight(x, z);
    this.moveArea = new MoveArea(size, height, this.options.moveAreaColor);
    this.route = new RoutePreview(size, height, this.options.routeColor);
    this.scene.add(this.moveArea, this.route);
  }
  //Ground under the move area / route preview, a bit above it; on water
  //above the highest waves (the overlay doesn't move with them).
  overlayHeight(worldX, worldZ) {
    return this._style.overlayHeight(worldX, worldZ) + Math.max(0.2, this.options.size * 0.01);
  }
  setupYields() {
    this.yields = new YieldOverlay({
      size: this.options.size,
      iconSize: this.options.yieldIconSize,
      heightAt: (x, z) => this.heightAt(x, z),
      fogAt: (x, y) => this.warFogShown ? this.fogStates.get(`${x},${y}`) ?? 2 /* Visible */ : 2 /* Visible */
    });
    this.scene.add(this.yields);
  }
  createMarkerMaterial(color) {
    return new MeshBasicMaterial2({
      color,
      side: DoubleSide2,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2
    });
  }
  createMarkerGeometry(size) {
    const innerScale = 0.97;
    const segmentsPerSide = 8;
    const perimeterPoints = [];
    const corners = [0, 1, 2, 3, 4, 5].map((i) => {
      const angle = Math.PI / 180 * (60 * i);
      return { x: size * Math.cos(angle), z: size * Math.sin(angle) };
    });
    for (let side = 0; side < corners.length; side++) {
      const a = corners[side];
      const b = corners[(side + 1) % corners.length];
      for (let step = 0; step < segmentsPerSide; step++) {
        const t = step / segmentsPerSide;
        perimeterPoints.push({
          x: a.x * (1 - t) + b.x * t,
          z: a.z * (1 - t) + b.z * t
        });
      }
    }
    const positions = new Float32Array(perimeterPoints.length * 2 * 3);
    const indices = [];
    for (let i = 0; i < perimeterPoints.length; i++) {
      const outer = perimeterPoints[i];
      const inner = { x: outer.x * innerScale, z: outer.z * innerScale };
      const vertex = i * 6;
      positions[vertex] = outer.x;
      positions[vertex + 1] = 0;
      positions[vertex + 2] = outer.z;
      positions[vertex + 3] = inner.x;
      positions[vertex + 4] = 0;
      positions[vertex + 5] = inner.z;
      const next = (i + 1) % perimeterPoints.length;
      const outerA = i * 2;
      const innerA = outerA + 1;
      const outerB = next * 2;
      const innerB = outerB + 1;
      indices.push(outerA, innerA, outerB, innerA, innerB, outerB);
    }
    const geometry = new BufferGeometry2();
    geometry.setAttribute("position", new BufferAttribute3(positions, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    return geometry;
  }
  //The marker ring follows the ground of its tile (vertex by vertex).
  placeMarker(marker, x, y) {
    const center = getHexCenter(x, y, this.options.size);
    marker.visible = true;
    marker.position.set(center.x, 0, center.y);
    const position = marker.geometry.getAttribute("position");
    const lift = Math.max(0.2, this.options.size * 0.01);
    for (let i = 0; i < position.count; i++) {
      position.setY(i, this._style.heightAt(center.x + position.getX(i), center.y + position.getZ(i)) + lift);
    }
    position.needsUpdate = true;
    marker.geometry.computeBoundingSphere();
  }
  setupEvents() {
    window.addEventListener("resize", this.handleResize, { passive: true });
    this.canvas.addEventListener("mousedown", this.onMouseDown);
    window.addEventListener("pointermove", this.onPointerMove);
    window.addEventListener("mouseup", this.onMouseUp);
  }
  //Real cost of a frame at the current view, independent of the monitor's
  //refresh cap: renders `frames` frames back to back, each one waited for
  //on the GPU (a 1-pixel readback), and returns the average ms per frame
  //and the fps that would give. Blocks the page while it runs.
  benchmark(frames = 200) {
    const gl = this.renderer.getContext();
    const pixel = new Uint8Array(4);
    const frame = () => {
      this._style.update(1 / 60);
      this.yields.update();
      this.renderer.render(this.scene, this.camera);
      gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, pixel);
    };
    for (let i = 0; i < 10; i++) frame();
    const start = performance.now();
    for (let i = 0; i < frames; i++) frame();
    const ms = (performance.now() - start) / frames;
    return { ms: Math.round(ms * 100) / 100, fps: Math.round(1e3 / ms) };
  }
  refreshVisibleMarkers() {
    if (this.pointer.visible && this.lastHover) this.placeMarker(this.pointer, this.lastHover.x, this.lastHover.y);
    if (this.selector.visible && this.lastSelected) this.placeMarker(this.selector, this.lastSelected.x, this.lastSelected.y);
  }
  //-------------------------------------------------------------------------
  //Public API
  //-------------------------------------------------------------------------
  //Builds the map: the style draws the ground (see MapStyle.load).
  async load(mapData) {
    this.mapData = mapData;
    this.fogStates.clear();
    this.frameMap(mapData);
    await this._style.load(mapData);
    this.reapplyFog();
    this.yields.markDirty();
    this.emit("load", void 0);
  }
  //What draws the ground - its own live settings are there
  //(map.style.waterWaveAmplitude = 2 for the textured style).
  get style() {
    return this._style;
  }
  //A city or a building stands on the tile: the style clears its trees /
  //decorations (or gives them back).
  setTileCleared(x, y, cleared) {
    this._style.setTileCleared(x, y, cleared);
  }
  getTile(x, y) {
    return this.mapData?.data[x]?.[y];
  }
  //-------------------------------------------------------------------------
  //Fog of war (see objects/FogOfWar.ts) - the style draws one tile in the
  //given state (0 = Unseen, 1 = Explored, 2 = Visible).
  //Every tile defaults to Visible, so calling this is entirely optional; a
  //consumer that wants fog of war (e.g. GameEngine, when its own fogOfWar
  //option is on) drives it from unit positions/view ranges.
  //
  //The state is always recorded in fogStates, even while warFogVisible is
  //false (the layers then just aren't repainted) - so consumers keep feeding
  //fog updates as usual and re-showing the fog repaints everything current.
  //-------------------------------------------------------------------------
  setTileFog(x, y, state) {
    this.fogStates.set(`${x},${y}`, state);
    if (this.warFogShown) this.applyTileFog(x, y, state);
  }
  applyTileFog(x, y, state) {
    this.yields.markDirty();
    this._style.setFog(x, y, state);
  }
  //Repaints every recorded tile: its real state when the fog is shown, or
  //Visible when it's hidden. The style calls it (MapStyleContext.reapplyFog)
  //after any rebuild of its own - fresh meshes start all-Visible.
  reapplyFog() {
    for (const [key3, state] of this.fogStates) {
      const [x, y] = key3.split(",").map(Number);
      this.applyTileFog(x, y, this.warFogShown ? state : 2 /* Visible */);
    }
  }
  //Purely visual show/hide of the war fog: hiding repaints every tile as
  //Visible but keeps the recorded states (and keeps recording new ones from
  //setTileFog), so re-showing restores the current fog exactly. A debug/
  //"reveal map" convenience - it does not touch GameEngine's FogOfWar
  //tracking, unit visibility or pathfinding.
  get warFogVisible() {
    return this.warFogShown;
  }
  set warFogVisible(value) {
    if (this.warFogShown === value) return;
    this.warFogShown = value;
    this.reapplyFog();
  }
  //-------------------------------------------------------------------------
  //Yield overlay (see objects/YieldOverlay.ts): resource icons over tiles,
  //like Civ's yield display. The map only draws - which resources exist and
  //what each tile yields is decided by the game (GameEngine + ResourceRules)
  //and passed in. Tiles under Unseen fog show nothing.
  //-------------------------------------------------------------------------
  //Icons per resource id (image URL + badge color). Icons are shown in
  //this order within a tile.
  setResourceIcons(icons) {
    return this.yields.setIcons(icons);
  }
  //Shows the overlay with these yields (replaces the previous set).
  showYields(cells) {
    this.yields.setYields(cells);
  }
  hideYields() {
    this.yields.hide();
  }
  get yieldsShown() {
    return this.yields.shown;
  }
  //Color multiplier of Explored tiles - the engine dims remembered cities
  //and buildings there by the same amount.
  get fogDarkenFactor() {
    return this.options.fogDarkenFactor;
  }
  get yieldIconSize() {
    return this.yields.iconSize;
  }
  set yieldIconSize(value) {
    this.options.yieldIconSize = value;
    this.yields.iconSize = value;
  }
  get gridVisible() {
    return this._style.gridVisible;
  }
  set gridVisible(value) {
    this.options.gridVisible = value;
    this._style.gridVisible = value;
  }
  selectTile(x, y) {
    this.placeMarker(this.selector, x, y);
    this.lastSelected = { x, y };
  }
  get selectedTile() {
    return this.lastSelected;
  }
  get size() {
    return this.options.size;
  }
  //Route preview from path[0] to its last cell; turns[i] = the turn in
  //which the unit stands on path[i] (GameEngine.routeTurns) - each turn's
  //last cell gets a badge with the turn's number.
  drawRoutePath(path, turns) {
    this.route.show(path, turns);
  }
  cleanRoutePath() {
    this.route.hide();
  }
  //Tints the cells a selected unit can reach this turn and outlines them
  //(GameEngine.reachableTiles).
  showMoveArea(cells) {
    this.moveArea.show(cells);
  }
  clearMoveArea() {
    this.moveArea.hide();
  }
  //Escape hatch for consumers that want to add their own Object3D (units,
  //effects, custom markers) to the map's scene.
  add(object) {
    this.scene.add(object);
  }
  remove(object) {
    this.scene.remove(object);
  }
  //Ground height at a world point, as the style draws it (the textured
  //style: beach slopes, river beds, mountains, the animated water surface).
  //Units use it to follow the relief and ride the waves.
  heightAt(worldX, worldZ) {
    return this._style.heightAt(worldX, worldZ);
  }
  getCamera() {
    return this.camera;
  }
  getScene() {
    return this.scene;
  }
};

// src/helpers/setoptions.ts
function setOptions(obj, options) {
  if (!Object.hasOwn(obj, "options")) {
    obj.options = obj.options ? Object.create(obj.options) : {};
  }
  for (const i in options) {
    obj.options[i] = options[i];
  }
  return obj.options;
}

// src/objects/Unit.ts
import {
  AnimationMixer,
  CanvasTexture as CanvasTexture3,
  CatmullRomCurve3,
  Mesh as Mesh5,
  MeshBasicMaterial as MeshBasicMaterial3,
  Object3D as Object3D3,
  Quaternion as Quaternion3,
  RingGeometry,
  SRGBColorSpace as SRGBColorSpace2,
  Sprite as Sprite2,
  SpriteMaterial as SpriteMaterial2,
  Vector3 as Vector35
} from "three";
import { clone as cloneSkinned } from "three/examples/jsm/utils/SkeletonUtils.js";

// src/helpers/models.ts
import { Group as Group2, Matrix4, Vector3 as Vector33, Quaternion, Euler, MathUtils } from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
var DEFAULT_INFO = {
  offset: { x: 0, y: 0, z: 0 },
  rotation: { x: 0, y: 0, z: 0 },
  scale: 1
};
function loadGLTF(url) {
  return new Promise((resolve, reject) => {
    new GLTFLoader().load(url, (gltf) => resolve({ scene: gltf.scene, animations: gltf.animations }), void 0, reject);
  });
}
async function loadInfo(url) {
  try {
    const response = await fetch(url);
    if (!response.ok) return DEFAULT_INFO;
    return { ...DEFAULT_INFO, ...await response.json() };
  } catch {
    return DEFAULT_INFO;
  }
}
function fixupMatrix(info) {
  const rotation = new Euler(
    MathUtils.degToRad(info.rotation.x),
    MathUtils.degToRad(info.rotation.y),
    MathUtils.degToRad(info.rotation.z)
  );
  return new Matrix4().compose(
    new Vector33(info.offset.x, info.offset.y, info.offset.z),
    new Quaternion().setFromEuler(rotation),
    new Vector33(info.scale, info.scale, info.scale)
  );
}
var cache = /* @__PURE__ */ new Map();
function loadModel(path) {
  let promise = cache.get(path);
  if (!promise) {
    promise = (async () => {
      const [{ scene, animations }, info] = await Promise.all([
        loadGLTF(`${path}/model.glb`),
        loadInfo(`${path}/info.json`)
      ]);
      scene.updateMatrixWorld(true);
      return { scene, info, fixup: fixupMatrix(info), animations };
    })();
    cache.set(path, promise);
  }
  return promise;
}
async function placeModel(path, scale = 1) {
  const { scene, fixup } = await loadModel(path);
  const model = scene.clone(true);
  model.applyMatrix4(fixup);
  const materials = [];
  model.traverse((o) => {
    const mesh = o;
    if (!mesh.isMesh) return;
    const own = (Array.isArray(mesh.material) ? mesh.material : [mesh.material]).map((m) => m.clone());
    mesh.material = Array.isArray(mesh.material) ? own : own[0];
    materials.push(...own);
  });
  const object = new Group2();
  object.add(model);
  object.scale.setScalar(scale);
  return { object, materials };
}

// src/helpers/behaviors.ts
import { Euler as Euler2, MathUtils as MathUtils2, Matrix4 as Matrix42, Object3D as Object3D2, Quaternion as Quaternion2, Vector3 as Vector34 } from "three";
var THREE_MATH = { Vector3: Vector34, Quaternion: Quaternion2, Euler: Euler2, Matrix4: Matrix42, Object3D: Object3D2, MathUtils: MathUtils2 };
var registry = /* @__PURE__ */ new Map();
var modules = /* @__PURE__ */ new Map();
function registerUnitBehavior(name, factory) {
  registry.set(name, factory);
}
function loadUnitBehavior(entry, folder) {
  if (entry.use) {
    const factory = registry.get(entry.use);
    return factory ? Promise.resolve(factory) : Promise.reject(new Error(`behavior "${entry.use}" is not registered`));
  }
  let source;
  if (entry.script) source = new URL(entry.script, new URL(folder.replace(/\/?$/, "/"), document.baseURI)).href;
  else if (entry.code) source = entry.code;
  else return Promise.reject(new Error("a behavior needs script, code or use"));
  let promise = modules.get(source);
  if (!promise) {
    const url = entry.script ? source : URL.createObjectURL(new Blob([source], { type: "text/javascript" }));
    promise = import(
      /* webpackIgnore: true */
      /* @vite-ignore */
      url
    ).then((module) => {
      if (typeof module.default !== "function") throw new Error(`${entry.script ?? "inline code"}: no default export function`);
      return module.default;
    });
    modules.set(source, promise);
  }
  return promise;
}

// src/enums.ts
var Land = /* @__PURE__ */ ((Land2) => {
  Land2["sea"] = "sea";
  Land2["coastal"] = "coastal";
  Land2["land"] = "land";
  Land2["sand"] = "sand";
  Land2["tundra"] = "tundra";
  Land2["snow"] = "snow";
  Land2["mountain"] = "mountain";
  return Land2;
})(Land || {});
var LandColor = {
  ["land" /* land */]: 8694355,
  ["coastal" /* coastal */]: 5205120,
  ["sea" /* sea */]: 2766476,
  ["sand" /* sand */]: 11446117,
  ["tundra" /* tundra */]: 16777215,
  ["snow" /* snow */]: 16777215,
  ["mountain" /* mountain */]: 9142389
};
var LandPriority = {
  ["sea" /* sea */]: 0,
  ["coastal" /* coastal */]: 1,
  ["land" /* land */]: 2,
  ["sand" /* sand */]: 3,
  ["tundra" /* tundra */]: 4,
  ["snow" /* snow */]: 5,
  ["mountain" /* mountain */]: 6
};
var UnitActions = /* @__PURE__ */ ((UnitActions2) => {
  UnitActions2["attack"] = "attack";
  UnitActions2["walk"] = "walk";
  UnitActions2["distanceAttack"] = "distanceAttack";
  UnitActions2["death"] = "death";
  UnitActions2["idle"] = "idle";
  UnitActions2["defence"] = "defence";
  return UnitActions2;
})(UnitActions || {});

// src/Storage.ts
var Storage = class {
  constructor(kind, capacity = Infinity) {
    this.items = {};
    this.kind = kind;
    this.capacity = capacity;
  }
  get(id) {
    return this.items[id] ?? 0;
  }
  get total() {
    let sum = 0;
    for (const id in this.items) sum += this.items[id];
    return sum;
  }
  get free() {
    return Math.max(0, this.capacity - this.total);
  }
  //Copy of the contents.
  get contents() {
    return { ...this.items };
  }
  //Adds up to `amount` (what fits); returns how much was added.
  add(id, amount) {
    const added = Math.max(0, Math.min(amount, this.free));
    if (added > 0) this.items[id] = this.get(id) + added;
    return added;
  }
  //Takes up to `amount` (what is there); returns how much was taken.
  take(id, amount) {
    const taken = Math.max(0, Math.min(amount, this.get(id)));
    if (taken > 0) {
      this.items[id] = this.get(id) - taken;
      if (this.items[id] <= 0) delete this.items[id];
    }
    return taken;
  }
};

// src/objects/Unit.ts
var Unit = class extends EventEmitter {
  constructor(options) {
    super();
    this._unit = new Object3D3();
    //the current move order (may span turns), null without one
    this.order = null;
    //the leg being walked right now, null while standing
    this.route = null;
    this._movesLeft = 0;
    this._charges = 0;
    this._health = 0;
    this._attack = 0;
    this._defence = 0;
    this.targetYaw = new Quaternion3();
    this.clips = /* @__PURE__ */ new Map();
    //the model sits in `body`, which only behaviors move (lean, bob...)
    this.body = new Object3D3();
    //plug-in code from info.json "behaviors" (see helpers/behaviors.ts)
    this.behaviors = [];
    this._cargo = [];
    this.options = {
      animateSpeed: 1,
      //Seconds to move from one cell to the next
      size: 40,
      //Map hex size, for cell -> world position
      type: "Assets/units/ship",
      x: 0,
      y: 0,
      actions: new Array(),
      movement: 1,
      //Movement points per turn - overridden by the model's own info.json
      //Combat stats (info.json): maximum health, attack and defence
      //strength (0 = none), attack range in cells (0 = melee). What they
      //mean is up to the game - see AttackEvent.
      health: 100,
      attack: 0,
      defence: 0,
      distanceAttack: 0,
      //Building (info.json): building types it can build, work per turn,
      //charges (see BuildingType.charges), cargo store capacity
      builds: new Array(),
      work: 1,
      charges: 0,
      storage: void 0,
      moveCosts: void 0,
      //state -> clip name, e.g. { idle: "idle", walk: "walk" }
      animations: {},
      transport: void 0,
      behaviors: new Array(),
      id: "new id",
      viewRange: 0,
      //Hex tiles seen around this unit (see FogOfWar.ts) - overridden by the model's own info.json
      //Terrain the unit may enter, overridden by the model's own info.json
      //(e.g. a ship sets coastal and sea only) - default deny, so a unit
      //whose info.json omits a terrain type never routes across it.
      sea: false,
      coastal: false,
      land: false,
      sand: false,
      tundra: false,
      snow: false,
      mountain: false
    };
    this.context = options.context;
    this._owner = options.owner;
    const { context, owner, health, ...rest } = options;
    this.startHealth = health;
    setOptions(this, rest);
  }
  //Loads the model and its info.json stats, places the model on its cell.
  async setUnit() {
    const { scene, info, fixup, animations } = await loadModel(this.options.type);
    setOptions(this, info);
    this._charges = this.options.charges;
    this._attack = this.options.attack;
    this._defence = this.options.defence;
    this.health = this.startHealth ?? this.options.health;
    if (this.options.storage) this._storage = new Storage("unit", this.options.storage.capacity);
    const model = cloneSkinned(scene);
    model.applyMatrix4(fixup);
    this.body.add(model);
    this._unit.add(this.body);
    this._unit.add(this.createOwnerRing());
    await this.setupBehaviors(model);
    this.placeOnCell(this.position);
    if (animations.length > 0) {
      this.mixer = new AnimationMixer(model);
      for (const [state, name] of Object.entries(this.options.animations)) {
        const clip = animations.find((a) => a.name === name);
        if (clip) this.clips.set(state, this.mixer.clipAction(clip));
        else console.warn(`Unit ${this.id}: animation "${name}" (${state}) not in ${this.options.type}.`);
      }
      this.animate("idle");
    }
  }
  //Plays the clip mapped to `state` (info.json "animations"), cross-fading
  //from the current one; no-op without such a clip.
  animate(state) {
    const action = this.clips.get(state);
    if (!action || action === this.playing) return;
    action.reset().play();
    if (this.playing) action.crossFadeFrom(this.playing, 0.2, false);
    this.playing = action;
  }
  //Loads and starts the behaviors listed in info.json. One that fails to
  //load or start is reported and skipped - the unit works without it.
  async setupBehaviors(model) {
    for (const entry of this.options.behaviors) {
      try {
        const factory = await loadUnitBehavior(entry, this.options.type);
        const behavior = factory({
          unit: this,
          root: this._unit,
          body: this.body,
          model,
          options: entry,
          size: this.options.size,
          heightAt: (x, z) => this.context ? this.context.heightAt(x, z) : 0,
          three: THREE_MATH
        });
        if (behavior) this.behaviors.push(behavior);
      } catch (error) {
        console.warn(`Unit ${this.id}: behavior ${entry.script ?? entry.use ?? "(inline)"} failed:`, error);
      }
    }
  }
  //Movement points a step from -> to costs this unit.
  moveCost(from, to) {
    return this.context?.moveCost ? this.context.moveCost(this, from, to) : 1;
  }
  //Flat ring in the owner's color under the model - whose unit it is. A
  //dark rim around it keeps blue/green owners readable on water and grass.
  createOwnerRing() {
    const size = this.options.size;
    const ring = new Object3D3();
    const layer = (inner, outer, color, opacity, y) => {
      const mesh = new Mesh5(
        new RingGeometry(size * inner, size * outer, 48),
        new MeshBasicMaterial3({ color, transparent: true, opacity, depthWrite: false })
      );
      mesh.rotation.x = -Math.PI / 2;
      mesh.position.y = y;
      mesh.renderOrder = 5;
      ring.add(mesh);
    };
    layer(0.4, 0.56, "#120e0a", 0.6, 1);
    layer(0.43, 0.53, this._owner.color, 0.95, 1.1);
    ring.name = "ownerRing";
    return ring;
  }
  setContext(context) {
    this.context = context;
    this.placeOnCell(this.position);
  }
  get unit() {
    return this._unit;
  }
  get actions() {
    return this.options.actions;
  }
  get action() {
    return this._action;
  }
  //The cell the unit is in RIGHT NOW - during a move it advances cell by
  //cell together with the "move" event, it does not jump to the destination.
  get position() {
    return { x: this.options.x, y: this.options.y };
  }
  get id() {
    return this.options.id;
  }
  get owner() {
    return this._owner;
  }
  get viewRange() {
    return this.options.viewRange;
  }
  get isMoving() {
    return this.route !== null;
  }
  //Final cell of the current order (its position without one).
  get destination() {
    return this.order ? this.order.target : this.position;
  }
  get hasOrder() {
    return this.order !== null;
  }
  //Movement points per turn (info.json) and what is left of them this turn.
  get movement() {
    return this.options.movement;
  }
  get movesLeft() {
    return this._movesLeft;
  }
  //Building types this unit can build, its work points per turn, charges
  //left (0 = none, see BuildingType.charges), its cargo store (if any).
  get builds() {
    return this.options.builds;
  }
  get work() {
    return this.options.work;
  }
  get charges() {
    return this._charges;
  }
  //Spends charges; returns false (nothing spent) if there are not enough.
  useCharges(count) {
    if (count > this._charges) return false;
    this._charges -= count;
    return true;
  }
  //Combat stats: health now (0 .. maxHealth - the engine does nothing when
  //it hits 0, the game removes the unit), maximum health, attack and
  //defence strength, attack range (see AttackEvent). The game sets them
  //(damage, healing, promotions); attack/defence start from info.json.
  get health() {
    return this._health;
  }
  set health(value) {
    this._health = Math.max(0, Math.min(value, this.options.health));
  }
  get maxHealth() {
    return this.options.health;
  }
  get attack() {
    return this._attack;
  }
  set attack(value) {
    this._attack = value;
  }
  get defence() {
    return this._defence;
  }
  set defence(value) {
    this._defence = value;
  }
  get distanceAttack() {
    return this.options.distanceAttack;
  }
  //Takes movement points this turn (an attack that ends the unit's turn...).
  spendMoves(points) {
    this._movesLeft = Math.max(0, this._movesLeft - points);
  }
  //Its own movement cost overrides (info.json "moveCosts", see MoveCosts).
  get moveCosts() {
    return this.options.moveCosts;
  }
  get storage() {
    return this._storage;
  }
  //New turn of the owner: full movement points again. Called by the engine.
  startTurn() {
    this._movesLeft = this.options.movement;
  }
  //Which Land types this unit may enter (its info.json terrain flags) -
  //feeds PathFinder so a route never crosses a tile the unit can't reach.
  get terrain() {
    return {
      ["sea" /* sea */]: this.options.sea,
      ["coastal" /* coastal */]: this.options.coastal,
      ["land" /* land */]: this.options.land,
      ["sand" /* sand */]: this.options.sand,
      ["tundra" /* tundra */]: this.options.tundra,
      ["snow" /* snow */]: this.options.snow,
      ["mountain" /* mountain */]: this.options.mountain
    };
  }
  //Transport: how many units it carries (info.json transport.capacity, 0 =
  //not a transport), the units aboard, the ship it is aboard (if any).
  get capacity() {
    return this.options.transport?.capacity ?? 0;
  }
  get cargo() {
    return [...this._cargo];
  }
  get carrier() {
    return this._carrier;
  }
  //Engine side of GameEngine.load()/unload() - use those: they keep the
  //map's occupancy, fog and events in step. Aboard, a unit has no model on
  //the map; its cell follows the ship's.
  attachTo(carrier) {
    this.detach();
    this._carrier = carrier;
    carrier._cargo.push(this);
    this.options.x = carrier.options.x;
    this.options.y = carrier.options.y;
    this._unit.visible = false;
    carrier.updateCargoBadge();
  }
  detach() {
    const carrier = this._carrier;
    if (!carrier) return;
    carrier._cargo = carrier._cargo.filter((u) => u !== this);
    carrier.updateCargoBadge();
    this._carrier = void 0;
    this._unit.visible = true;
    this.placeOnCell(this.position);
  }
  //Puts the unit on a cell at once, no movement (GameEngine.unload; the
  //engine's occupancy is not touched here). Ignored while moving / aboard.
  teleport(cell) {
    if (this.route || this._carrier) return;
    this.options.x = cell.x;
    this.options.y = cell.y;
    this.placeOnCell(cell);
  }
  //The number of units aboard, over the ship (none when empty).
  updateCargoBadge() {
    const count = this._cargo.length;
    if (count === 0) {
      if (this.cargoBadge) {
        this.cargoBadge.material.map?.dispose();
        this.cargoBadge.material.dispose();
        this.cargoBadge.removeFromParent();
        this.cargoBadge = void 0;
      }
      return;
    }
    if (!this.cargoBadge) {
      const canvas2 = document.createElement("canvas");
      canvas2.width = canvas2.height = 64;
      const texture2 = new CanvasTexture3(canvas2);
      texture2.colorSpace = SRGBColorSpace2;
      this.cargoBadge = new Sprite2(new SpriteMaterial2({ map: texture2, depthWrite: false }));
      this.cargoBadge.scale.setScalar(this.options.size * 0.35);
      this.cargoBadge.position.y = this.options.size * 0.9;
      this.cargoBadge.renderOrder = 12;
      this._unit.add(this.cargoBadge);
    }
    const texture = this.cargoBadge.material.map;
    const canvas = texture.image;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, 64, 64);
    ctx.fillStyle = this._owner.color;
    ctx.beginPath();
    ctx.arc(32, 32, 28, 0, Math.PI * 2);
    ctx.fill();
    ctx.lineWidth = 5;
    ctx.strokeStyle = "#120e0a";
    ctx.stroke();
    ctx.fillStyle = "#fff";
    ctx.font = "bold 36px sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(String(count), 32, 34);
    texture.needsUpdate = true;
  }
  activate(action) {
    if (this.options.actions.includes(action)) this._action = action;
    else console.warn(`Unit ${this.id}: action "${action}" is not available.`);
  }
  //Founds a city on the unit's tile, owned by the unit's owner. No rule
  //checks (which units may found cities, where, on whose turn) - that is
  //the game's / the server's business; the engine only refuses what the
  //map can't take (water, another city's territory, a building on the tile).
  foundCity(info) {
    if (!this.context?.foundCity) return Promise.reject(new Error(`Unit ${this.id} has no context (not created by a GameEngine).`));
    return this.context.foundCity(this, info);
  }
  //Attacks a unit, city or building (or whatever stands on a cell) - see
  //AttackEvent and GameEngine.attack. Resolves the event's object after the
  //last event, undefined if refused or cancelled.
  strike(target) {
    if (!this.context?.attack) return Promise.reject(new Error(`Unit ${this.id} has no context (not created by a GameEngine).`));
    return this.context.attack(this, target);
  }
  //Gives the unit a move order to target (see the class comment). Resolves
  //true on arrival - possibly turns later - false if refused or dropped.
  move(target) {
    if (this.route) {
      console.warn(`Unit ${this.id} is already moving - move() ignored.`);
      return Promise.resolve(false);
    }
    if (!this.context) {
      console.warn(`Unit ${this.id} has no context (not created by a GameEngine) - move() ignored.`);
      return Promise.resolve(false);
    }
    if (this.context.canAct && !this.context.canAct(this)) return Promise.resolve(false);
    this.cancelOrder();
    return new Promise((resolve) => {
      this.order = { target: { x: target.x, y: target.y }, resolve };
      this.walkLeg();
    });
  }
  //Continues a waiting order with the movement points left - the engine
  //calls it at the start of the owner's turn.
  continueOrder() {
    if (this.order && !this.route) this.walkLeg();
  }
  //Drops the order (its promise resolves false). A leg already being walked
  //finishes its animation, the unit then stays where it is.
  cancelOrder() {
    this.finishOrder(false);
  }
  finishOrder(arrived) {
    const order = this.order;
    this.order = null;
    order?.resolve(arrived);
  }
  //Starts the next leg of the order: re-plans the route (other units moved
  //since last turn), walks as far as movesLeft reaches.
  walkLeg() {
    const order = this.order;
    if (!order || !this.context) return;
    const path = this.context.findPath(this, order.target);
    if (path.length < 2) return this.finishOrder(false);
    const costs = [];
    let left = this._movesLeft;
    for (let i = 1; i < path.length && left > 0; i++) {
      const cost = this.moveCost(path[i - 1], path[i]);
      if (cost > left && this.context.enterRule === "fullCost" && left < this.options.movement) break;
      costs.push(Math.min(cost, left));
      left -= cost;
    }
    if (costs.length === 0) return;
    const leg = path.slice(0, costs.length + 1);
    let cancelled = false;
    this.emit("before_move", {
      unit: this,
      from: leg[0],
      to: leg[leg.length - 1],
      destination: order.target,
      path: leg,
      cancel: () => {
        cancelled = true;
      }
    });
    if (cancelled || this.order !== order) return this.finishOrder(false);
    this.context.moveStarted?.(this, leg);
    const points = leg.map((p) => {
      const c = getHexCenter(p.x, p.y, this.options.size);
      return new Vector35(c.x, 0, c.y);
    });
    const curve = new CatmullRomCurve3(points, false, "centripetal");
    this.route = { path: leg, curve, progress: 0, cell: 0, costs };
    this.animate("walk");
  }
  //Advances the movement animation by dtS seconds. Called every frame by
  //the owner; a no-op while standing (it only keeps the model on the ground,
  //so boats ride the waves).
  update(dtS) {
    if (this._carrier) return;
    this.mixer?.update(dtS);
    for (const behavior of this.behaviors) behavior.update?.(dtS);
    const route = this.route;
    if (!route) {
      if (this.context) this._unit.position.y = this.context.heightAt(this._unit.position.x, this._unit.position.z);
      return;
    }
    const last = route.path.length - 1;
    route.progress = Math.min(route.progress + dtS / Math.max(this.options.animateSpeed, 0.01), last);
    const t = route.progress / last;
    const point = route.curve.getPoint(t);
    point.y = this.context ? this.context.heightAt(point.x, point.z) : 0;
    this._unit.position.copy(point);
    const tangent = route.curve.getTangent(t);
    if (tangent.x !== 0 || tangent.z !== 0) {
      this.targetYaw.setFromAxisAngle(new Vector35(0, 1, 0), Math.atan2(tangent.x, tangent.z));
      this._unit.quaternion.slerp(this.targetYaw, Math.min(1, dtS * 10));
    }
    const cell = Math.min(Math.round(route.progress), last);
    while (route.cell < cell) {
      const from = route.path[route.cell];
      route.cell++;
      const to = route.path[route.cell];
      this.options.x = to.x;
      this.options.y = to.y;
      for (const unit of this._cargo) {
        unit.options.x = to.x;
        unit.options.y = to.y;
      }
      this._movesLeft = Math.max(0, this._movesLeft - route.costs[route.cell - 1]);
      this.emit("move", { unit: this, from, to, index: route.cell, path: route.path });
    }
    if (route.progress >= last) {
      this.route = null;
      this.animate("idle");
      const to = route.path[last];
      const arrived = !!this.order && this.order.target.x === to.x && this.order.target.y === to.y;
      this.emit("end_move", { unit: this, from: route.path[0], to, path: route.path, arrived });
      if (arrived) this.finishOrder(true);
    }
  }
  placeOnCell(cell) {
    const c = getHexCenter(cell.x, cell.y, this.options.size);
    const y = this.context ? this.context.heightAt(c.x, c.y) : 0;
    this._unit.position.set(c.x, y, c.y);
  }
  //Removes the model from its parent and drops all listeners.
  dispose() {
    this.route = null;
    this.finishOrder(false);
    this.mixer?.stopAllAction();
    for (const behavior of this.behaviors) behavior.dispose?.();
    this.behaviors = [];
    this.detach();
    this._cargo = [];
    this.updateCargoBadge();
    this._unit.removeFromParent();
    this._unit.getObjectByName("ownerRing")?.traverse((o) => {
      if (o instanceof Mesh5) {
        o.geometry.dispose();
        o.material.dispose();
      }
    });
    this.offAll();
  }
};

// src/objects/City.ts
import { Box3, Color as Color3, Group as Group3, Vector3 as Vector36 } from "three";

// src/objects/citysprite.ts
import { Texture, SpriteMaterial as SpriteMaterial3, Sprite as Sprite3 } from "three";
function makeTextSprite(message, parameters) {
  if (parameters === void 0) parameters = {};
  let fontface = parameters.hasOwnProperty("fontface") ? parameters["fontface"] : "Arial";
  let fontsize = parameters.hasOwnProperty("fontsize") ? parameters["fontsize"] : 18;
  let borderThickness = parameters.hasOwnProperty("borderThickness") ? parameters["borderThickness"] : 4;
  let borderColor = parameters.hasOwnProperty("borderColor") ? parameters["borderColor"] : { r: 0, g: 0, b: 0, a: 1 };
  let backgroundColor = parameters.hasOwnProperty("backgroundColor") ? parameters["backgroundColor"] : { r: 255, g: 255, b: 255, a: 1 };
  let canvas = document.createElement("canvas");
  let context = canvas.getContext("2d");
  context.font = "Bold " + fontsize + "px " + fontface;
  let metrics = context.measureText(message);
  let textWidth = metrics.width;
  const width = Math.ceil(textWidth + borderThickness * 2);
  const height = Math.ceil(fontsize * 1.4 + borderThickness * 2);
  canvas.width = width;
  canvas.height = height;
  context = canvas.getContext("2d");
  context.font = "Bold " + fontsize + "px " + fontface;
  context.fillStyle = "rgba(" + backgroundColor.r + "," + backgroundColor.g + "," + backgroundColor.b + "," + backgroundColor.a + ")";
  context.strokeStyle = "rgba(" + borderColor.r + "," + borderColor.g + "," + borderColor.b + "," + borderColor.a + ")";
  context.lineWidth = borderThickness;
  roundRect(context, borderThickness / 2, borderThickness / 2, textWidth + borderThickness, fontsize * 1.4 + borderThickness, 6);
  context.fillStyle = "rgba(0, 0, 0, 1.0)";
  context.fillText(message, borderThickness, fontsize + borderThickness);
  var texture = new Texture(canvas);
  texture.needsUpdate = true;
  var spriteMaterial = new SpriteMaterial3(
    { map: texture, transparent: true, depthWrite: false }
  );
  var sprite = new Sprite3(spriteMaterial);
  const scale = 100 / 300;
  sprite.scale.set(width * scale, height * scale, 1);
  return sprite;
}
function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
}

// src/objects/City.ts
var City = class {
  constructor(options) {
    //Tiles that belong to the city (set by the engine).
    this.territory = [];
    this.storage = new Storage("city");
    //Its buildings - in the city and on its territory, built or not.
    this.buildings = [];
    this.object = new Group3();
    this.labelHeight = 0;
    this.dim = 1;
    this.materials = [];
    this.options = options;
    this.id = options.id;
    this.name = options.name;
    this.owner = options.owner;
    this.position = { x: options.x, y: options.y };
    this.radius = options.radius;
  }
  //Loads the model and puts model + label on the city's tile.
  async load(size, heightAt) {
    const center = getHexCenter(this.position.x, this.position.y, size);
    const { object, materials } = await placeModel(this.options.model, this.options.scale ?? 1);
    this.materials = materials.map((material) => ({ material, color: colorOf(material)?.clone() }));
    this.object.add(object);
    this.object.position.set(center.x, heightAt(center.x, center.y), center.y);
    this.labelHeight = new Box3().setFromObject(object).getSize(new Vector36()).y + size / 5;
    this.setLook(this.owner, 1);
  }
  //What the viewer sees: `owner` colors the label (the owner it knows of,
  //not necessarily the current one), `dim` multiplies the colors (1 = in
  //view, the map's fogDarkenFactor = remembered).
  setLook(owner, dim) {
    if (owner !== this.shownOwner) {
      this.shownOwner = owner;
      if (this.label) {
        this.label.removeFromParent();
        this.label.material.map?.dispose();
        this.label.material.dispose();
      }
      const color = new Color3(owner.color);
      this.label = makeTextSprite(` ${this.name} `, {
        fontsize: 32,
        fontface: "Georgia",
        borderColor: { r: color.r * 255, g: color.g * 255, b: color.b * 255, a: 0.9 }
      });
      this.label.position.set(0, this.labelHeight, 0);
      this.object.add(this.label);
      this.dim = -1;
    }
    if (dim === this.dim) return;
    this.dim = dim;
    for (const { material, color } of this.materials) if (color) colorOf(material).copy(color).multiplyScalar(dim);
    this.label.material.color.setScalar(dim);
  }
  isInTerritory(cell) {
    return this.territory.some((t) => t.x === cell.x && t.y === cell.y);
  }
  dispose() {
    this.object.removeFromParent();
    this.label?.material.map?.dispose();
    this.label?.material.dispose();
    for (const { material } of this.materials) material.dispose();
  }
};
function colorOf(material) {
  const color = material.color;
  return color instanceof Color3 ? color : void 0;
}

// src/objects/Building.ts
import { CanvasTexture as CanvasTexture4, Group as Group4, SRGBColorSpace as SRGBColorSpace3, Sprite as Sprite5, SpriteMaterial as SpriteMaterial4 } from "three";
var Building = class {
  constructor(id, type, owner, position) {
    this.state = "construction";
    this.progress = 0;
    // work points done
    this.paused = false;
    this.object = new Group4();
    this.materials = [];
    this.dim = 1;
    this.id = id;
    this.type = type;
    this.owner = owner;
    this.position = { x: position.x, y: position.y };
    if (type.storage) this.storage = new Storage("building", type.storage.capacity);
  }
  get work() {
    return this.type.work ?? 0;
  }
  get isDone() {
    return this.state === "done";
  }
  //Puts the building's look on its tile (terrain buildings only).
  async load(size, heightAt) {
    if (this.type.placement !== "terrain") return;
    const center = getHexCenter(this.position.x, this.position.y, size);
    this.object.position.set(center.x, heightAt(center.x, center.y), center.y);
    if (this.type.model) {
      const { object, materials } = await placeModel(this.type.model, this.type.scale ?? 1);
      this.materials = materials.map((material) => ({ material, transparent: material.transparent, color: colorOf(material)?.clone() }));
      this.object.add(object);
    } else if (this.type.icon) {
      this.icon = new Image();
      this.icon.src = this.type.icon;
      try {
        await this.icon.decode();
      } catch {
        console.warn(`Building icon "${this.type.icon}" (${this.type.id}) failed to load.`);
        this.icon = void 0;
      }
      this.badgeCanvas = document.createElement("canvas");
      this.badgeCanvas.width = this.badgeCanvas.height = 128;
      this.badgeTexture = new CanvasTexture4(this.badgeCanvas);
      this.badgeTexture.colorSpace = SRGBColorSpace3;
      const material = new SpriteMaterial4({ map: this.badgeTexture, transparent: true, depthWrite: false });
      this.materials = [{ material, transparent: true, color: material.color.clone() }];
      const sprite = new Sprite5(material);
      sprite.scale.setScalar(size * 0.55);
      sprite.position.y = size * 0.45;
      this.object.add(sprite);
    }
    this.setLook(this.owner, 1);
    this.refreshLook();
  }
  //What the viewer sees: the owner it knows of (badge color) and the color
  //multiplier (1 = in view, the map's fogDarkenFactor = remembered).
  setLook(owner, dim) {
    if (owner !== this.shownOwner) {
      this.shownOwner = owner;
      this.drawBadge(owner.color);
    }
    if (dim === this.dim) return;
    this.dim = dim;
    for (const { material, color } of this.materials) if (color) colorOf(material).copy(color).multiplyScalar(dim);
  }
  //The icon on a round badge in the owner's color (a white glyph on a
  //transparent background works best, like resource icons).
  drawBadge(color) {
    if (!this.badgeCanvas || !this.badgeTexture) return;
    const ctx = this.badgeCanvas.getContext("2d");
    ctx.clearRect(0, 0, 128, 128);
    ctx.beginPath();
    ctx.arc(64, 64, 54, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.lineWidth = 7;
    ctx.strokeStyle = "rgba(20, 16, 12, 0.9)";
    ctx.stroke();
    if (this.icon) ctx.drawImage(this.icon, 26, 26, 76, 76);
    this.badgeTexture.needsUpdate = true;
  }
  //Faded while under construction.
  refreshLook() {
    const done = this.isDone;
    for (const { material, transparent } of this.materials) {
      material.transparent = done ? transparent : true;
      material.opacity = done ? 1 : 0.45;
      material.needsUpdate = true;
    }
  }
  dispose() {
    this.object.removeFromParent();
    this.badgeTexture?.dispose();
    for (const { material } of this.materials) material.dispose();
  }
};

// src/Resources.ts
var ResourceRules = class {
  constructor(config) {
    this.byId = /* @__PURE__ */ new Map();
    this.warned = /* @__PURE__ */ new Set();
    this.config = config;
    for (const def of config.resources) {
      if (this.byId.has(def.id)) throw new Error(`Resource id "${def.id}" is defined twice.`);
      this.byId.set(def.id, def);
    }
  }
  get resources() {
    return this.config.resources;
  }
  get(id) {
    return this.byId.get(id);
  }
  tileYield(tile) {
    const sum = {};
    const add = (yields) => {
      if (!yields) return;
      for (const id in yields) {
        if (!this.byId.has(id)) {
          if (!this.warned.has(id)) console.warn(`Unknown resource "${id}" in yields - ignored.`);
          this.warned.add(id);
          continue;
        }
        sum[id] = (sum[id] ?? 0) + yields[id];
      }
    };
    add(this.config.terrain?.[tile.type]);
    for (const modifier of tile.modifiers ?? []) add(this.config.modifiers?.[modifier]);
    add(tile.yields);
    return this.normalize(sum);
  }
  //Storage rules (see ResourceDef): where the resource may be kept, where
  //spending takes it from (in order), where income lands (in order).
  storageOf(id) {
    return this.byId.get(id)?.storage ?? ["player"];
  }
  spendFrom(id) {
    const def = this.byId.get(id);
    if (def?.spendFrom) return def.spendFrom;
    const storage = this.storageOf(id);
    return ["player", "city", "building"].filter((k) => storage.includes(k));
  }
  depositTo(id) {
    const def = this.byId.get(id);
    if (def?.depositTo) return def.depositTo;
    const storage = this.storageOf(id);
    return ["building", "city", "player"].filter((k) => storage.includes(k));
  }
  //Drops zero/negative totals and unknown ids, orders like `resources`.
  normalize(yields) {
    const out = {};
    for (const def of this.config.resources) {
      const amount = yields[def.id];
      if (amount > 0) out[def.id] = amount;
    }
    return out;
  }
};

// src/Economy.ts
function storesOf(sources, kind) {
  const s = sources[kind];
  return !s ? [] : Array.isArray(s) ? s : [s];
}
function missingFor(rules, cost, sources) {
  const missing = {};
  for (const id in cost) {
    let available = 0;
    for (const kind of rules.spendFrom(id)) {
      for (const store of storesOf(sources, kind)) available += store.get(id);
    }
    if (available < cost[id]) missing[id] = cost[id] - available;
  }
  return missing;
}
function spend(rules, cost, sources) {
  if (Object.keys(missingFor(rules, cost, sources)).length > 0) return false;
  for (const id in cost) {
    let left = cost[id];
    for (const kind of rules.spendFrom(id)) {
      for (const store of storesOf(sources, kind)) {
        left -= store.take(id, left);
        if (left <= 0) break;
      }
      if (left <= 0) break;
    }
  }
  return true;
}
function deposit(rules, income, sources) {
  const lost = {};
  for (const id in income) {
    let left = income[id];
    for (const kind of rules.depositTo(id)) {
      for (const store of storesOf(sources, kind)) {
        left -= store.add(id, left);
        if (left <= 0) break;
      }
      if (left <= 0) break;
    }
    if (left > 0) lost[id] = left;
  }
  return lost;
}
function addYields(a, b) {
  const out = { ...a };
  for (const id in b ?? {}) out[id] = (out[id] ?? 0) + b[id];
  return out;
}

// src/Player.ts
var Player = class {
  constructor(info) {
    //The player's VIRTUAL global stock - only resources whose
    //ResourceDef.storage includes "player" ever land here. Real, local stores
    //are cities', buildings' and units' own.
    this.storage = new Storage("player");
    this.id = info.id;
    this.name = info.name ?? info.id;
    this.color = info.color ?? "#ffffff";
  }
};

// src/helpers/pathfinder.ts
var PathFinder = class {
  constructor(map, restricted, accessible, options = {}) {
    this.map = map;
    this.restricted = restricted;
    this.accessible = accessible;
    this.cost = options.cost ?? (() => 1);
    this.minCost = options.cost ? Math.max(0, options.minCost ?? 1) : 1;
    this.budget = options.budget;
    this.endOnly = options.endOnly;
  }
  //Cells from start to end (both included), or [] if there is no route.
  //The start cell itself is not checked (a unit aboard a ship starts on
  //water).
  find(startX, startY, endX, endY) {
    if (startX === endX && startY === endY) return [];
    if (!this.inside(startX, startY) || !this.enterable(endX, endY)) return [];
    if (this.budget) {
      const goal2 = endY * this.map.w + endX;
      const { turn, parent: parent2 } = this.searchTurns(startX, startY, this.budget, Infinity, goal2);
      if (!(turn[goal2] < Infinity)) return [];
      const path2 = [];
      for (let cell = goal2; cell >= 0; cell = parent2[cell]) {
        const x = cell % this.map.w;
        path2.push({ x, y: (cell - x) / this.map.w });
      }
      return path2.reverse();
    }
    const w = this.map.w;
    const id = (x, y) => y * w + x;
    const g = /* @__PURE__ */ new Map();
    const parent = /* @__PURE__ */ new Map();
    const closed = /* @__PURE__ */ new Set();
    const open = new MinHeap();
    const start = id(startX, startY), goal = id(endX, endY);
    g.set(start, 0);
    open.push(start, this.heuristic(startX, startY, endX, endY));
    while (open.size > 0) {
      const current = open.pop();
      if (current === goal) break;
      if (closed.has(current)) continue;
      closed.add(current);
      const cx = current % w, cy = (current - cx) / w;
      if (current !== start && this.isEndOnly(cx, cy)) continue;
      for (const n of getNeighbors(cx, cy)) {
        if (!this.enterable(n.x, n.y)) continue;
        const next = id(n.x, n.y);
        if (closed.has(next)) continue;
        const step = this.cost({ x: cx, y: cy }, n);
        if (!(step < Infinity)) continue;
        const cost = g.get(current) + Math.max(0, step);
        if (cost >= (g.get(next) ?? Infinity)) continue;
        g.set(next, cost);
        parent.set(next, current);
        open.push(next, cost + this.heuristic(n.x, n.y, endX, endY));
      }
    }
    if (!g.has(goal)) return [];
    const path = [];
    for (let cell = goal; cell !== void 0; cell = parent.get(cell)) {
      const x = cell % w;
      path.push({ x, y: (cell - x) / w });
    }
    return path.reverse();
  }
  //Cells the walker reaches this turn (needs a budget), start excluded.
  reachable(startX, startY) {
    if (!this.budget || !this.inside(startX, startY)) return [];
    const w = this.map.w;
    const { turn } = this.searchTurns(startX, startY, this.budget, 0);
    const cells = [];
    for (let cell = 0; cell < turn.length; cell++) {
      if (turn[cell] !== 0 || cell === startY * w + startX) continue;
      const x = cell % w;
      cells.push({ x, y: (cell - x) / w });
    }
    return cells;
  }
  //Dijkstra over (turn, points left) - fewer turns first, then more points
  //left - up to maxTurn, stopping at goal if given. The steps follow
  //stepTurns(): the same rules Unit walks by.
  searchTurns(startX, startY, budget, maxTurn, goal = -1) {
    const w = this.map.w, n = w * this.map.h;
    const turn = new Float64Array(n).fill(Infinity);
    const left = new Float64Array(n);
    const parent = new Int32Array(n).fill(-1);
    const closed = new Uint8Array(n);
    const open = new MinHeap();
    const span = Math.max(budget.movement, budget.movesLeft, 0) + 1;
    const start = startY * w + startX;
    turn[start] = 0;
    left[start] = Math.max(0, budget.movesLeft);
    open.push(start, -left[start]);
    while (open.size > 0) {
      const current = open.pop();
      if (current === goal) break;
      if (closed[current]) continue;
      closed[current] = 1;
      const cx = current % w, cy = (current - cx) / w;
      if (current !== start && this.isEndOnly(cx, cy)) continue;
      for (const nb of getNeighbors(cx, cy)) {
        if (!this.enterable(nb.x, nb.y)) continue;
        const next = nb.y * w + nb.x;
        if (closed[next]) continue;
        const step = stepTurns(turn[current], left[current], this.cost({ x: cx, y: cy }, nb), budget);
        if (!step || step.turn > maxTurn) continue;
        if (step.turn > turn[next] || step.turn === turn[next] && step.left <= left[next]) continue;
        turn[next] = step.turn;
        left[next] = step.left;
        parent[next] = current;
        open.push(next, step.turn * span - step.left);
      }
    }
    return { turn, parent };
  }
  inside(x, y) {
    return x >= 0 && y >= 0 && x < this.map.w && y < this.map.h;
  }
  isEndOnly(x, y) {
    return !!this.endOnly && this.endOnly(x, y);
  }
  enterable(x, y) {
    if (!this.inside(x, y)) return false;
    if (this.isEndOnly(x, y)) return true;
    const tile = this.map.data[x]?.[y];
    if (!tile || this.restricted[tile.type] !== true) return false;
    return !this.accessible || this.accessible(x, y);
  }
  heuristic(x1, y1, x2, y2) {
    return hexDistance(x1, y1, x2, y2) * this.minCost;
  }
};
function stepTurns(turn, left, cost, budget) {
  if (!(cost < Infinity)) return null;
  cost = Math.max(0, cost);
  if (left <= 0) {
    if (budget.movement <= 0) return null;
    turn++;
    left = budget.movement;
  }
  if (cost > left && budget.enterRule === "fullCost" && left < budget.movement) {
    turn++;
    left = budget.movement;
  }
  return { turn, left: Math.max(0, left - cost) };
}
function routeTurns(path, cost, budget) {
  const turns = path.length ? [0] : [];
  let turn = 0, left = budget.movesLeft;
  for (let i = 1; i < path.length; i++) {
    const step = stepTurns(turn, left, cost(path[i - 1], path[i]), budget);
    if (!step) break;
    ({ turn, left } = step);
    turns.push(turn);
  }
  return turns;
}
function hexDistance(x1, y1, x2, y2) {
  const shift = (x) => (x + (x & 1)) / 2;
  const dq = x1 - x2;
  const dr = y1 - shift(x1) - (y2 - shift(x2));
  return (Math.abs(dq) + Math.abs(dr) + Math.abs(dq + dr)) / 2;
}
var MinHeap = class {
  constructor() {
    this.ids = [];
    this.keys = [];
  }
  get size() {
    return this.ids.length;
  }
  push(id, key3) {
    let i = this.ids.length;
    this.ids.push(id);
    this.keys.push(key3);
    while (i > 0) {
      const up = i - 1 >> 1;
      if (this.keys[up] <= key3) break;
      this.ids[i] = this.ids[up];
      this.keys[i] = this.keys[up];
      i = up;
    }
    this.ids[i] = id;
    this.keys[i] = key3;
  }
  pop() {
    const top = this.ids[0];
    const id = this.ids.pop(), key3 = this.keys.pop();
    const n = this.ids.length;
    if (n > 0) {
      let i = 0;
      for (; ; ) {
        const l = i * 2 + 1, r = l + 1;
        let m = i, mKey = key3;
        if (l < n && this.keys[l] < mKey) {
          m = l;
          mKey = this.keys[l];
        }
        if (r < n && this.keys[r] < mKey) {
          m = r;
        }
        if (m === i) break;
        this.ids[i] = this.ids[m];
        this.keys[i] = this.keys[m];
        i = m;
      }
      this.ids[i] = id;
      this.keys[i] = key3;
    }
    return top;
  }
};

// src/gameengine.ts
var key = (p) => `${p.x},${p.y}`;
var COMBAT_EVENTS = ["before_attack", "attack", "after_attack", "before_defence", "defence", "after_defence"];
var WATER = ["sea" /* sea */, "coastal" /* coastal */];
var GameEngine = class extends EventEmitter {
  constructor(options) {
    super();
    this.units = /* @__PURE__ */ new Map();
    //"x,y" -> id of the unit standing there or reserved it as its destination
    this.occupied = /* @__PURE__ */ new Map();
    this.players = /* @__PURE__ */ new Map();
    this._turn = 0;
    this.yieldsShown = false;
    this.cities = /* @__PURE__ */ new Map();
    this.territory = /* @__PURE__ */ new Map();
    // "x,y" -> city owning the tile
    this.buildingTypes = /* @__PURE__ */ new Map();
    this.buildings = /* @__PURE__ */ new Map();
    this.terrainBuildings = /* @__PURE__ */ new Map();
    // "x,y" -> building on the tile
    this.unitTasks = /* @__PURE__ */ new Map();
    // unit id -> construction it works on
    //What each player knows of others' cities and terrain buildings: the
    //owner it saw the last time the tile was in its view. Unknown = not shown.
    this.memory = /* @__PURE__ */ new Map();
    this.nextId = 0;
    this.options = {
      preventCellClick: true,
      fogOfWar: true,
      tileYield: void 0,
      cityModel: "Assets/models/monument",
      cityRadius: 1,
      cityWork: 1,
      cityIncome: void 0,
      productionList: void 0,
      moveCost: void 0,
      canBoard: void 0
    };
    //unit id -> the ship it is on its way to board (counts against its room)
    this.boarding = /* @__PURE__ */ new Map();
    this.movement = {};
    setOptions(this, options);
    this._map = new HexMap(options);
    this._map.on("click", (payload) => this.cellClick(payload));
    this._map.on("hover", (payload) => this.cellHover(payload));
    this._map.on("frame", ({ dt }) => {
      for (const unit of this.units.values()) unit.update(dt);
    });
  }
  async init(data) {
    this._mapData = data.map;
    if (data.resources) await this.setResources(data.resources);
    if (data.buildingTypes) this.setBuildingTypes(data.buildingTypes);
    if (data.movement) this.setMovementRules(data.movement);
    await this._map.load(data.map);
    for (const player of this.players.values()) {
      if (this.options.fogOfWar && !player.fog) player.fog = new FogOfWar(data.map);
    }
    for (const info of data.players) this.addPlayer(info);
    for (const placement of data.cities ?? []) await this.foundCity(placement);
    for (const placement of data.buildings ?? []) await this.placeBuilding(placement);
    for (const placement of data.units ?? []) await this.createUnit(placement);
    if (this.yieldsShown) this.refreshYields();
    const first = this.players.values().next().value;
    if (first) {
      this._turn = 1;
      this.beginTurn(first);
    }
  }
  //-------------------------------------------------------------------------
  //Players
  //-------------------------------------------------------------------------
  //Adds a player (with its own fog of war, all Unseen) at the end of the
  //turn order. The first player added becomes the active one.
  addPlayer(info) {
    if (this.players.has(info.id)) throw new Error(`Player id "${info.id}" already exists.`);
    const player = new Player(info);
    if (this.options.fogOfWar && this._mapData) player.fog = new FogOfWar(this._mapData);
    this.players.set(player.id, player);
    if (!this._activePlayer) this.setActivePlayer(player);
    return player;
  }
  //Removes a player together with all its units. If it was its turn, the
  //turn passes to the next player (no turn_end for the removed one).
  removePlayer(id) {
    const player = this.players.get(id);
    if (!player) return;
    const next = this._activePlayer === player ? this.nextPlayer(player) : void 0;
    for (const unit of this.unitsOf(player)) this.removeUnit(unit.id);
    this.players.delete(id);
    this.memory.delete(player);
    if (this._activePlayer !== player) return;
    this._activePlayer = void 0;
    if (next && next !== player) {
      this.setActivePlayer(next);
      if (this._turn > 0) this.beginTurn(next);
    }
  }
  getPlayer(id) {
    return this.players.get(id);
  }
  get allPlayers() {
    return [...this.players.values()];
  }
  unitsOf(player) {
    const id = typeof player === "string" ? player : player.id;
    return this.allUnits.filter((u) => u.owner.id === id);
  }
  //-------------------------------------------------------------------------
  //Turns
  //-------------------------------------------------------------------------
  //Whose turn it is: the map shows this player's fog, only this player's
  //units and cities take orders.
  get activePlayer() {
    return this._activePlayer;
  }
  //Round number: 1 on the first player's first turn, +1 every time the
  //turn order wraps around. 0 before init().
  get turn() {
    return this._turn;
  }
  //Ends the active player's turn and starts the next player's.
  endTurn() {
    const player = this._activePlayer;
    if (!player || this._turn === 0) return;
    this.emit("turn_end", { player, turn: this._turn });
    const next = this.nextPlayer(player);
    if (!next) return;
    if (this.turnIndex(next) <= this.turnIndex(player)) this._turn++;
    this.setActivePlayer(next);
    this.beginTurn(next);
  }
  turnIndex(player) {
    return [...this.players.values()].indexOf(player);
  }
  nextPlayer(player) {
    const order = [...this.players.values()];
    return order[(order.indexOf(player) + 1) % order.length];
  }
  //Construction and income first (so turn_start listeners see the new
  //stocks), movement points back, turn_start, then the player's waiting
  //move orders continue.
  beginTurn(player) {
    this.runEconomy(player);
    const units = this.unitsOf(player);
    for (const unit of units) unit.startTurn();
    this.emit("turn_start", { player, turn: this._turn });
    for (const unit of units) unit.continueOrder();
  }
  setActivePlayer(player) {
    if (player === this._activePlayer) return;
    this._activePlayer = player;
    this.selectUnit(void 0);
    if (player.fog) {
      this.recomputeFog(player);
      for (const tile of player.fog.allTiles()) this._map.setTileFog(tile.x, tile.y, tile.state);
    }
    this.updateVisibility();
    if (this.yieldsShown) this.refreshYields();
  }
  //-------------------------------------------------------------------------
  //Resources & yields
  //-------------------------------------------------------------------------
  //Sets the game's resources and yield rules (see ResourceConfig) and loads
  //their icons into the map. Can be called again to switch rule sets.
  async setResources(config) {
    this._resources = new ResourceRules(config);
    await this._map.setResourceIcons(config.resources);
    if (this.yieldsShown) this.refreshYields();
  }
  get resources() {
    return this._resources;
  }
  //What a tile yields: { resourceId: amount }, only positive amounts, in
  //the order of the resource list. {} without resources or off the map.
  tileYield(x, y) {
    const tile = this._mapData?.data[x]?.[y];
    const rules = this._resources;
    if (!tile || !rules) return {};
    const building = this.terrainBuildings.get(key({ x, y }));
    const base = rules.normalize(addYields(rules.tileYield(tile), building?.isDone ? building.type.tileYields : void 0));
    const hook = this.options.tileYield;
    return hook ? rules.normalize(hook(tile, { x, y }, base)) : base;
  }
  //Yield display mode: resource icons over every tile (except Unseen).
  get yieldsVisible() {
    return this.yieldsShown;
  }
  set yieldsVisible(value) {
    this.yieldsShown = value;
    if (value) this.refreshYields();
    else this._map.hideYields();
  }
  //Recomputes every tile's yield and redraws the overlay - call after the
  //game changed tile data (a new modifier...). A no-op while the overlay is
  //hidden.
  refreshYields() {
    if (!this.yieldsShown || !this._mapData) return;
    const cells = [];
    for (let x = 0; x < this._mapData.w; x++) {
      for (let y = 0; y < this._mapData.h; y++) {
        cells.push({ x, y, yields: this.tileYield(x, y) });
      }
    }
    this._map.showYields(cells);
  }
  //Moves up to `amount` of a resource between two stores (unloading a cart
  //in a city, picking up a mine's output...). Only into a store the
  //resource may be kept in (ResourceDef.storage), only what is there and
  //what fits. Returns the amount moved. WHO may transfer WHERE (adjacency,
  //a unit on the tile...) is the game's rule, not checked here.
  transfer(from, to, resource, amount) {
    if (!this._resources?.storageOf(resource).includes(to.kind)) return 0;
    const moved = Math.min(amount, from.get(resource), to.free);
    if (moved <= 0) return 0;
    from.take(resource, moved);
    to.add(resource, moved);
    return moved;
  }
  //-------------------------------------------------------------------------
  //Units
  //-------------------------------------------------------------------------
  //Creates a unit, loads its model and puts it on the map. Throws if the id
  //is taken, the owner is unknown or the cell is occupied.
  async createUnit(placement) {
    if (this.units.has(placement.id)) throw new Error(`Unit id "${placement.id}" already exists.`);
    const owner = this.players.get(placement.owner);
    if (!owner) throw new Error(`Unit "${placement.id}": unknown owner "${placement.owner}".`);
    const cell = { x: placement.x, y: placement.y };
    if (this.occupied.has(key(cell))) throw new Error(`Cell ${key(cell)} is occupied by "${this.occupied.get(key(cell))}".`);
    const engine = this;
    const unit = new Unit({
      ...placement,
      owner,
      size: this._map.size,
      context: {
        findPath: (u, target) => this.findPath(u.position, target, u),
        heightAt: (x, z) => this._map.heightAt(x, z),
        moveStarted: (u, path) => this.reserveMove(u, path[0], path[path.length - 1]),
        // its owner's turn, not busy building, no unit on its way aboard
        canAct: (u) => u.owner === this._activePlayer && !this.unitTasks.has(u.id) && !this.awaitsBoarders(u),
        moveCost: (u, from, to) => this.moveCost(u, from, to),
        get enterRule() {
          return engine.movement.enterRule;
        },
        foundCity: (u, info) => this.foundCityBy(u, info),
        attack: (u, target) => this.attack(u, target)
      }
    });
    await unit.setUnit();
    unit.startTurn();
    this.units.set(unit.id, unit);
    this.occupied.set(key(cell), unit.id);
    this._map.add(unit.unit);
    unit.on("before_move", (e) => this.emit("before_move", e));
    for (const event of COMBAT_EVENTS) unit.on(event, (e) => this.emit(event, e));
    unit.on("move", (e) => {
      this.emit("move", e);
      this.recomputeFog(owner);
    });
    unit.on("end_move", (e) => {
      const carrier = this.boarding.get(unit.id);
      if (carrier && carrier.position.x === e.to.x && carrier.position.y === e.to.y) {
        this.boarding.delete(unit.id);
        unit.attachTo(carrier);
        this.emit("unit_loaded", { unit, carrier });
      }
      this.emit("end_move", e);
      this.recomputeFog(owner);
    });
    this.recomputeFog(owner);
    return unit;
  }
  //Removes a unit. A construction it worked on stays (paused) - another
  //unit able to build it can resume it.
  removeUnit(id) {
    const unit = this.units.get(id);
    if (!unit) return;
    const task = this.unitTasks.get(id);
    if (task) task.builder = void 0;
    this.unitTasks.delete(id);
    for (const [cell, owner] of this.occupied) if (owner === id) this.occupied.delete(cell);
    if (this._currentUnit === unit) this.selectUnit(void 0);
    for (const aboard of unit.cargo) this.removeUnit(aboard.id);
    this.boarding.delete(id);
    this.units.delete(id);
    unit.dispose();
    this.recomputeFog(unit.owner);
  }
  getUnit(id) {
    return this.units.get(id);
  }
  //Unit standing on (or heading to) a cell - a ship, not the units aboard.
  unitAt(cell) {
    const id = this.occupied.get(key(cell));
    return id ? this.units.get(id) : void 0;
  }
  //Every unit on a cell: the one standing there, then the units aboard it.
  unitsAt(cell) {
    const unit = this.unitAt(cell);
    return unit ? [unit, ...unit.cargo] : [];
  }
  //-------------------------------------------------------------------------
  //Combat. No rules here: who may attack whom, range, damage, the result -
  //all of it is the game's listeners' code (see AttackEvent).
  //-------------------------------------------------------------------------
  //Attacks a unit, city or building - or what stands on a cell: a unit
  //(a ship, not the units aboard), else a city center, else a terrain
  //building. Fires before_attack, before_defence (either may cancel()),
  //attack, defence (awaiting what their listeners passed to waitFor()),
  //after_attack, after_defence. Refused (undefined, no events) only when
  //there is nothing to attack, the target is the attacker itself or the
  //attacker can't act now (not its owner's turn, busy building, aboard
  //a ship being boarded). Resolves the event's object (with the game's
  //`data`) after the last event, undefined if refused or cancelled.
  async attack(attacker, target) {
    const defender = target instanceof Unit || target instanceof City || target instanceof Building ? target : this.unitAt(target) ?? this.cityAt(target) ?? this.buildingAt(target);
    if (!defender || defender === attacker || this.units.get(attacker.id) !== attacker) return void 0;
    if (attacker.owner !== this._activePlayer || this.unitTasks.has(attacker.id) || this.awaitsBoarders(attacker)) return void 0;
    let cancelled = false;
    const waits = [];
    const event = {
      attacker,
      defender,
      from: attacker.position,
      to: defender.position,
      data: {},
      cancel: () => {
        cancelled = true;
      },
      waitFor: (promise) => {
        waits.push(promise);
      }
    };
    const fire = (side, name) => {
      if (side instanceof Unit) side.emit(name, event);
      else this.emit(name, event);
    };
    const settle = async () => {
      while (waits.length) await Promise.all(waits.splice(0));
    };
    fire(attacker, "before_attack");
    if (!cancelled) fire(defender, "before_defence");
    if (cancelled) return void 0;
    fire(attacker, "attack");
    fire(defender, "defence");
    await settle();
    fire(attacker, "after_attack");
    fire(defender, "after_defence");
    await settle();
    return event;
  }
  //-------------------------------------------------------------------------
  //Transport. A unit boards a ship of its owner by moving onto its cell
  //(routes may end there - see findPath) and leaves it by moving off; while
  //aboard it has no model, takes no cell, sees from the ship and moves with
  //it. load()/unload() do the same at once, without moving (no rules: who
  //may board and where is the game's / the server's business, only room
  //and a free cell are required).
  //-------------------------------------------------------------------------
  //Room left on a ship (units on their way aboard count).
  roomOn(carrier) {
    const coming = [...this.boarding.values()].filter((c) => c === carrier).length;
    return Math.max(0, carrier.capacity - carrier.cargo.length - coming);
  }
  //May `unit` board `carrier` now: the owner's ship with room, and the
  //canBoard rule (default: the unit is not a transport itself).
  canBoard(unit, carrier) {
    if (unit === carrier || unit.carrier === carrier || carrier.owner !== unit.owner) return false;
    if (carrier.capacity <= 0 || this.roomOn(carrier) <= 0) return false;
    const rule = this.options.canBoard;
    return rule ? rule(unit, carrier) : unit.capacity === 0;
  }
  //Puts a unit aboard a ship at once, wherever both stand (from another
  //ship too).
  load(unit, carrier) {
    if (unit.isMoving || !this.canBoard(unit, carrier)) return false;
    unit.cancelOrder();
    for (const [cell, owner] of this.occupied) if (owner === unit.id) this.occupied.delete(cell);
    unit.attachTo(carrier);
    this.emit("unit_loaded", { unit, carrier });
    this.recomputeFog(unit.owner);
    return true;
  }
  //Takes a unit off its ship at once onto a free cell (any - next to the
  //ship or not, the terrain is not checked).
  unload(unit, cell) {
    const carrier = unit.carrier;
    if (!carrier || this.occupied.has(key(cell)) || !this._mapData?.data[cell.x]?.[cell.y]) return false;
    unit.cancelOrder();
    unit.detach();
    unit.teleport(cell);
    this.occupied.set(key(cell), unit.id);
    this.emit("unit_unloaded", { unit, carrier, cell });
    this.recomputeFog(unit.owner);
    return true;
  }
  awaitsBoarders(carrier) {
    for (const target of this.boarding.values()) if (target === carrier) return true;
    return false;
  }
  get allUnits() {
    return [...this.units.values()];
  }
  //The construction a unit is working on (it can't move meanwhile).
  taskOf(unit) {
    return this.unitTasks.get(unit.id);
  }
  //Moves a unit by id - same as getUnit(id).move(target).
  moveUnit(id, target) {
    const unit = this.units.get(id);
    return unit ? unit.move(target) : Promise.resolve(false);
  }
  //-------------------------------------------------------------------------
  //Cities
  //-------------------------------------------------------------------------
  //Founds a city: its model and label on the tile, territory = tiles within
  //its radius not owned by another city, buildings already standing there
  //become its own. No game rules (who may found a city where - that is the
  //game's / the server's call); throws only when the map can't take it: an
  //unknown owner, a taken id, water, a tile another city owns or a building
  //stands on. `unit` is only reported in city_founded (see Unit.foundCity).
  async foundCity(placement, unit) {
    const id = placement.id ?? this.newId("city");
    if (this.cities.has(id)) throw new Error(`City id "${id}" already exists.`);
    const owner = this.players.get(placement.owner);
    if (!owner) throw new Error(`City "${id}": unknown owner "${placement.owner}".`);
    const cell = { x: placement.x, y: placement.y };
    const tile = this._mapData.data[cell.x]?.[cell.y];
    if (!tile || WATER.includes(tile.type)) throw new Error(`City "${id}": ${key(cell)} is not land.`);
    if (this.territory.has(key(cell))) throw new Error(`City "${id}": ${key(cell)} belongs to another city.`);
    if (this.terrainBuildings.has(key(cell))) throw new Error(`City "${id}": a building stands on ${key(cell)}.`);
    const city = new City({
      id,
      name: placement.name,
      owner,
      x: cell.x,
      y: cell.y,
      radius: placement.radius ?? this.options.cityRadius,
      model: placement.model ?? this.options.cityModel,
      scale: placement.scale
    });
    await city.load(this._map.size, (x, z) => this._map.heightAt(x, z));
    city.territory = tilesWithinRange(this._mapData, cell.x, cell.y, city.radius).filter((t) => !this.territory.has(key(t)));
    if (!city.territory.some((t) => t.x === cell.x && t.y === cell.y)) city.territory.push(cell);
    for (const t of city.territory) this.territory.set(key(t), city);
    for (const building of this.buildings.values()) {
      if (!building.city && city.isInTerritory(building.position)) this.attachToCity(building, city);
    }
    this.cities.set(city.id, city);
    this._map.add(city.object);
    this._map.setTileCleared(cell.x, cell.y, true);
    this.emit("city_founded", { city, unit });
    this.recomputeFog(owner);
    return city;
  }
  //Hands a city over to another player - capture, a peace treaty, a revolt:
  //WHEN that happens is game logic, this only does it. Goes with the city:
  //its store, its buildings (in the city and on its territory), its
  //construction. Constructions the old owner's units were working on stop
  //(paused - the new owner's units can resume them).
  setCityOwner(city, owner) {
    const from = city.owner;
    if (from === owner) return;
    city.owner = owner;
    for (const building of city.buildings) this.changeBuildingOwner(building, owner);
    this.emit("city_owner_changed", { city, from, to: owner });
    this.recomputeFog(from);
    this.recomputeFog(owner);
    if (this.yieldsShown) this.refreshYields();
  }
  //Hands one building over (e.g. a captured mine outside any city).
  setBuildingOwner(building, owner) {
    if (building.owner === owner) return;
    this.changeBuildingOwner(building, owner);
    this.updateVisibility();
  }
  changeBuildingOwner(building, owner) {
    const from = building.owner;
    if (from === owner) return;
    building.owner = owner;
    if (building.builder instanceof Unit) this.releaseBuilder(building);
    this.emit("building_owner_changed", { building, from, to: owner });
  }
  //Removes a city (razed, abandoned). Its territory becomes free; its
  //buildings stay with their owner, no longer part of any city ("city"
  //placement ones are removed with it - they have nowhere to stand).
  removeCity(id) {
    const city = this.cities.get(id);
    if (!city) return;
    if (city.construction) this.releaseBuilder(city.construction);
    for (const building of [...city.buildings]) {
      if (building.type.placement === "city") this.removeBuilding(building.id);
      else building.city = void 0;
    }
    for (const t of city.territory) this.territory.delete(key(t));
    this.cities.delete(id);
    for (const known of this.memory.values()) known.delete(city);
    if (!this.terrainBuildings.has(key(city.position))) this._map.setTileCleared(city.position.x, city.position.y, false);
    city.dispose();
    this.emit("city_removed", { city });
    this.recomputeFog(city.owner);
    if (this.yieldsShown) this.refreshYields();
  }
  //Unit.foundCity(): a city of the unit's owner on the unit's tile; the unit
  //is removed if info.consume.
  async foundCityBy(unit, info) {
    const { consume, ...placement } = info;
    const city = await this.foundCity({ ...placement, owner: unit.owner.id, x: unit.position.x, y: unit.position.y }, unit);
    if (consume) this.removeUnit(unit.id);
    return city;
  }
  getCity(id) {
    return this.cities.get(id);
  }
  get allCities() {
    return [...this.cities.values()];
  }
  citiesOf(player) {
    const id = typeof player === "string" ? player : player.id;
    return this.allCities.filter((c) => c.owner.id === id);
  }
  //The city whose center is on this tile.
  cityAt(cell) {
    const city = this.territory.get(key(cell));
    return city && city.position.x === cell.x && city.position.y === cell.y ? city : void 0;
  }
  //The city whose territory contains this tile.
  cityOf(cell) {
    return this.territory.get(key(cell));
  }
  //A city's production list (by id, center tile or the city itself) from the
  //productionList option; undefined without the option or a city there.
  async cityProductionList(city) {
    const target = typeof city === "string" ? this.getCity(city) : city instanceof City ? city : this.cityAt(city);
    const source = this.options.productionList;
    return target && source ? source(target) : void 0;
  }
  //Work a city puts into its construction per turn (cityWork option).
  cityWork(city) {
    const work = this.options.cityWork;
    return typeof work === "function" ? work(city) : work;
  }
  //A city's income per turn: the yields of its territory plus its finished
  //buildings' cityYields (through the cityIncome hook, if set).
  cityIncome(city) {
    let income = {};
    for (const t of city.territory) income = addYields(income, this.tileYield(t.x, t.y));
    for (const b of city.buildings) if (b.isDone) income = addYields(income, b.type.cityYields);
    const hook = this.options.cityIncome;
    return hook ? hook(city, income) : income;
  }
  //-------------------------------------------------------------------------
  //Buildings
  //-------------------------------------------------------------------------
  //The game's building types (see BuildingType). Replaces the previous set.
  setBuildingTypes(types) {
    this.buildingTypes = new Map(types.map((t) => [t.id, t]));
  }
  getBuildingType(id) {
    return this.buildingTypes.get(id);
  }
  get allBuildingTypes() {
    return [...this.buildingTypes.values()];
  }
  getBuilding(id) {
    return this.buildings.get(id);
  }
  get allBuildings() {
    return [...this.buildings.values()];
  }
  buildingsOf(player) {
    const id = typeof player === "string" ? player : player.id;
    return this.allBuildings.filter((b) => b.owner.id === id);
  }
  //The terrain building (built or not) on a tile.
  buildingAt(cell) {
    return this.terrainBuildings.get(key(cell));
  }
  //Why `builder` can't build `typeId` (at `cell`, for a city building a
  //terrain building) right now - or null if it can. A unit always builds
  //on its own tile. Covers turn, placement, requirements, uniqueness,
  //charges and an upfront cost.
  canBuild(builder, typeId, cell) {
    const site = this.buildSite(builder, typeId, cell);
    return typeof site === "string" ? site : null;
  }
  //Starts building (see BuildingType): spends an upfront cost, then either
  //finishes at once (work 0, or a unit build with charges - the unit spends
  //them and is removed when it has none left) or puts the builder to work:
  //a unit stays busy on the tile, a city works on it instead of anything
  //else. Progress comes at the start of each of the owner's turns.
  //A unit on an unfinished construction of the same type (left by another
  //unit) resumes it. Throws with the canBuild() reason if not possible.
  async build(builder, typeId, cell) {
    const site = this.buildSite(builder, typeId, cell);
    if (typeof site === "string") throw new Error(site);
    const { type, position, city, resume } = site;
    if (resume) {
      this.assignBuilder(resume, builder);
      this.emit("build_start", { building: resume, builder });
      return resume;
    }
    const instant = (type.work ?? 0) <= 0 || builder instanceof Unit && (type.charges ?? 0) > 0;
    if (type.cost && (instant || type.costMode !== "perTurn")) {
      spend(this._resources, type.cost, this.storeSources(builder.owner, city, builder));
    }
    const building = new Building(this.newId("b"), type, builder.owner, position);
    await this.addBuilding(building, city);
    this.emit("build_start", { building, builder });
    if (instant) {
      if (builder instanceof Unit && (type.charges ?? 0) > 0) {
        builder.useCharges(type.charges);
        if (builder.charges <= 0) this.removeUnit(builder.id);
      }
      this.completeBuilding(building);
    } else {
      this.assignBuilder(building, builder);
    }
    return building;
  }
  //Drops an unfinished construction (no refund) and frees its builder.
  cancelBuild(building) {
    if (building.isDone) return;
    this.removeBuilding(building.id);
  }
  //Removes a building, built or not (its resources in store are lost).
  removeBuilding(id) {
    const building = this.buildings.get(id);
    if (!building) return;
    this.releaseBuilder(building);
    this.buildings.delete(id);
    if (this.terrainBuildings.get(key(building.position)) === building) {
      this.terrainBuildings.delete(key(building.position));
      if (!this.cityAt(building.position)) this._map.setTileCleared(building.position.x, building.position.y, false);
    }
    if (building.city) {
      const list = building.city.buildings;
      list.splice(list.indexOf(building), 1);
    }
    for (const known of this.memory.values()) known.delete(building);
    building.dispose();
    this.emit("building_removed", { building });
    if (building.isDone && building.type.tileYields) this.refreshYields();
  }
  //Puts an already finished building (GameData.buildings). No costs or
  //builder rules - only the type, owner and a free tile are checked.
  async placeBuilding(placement) {
    const type = this.buildingTypes.get(placement.type);
    if (!type) throw new Error(`Unknown building type "${placement.type}".`);
    const owner = this.players.get(placement.owner);
    if (!owner) throw new Error(`Building "${placement.type}": unknown owner "${placement.owner}".`);
    const cell = { x: placement.x, y: placement.y };
    if (type.placement === "terrain" && this.terrainBuildings.has(key(cell))) throw new Error(`A building already stands on ${key(cell)}.`);
    if (type.placement === "city" && !this.cityAt(cell)) throw new Error(`Building "${type.id}" must stand in a city - ${key(cell)} is not one.`);
    const building = new Building(placement.id ?? this.newId("b"), type, owner, cell);
    if (this.buildings.has(building.id)) throw new Error(`Building id "${building.id}" already exists.`);
    await this.addBuilding(building, this.cityOf(cell));
    this.completeBuilding(building);
    return building;
  }
  //Validates a build order - the reason as a string, or where it goes.
  buildSite(builder, typeId, cell) {
    const type = this.buildingTypes.get(typeId);
    if (!type) return `Unknown building type "${typeId}".`;
    const owner = builder.owner;
    if (owner !== this._activePlayer) return `Not ${owner.name}'s turn.`;
    const byUnit = builder instanceof Unit;
    const builtBy = type.builtBy ?? ["city"];
    if (!builtBy.includes(byUnit ? "unit" : "city")) return `${type.id} can't be built by a ${byUnit ? "unit" : "city"}.`;
    let position;
    let city;
    if (builder instanceof Unit) {
      if (!builder.builds.includes(type.id)) return `Unit ${builder.id} can't build ${type.id}.`;
      if (this.unitTasks.has(builder.id)) return `Unit ${builder.id} is busy building.`;
      if (builder.isMoving) return `Unit ${builder.id} is moving.`;
      position = builder.position;
      if (cell && key(cell) !== key(position)) return "A unit builds on its own tile.";
      if ((type.charges ?? 0) > 0 && builder.charges < type.charges) return `Unit ${builder.id} has ${builder.charges} charges, ${type.id} needs ${type.charges}.`;
      city = type.placement === "city" ? this.cityAt(position) : this.cityOf(position);
      if (type.placement === "city" && !city) return `${type.id} is built in a city - stand on one.`;
    } else {
      city = builder;
      if (city.construction) return `${city.name} is already building ${city.construction.type.id}.`;
      if (type.placement === "city") {
        position = city.position;
      } else {
        if (!cell) return `Where? ${type.id} stands on a tile - give one in the city's territory.`;
        if (!city.isInTerritory(cell)) return `${key(cell)} is outside ${city.name}'s territory.`;
        position = cell;
      }
    }
    if (city && city.owner !== owner) return `${city.name} belongs to ${city.owner.name}.`;
    if (type.placement === "city") {
      if ((type.unique ?? true) && city.buildings.some((b) => b.type.id === type.id)) return `${city.name} already has ${type.id}.`;
    } else {
      const tile = this._mapData.data[position.x]?.[position.y];
      if (!tile) return `No tile at ${key(position)}.`;
      if (this.cityAt(position)) return `${key(position)} is a city center.`;
      const existing = this.terrainBuildings.get(key(position));
      if (existing) {
        if (byUnit && !existing.isDone && !existing.builder && existing.type.id === type.id && existing.owner === owner) {
          return { type, position, city, resume: existing };
        }
        return `${existing.type.id} already stands on ${key(position)}.`;
      }
      if (type.terrain && !type.terrain.includes(tile.type)) return `${type.id} can't stand on ${tile.type}.`;
      const mods = tile.modifiers ?? [];
      if (type.modifiers && !type.modifiers.some((m) => mods.includes(m))) return `${type.id} needs ${type.modifiers.join(" or ")}.`;
      if (type.noModifiers?.some((m) => mods.includes(m))) return `${type.id} can't stand on ${type.noModifiers.filter((m) => mods.includes(m)).join(", ")}.`;
      if ((type.inTerritory ?? true) && city?.owner !== owner) return `${type.id} must stand in ${owner.name}'s territory.`;
      if (type.unique && city?.buildings.some((b) => b.type.id === type.id)) return `${city.name} already has ${type.id}.`;
    }
    const instant = (type.work ?? 0) <= 0 || byUnit && (type.charges ?? 0) > 0;
    if (type.cost && (instant || type.costMode !== "perTurn")) {
      if (!this._resources) return "No resources configured (setResources).";
      const missing = missingFor(this._resources, type.cost, this.storeSources(owner, city, builder));
      if (Object.keys(missing).length > 0) {
        return `Not enough: ${Object.entries(missing).map(([id, n]) => `${id} ${n}`).join(", ")}.`;
      }
    }
    return { type, position, city };
  }
  //The stores a construction (or anything of `owner` in `city`) may spend
  //from - see ResourceDef.spendFrom.
  storeSources(owner, city, builder) {
    return {
      player: owner.storage,
      city: city?.owner === owner ? city.storage : void 0,
      building: city?.owner === owner ? city.buildings.filter((b) => b.isDone && b.storage).map((b) => b.storage) : [],
      unit: builder instanceof Unit ? builder.storage : void 0
    };
  }
  async addBuilding(building, city) {
    this.buildings.set(building.id, building);
    if (city) this.attachToCity(building, city);
    if (building.type.placement === "terrain") {
      this.terrainBuildings.set(key(building.position), building);
      if (building.type.clearsTile ?? true) this._map.setTileCleared(building.position.x, building.position.y, true);
      await building.load(this._map.size, (x, z) => this._map.heightAt(x, z));
      this._map.add(building.object);
      this.updateVisibility();
    }
  }
  attachToCity(building, city) {
    building.city = city;
    city.buildings.push(building);
  }
  assignBuilder(building, builder) {
    building.builder = builder;
    if (builder instanceof Unit) {
      this.unitTasks.set(builder.id, building);
      builder.cancelOrder();
    } else {
      builder.construction = building;
    }
  }
  releaseBuilder(building) {
    const builder = building.builder;
    if (builder instanceof Unit) this.unitTasks.delete(builder.id);
    else if (builder && builder.construction === building) builder.construction = void 0;
    building.builder = void 0;
  }
  completeBuilding(building) {
    building.state = "done";
    building.progress = building.work;
    building.paused = false;
    building.refreshLook();
    this.releaseBuilder(building);
    this.emit("build_complete", { building });
    if (building.type.tileYields) this.refreshYields();
  }
  //One turn of the player's economy: construction progress (and perTurn
  //costs), then what its buildings and cities produce.
  runEconomy(player) {
    const rules = this._resources;
    for (const building of this.buildingsOf(player)) {
      const builder = building.builder;
      if (building.isDone || !builder) continue;
      if (building.type.cost && building.type.costMode === "perTurn") {
        const sources = this.storeSources(player, building.city, builder);
        if (!rules || !spend(rules, building.type.cost, sources)) {
          const missing = rules ? missingFor(rules, building.type.cost, sources) : building.type.cost;
          building.paused = true;
          this.emit("build_paused", { building, missing });
          continue;
        }
      }
      building.paused = false;
      building.progress += builder instanceof City ? this.cityWork(builder) : builder.work;
      this.emit("build_progress", { building });
      if (building.progress >= building.work) this.completeBuilding(building);
    }
    if (!rules) return;
    for (const building of this.buildingsOf(player)) {
      if (!building.isDone || !building.type.produces) continue;
      deposit(rules, building.type.produces, {
        building: building.storage ? [building.storage] : [],
        city: building.city?.owner === player ? building.city.storage : void 0,
        player: player.storage
      });
    }
    for (const city of this.citiesOf(player)) {
      deposit(rules, this.cityIncome(city), { city: city.storage, player: player.storage });
    }
  }
  //-------------------------------------------------------------------------
  //Routing
  //-------------------------------------------------------------------------
  //Route for `unit` (defaults to the selected one) from start to stop, or []
  //if there is none. Respects the unit's terrain flags, never enters tiles
  //its owner has not seen, and never enters a cell another unit (of any
  //player) stands on or is heading to.
  //
  //Called by Unit.move() through its context; once a leg starts its end is
  //reserved (see reserveMove), so two units can't be sent to the same cell.
  findPath(start, stop, unit = this._currentUnit) {
    return this.pathFinder(unit).find(start.x, start.y, stop.x, stop.y);
  }
  //Cells `unit` can reach this turn with its movement points left (by the
  //same rules as findPath), its own cell excluded. A selected unit shows
  //them on the map (HexMap.showMoveArea).
  reachableTiles(unit) {
    return this.pathFinder(unit).reachable(unit.position.x, unit.position.y);
  }
  //Turn in which `unit` stands on each cell of a route from findPath (0 =
  //this turn; path[0] = where it is). The route preview marks where each
  //turn ends.
  routeTurns(unit, path) {
    return routeTurns(path, (from, to) => this.moveCost(unit, from, to), this.turnBudget(unit));
  }
  //With a unit the routes are counted in turns from its movement points
  //(the fewest turns, see PathFinder); without one every step costs 1.
  pathFinder(unit) {
    const restrictions = unit ? unit.terrain : {
      sea: true,
      coastal: true,
      land: true,
      sand: true,
      tundra: true,
      snow: true,
      mountain: true
    };
    const fog = (unit?.owner ?? this._activePlayer)?.fog;
    return new PathFinder(this._mapData, restrictions, (x, y) => {
      if (fog && fog.getState(x, y) === 0 /* Unseen */) return false;
      const owner = this.occupied.get(`${x},${y}`);
      return owner === void 0 || owner === unit?.id;
    }, unit ? {
      cost: (from, to) => this.moveCost(unit, from, to),
      minCost: this.minMoveCost(unit),
      budget: this.turnBudget(unit),
      //a ship it may board: the route can end on it
      endOnly: (x, y) => {
        const ship = this.unitAt({ x, y });
        return !!ship && !ship.isMoving && this.canBoard(unit, ship);
      }
    } : {});
  }
  turnBudget(unit) {
    return { movesLeft: unit.movesLeft, movement: unit.movement, enterRule: this.movement.enterRule };
  }
  //-------------------------------------------------------------------------
  //Movement costs (see MovementRules)
  //-------------------------------------------------------------------------
  //The game's movement costs. Replaces the previous rules.
  setMovementRules(rules) {
    this.movement = rules;
  }
  get movementRules() {
    return this.movement;
  }
  //Movement points `unit` spends stepping from -> to (a neighbor): the
  //target tile's terrain + modifiers by the rules and the unit's own
  //moveCosts, then the moveCost hook. Infinity = it can't.
  moveCost(unit, from, to) {
    const tile = this._mapData.data[to.x]?.[to.y];
    if (!tile) return Infinity;
    const own = unit.moveCosts;
    const price = (value, fallback) => value === null ? Infinity : value ?? fallback;
    let cost = price(own?.terrain?.[tile.type] !== void 0 ? own.terrain[tile.type] : this.movement.terrain?.[tile.type], 1);
    for (const modifier of tile.modifiers ?? []) {
      cost += price(own?.modifiers?.[modifier] !== void 0 ? own.modifiers[modifier] : this.movement.modifiers?.[modifier], 0);
    }
    const hook = this.options.moveCost;
    return hook ? hook(unit, from, to, cost) : cost;
  }
  //Movement points a route costs `unit` (path[0] = where it stands).
  pathCost(unit, path) {
    let cost = 0;
    for (let i = 1; i < path.length; i++) cost += this.moveCost(unit, path[i - 1], path[i]);
    return cost;
  }
  //The cheapest step there can be for the unit - keeps PathFinder's
  //heuristic admissible. With a moveCost hook (roads can be cheaper than
  //any terrain) there is no telling: 0, plain Dijkstra.
  minMoveCost(unit) {
    if (this.options.moveCost) return 0;
    const values = [1, ...Object.values(this.movement.terrain ?? {}), ...Object.values(unit.moveCosts?.terrain ?? {})].filter((v) => typeof v === "number");
    const discounts = [...Object.values(this.movement.modifiers ?? {}), ...Object.values(unit.moveCosts?.modifiers ?? {})].filter((v) => typeof v === "number" && v < 0);
    return Math.max(0, Math.min(...values) + discounts.reduce((a, b) => a + b, 0));
  }
  //-------------------------------------------------------------------------
  //Input: select a unit by clicking it, click a cell to send it there, hover
  //previews the route.
  //-------------------------------------------------------------------------
  selectUnit(unit) {
    this._currentUnit = unit;
    this._map.cleanRoutePath();
    if (unit && !unit.isMoving) this._map.showMoveArea([unit.position, ...this.reachableTiles(unit)]);
    else this._map.clearMoveArea();
  }
  cellHover(payload) {
    this._map.cleanRoutePath();
    const unit = this._currentUnit;
    if (unit && !unit.isMoving) {
      const path = this.findPath(unit.position, payload, unit);
      if (path.length > 1) this._map.drawRoutePath(path, this.routeTurns(unit, path));
    }
    this.emit("hover", payload);
  }
  //A click on a unit the active player can see reports it (unitClick) and
  //selects it if it is the active player's own - clicking the selected
  //unit's cell again selects the next unit there (a ship, then the units
  //aboard). With a unit selected a click on another player's visible unit
  //or city center attacks it (attack() - the game's before_attack decides
  //if it may), a click anywhere else sends it there (onto a ship of its
  //owner = board it); otherwise a click on a visible city reports it
  //(cityClick).
  cellClick({ x, y }) {
    const cell = { x, y };
    const clicked = this.unitAt(cell);
    const current = this._currentUnit;
    const boards = !!clicked && !!current && clicked !== current.carrier && this.canBoard(current, clicked);
    if (current && !boards) {
      const foe = clicked?.unit.visible ? clicked : clicked ? void 0 : this.cityAt(cell);
      const visible = foe instanceof City ? foe.object.visible : !!foe;
      if (foe && visible && foe.owner !== current.owner) {
        void this.attack(current, foe);
        return;
      }
    }
    if (clicked && clicked.unit.visible && !boards) {
      if (!this.options.preventCellClick) this.emit("click", cell);
      let picked = clicked;
      if (clicked.owner === this._activePlayer) {
        const stack = this.unitsAt(cell);
        if (current && stack.includes(current)) picked = stack[(stack.indexOf(current) + 1) % stack.length];
      }
      this.selectUnit(picked.owner === this._activePlayer ? picked : void 0);
      this.emit("unitClick", { unit: picked, x, y });
      return;
    }
    const unit = current;
    if (unit) void unit.move(cell);
    this.selectUnit(void 0);
    const city = this.cityAt(cell);
    if (!unit && city?.object.visible) this.emit("cityClick", { city, x, y });
    this.emit("click", cell);
  }
  //Occupancy follows the move: the destination is reserved when the move
  //really starts (UnitContext.moveStarted - after before_move listeners had
  //their chance to cancel), the start cell is freed at the same moment.
  //Boarding: the ship keeps its cell, the unit is expected aboard (see
  //end_move in createUnit); leaving a ship: off it the moment it starts.
  reserveMove(unit, from, to) {
    const carrier = unit.carrier;
    if (carrier) {
      unit.detach();
      this.emit("unit_unloaded", { unit, carrier, cell: to });
    }
    if (this.occupied.get(key(from)) === unit.id) this.occupied.delete(key(from));
    const ship = this.unitAt(to);
    if (ship && ship !== unit && this.canBoard(unit, ship)) this.boarding.set(unit.id, ship);
    else this.occupied.set(key(to), unit.id);
    this.updateVisibility();
  }
  //-------------------------------------------------------------------------
  //Fog of war, per player: recomputed from the player's units' current
  //cells (a unit advances cell by cell during a move, so tiles reveal
  //progressively along the route) and its cities (radius + 1). Only the
  //active player's fog is drawn.
  //-------------------------------------------------------------------------
  recomputeFog(player) {
    if (player.fog) {
      const viewers = [
        ...this.unitsOf(player).map((u) => ({ ...u.position, viewRange: u.viewRange })),
        ...this.citiesOf(player).map((c) => ({ ...c.position, viewRange: c.radius + 1 }))
      ];
      const changes = player.fog.recompute(viewers);
      if (player === this._activePlayer) {
        for (const change of changes) this._map.setTileFog(change.x, change.y, change.state);
      }
    }
    this.updateVisibility();
  }
  //What the active player sees. Units: its own, and others' on tiles in its
  //view now. Cities and terrain buildings: its own, and others' it has ever
  //seen - as it saw them (the owner then), dimmed while out of view, like
  //the Explored terrain under them. A city founded or captured behind the
  //fog stays unknown / unchanged for the player until it looks again.
  updateVisibility() {
    this.rememberVisible();
    const active = this._activePlayer;
    const fog = active?.fog;
    const known = active ? this.memory.get(active) : void 0;
    const dimmed = this._map.fogDarkenFactor;
    const state = (p) => fog ? fog.getState(p.x, p.y) : 2 /* Visible */;
    for (const unit of this.units.values()) {
      unit.unit.visible = !unit.carrier && (unit.owner === active || state(unit.position) === 2 /* Visible */);
    }
    const show = (thing) => {
      const seenOwner = !fog || thing.owner === active ? thing.owner : known?.get(thing);
      thing.object.visible = seenOwner !== void 0;
      if (seenOwner) thing.setLook(seenOwner, state(thing.position) === 2 /* Visible */ ? 1 : dimmed);
    };
    for (const city of this.cities.values()) show(city);
    for (const building of this.terrainBuildings.values()) show(building);
  }
  //Every player remembers others' cities / terrain buildings on tiles in
  //its view now, with their current owner.
  rememberVisible() {
    for (const player of this.players.values()) {
      if (!player.fog) continue;
      let known = this.memory.get(player);
      if (!known) this.memory.set(player, known = /* @__PURE__ */ new Map());
      const see = (thing) => {
        if (thing.owner !== player && player.fog.getState(thing.position.x, thing.position.y) === 2 /* Visible */) known.set(thing, thing.owner);
      };
      for (const city of this.cities.values()) see(city);
      for (const building of this.terrainBuildings.values()) see(building);
    }
  }
  //Does the player know of this city / terrain building (its own, or seen)?
  knows(player, thing) {
    return !player.fog || thing.owner === player || this.memory.get(player)?.has(thing) === true;
  }
  newId(prefix) {
    let id;
    do
      id = `${prefix}${++this.nextId}`;
    while (this.cities.has(id) || this.buildings.has(id));
    return id;
  }
  get currentUnit() {
    return this._currentUnit;
  }
  get map() {
    return this._map;
  }
  //The active player's fog of war.
  get fogOfWar() {
    return this._activePlayer?.fog;
  }
};

// src/styles/textured/TerrainMesh.ts
import {
  InstancedBufferGeometry as InstancedBufferGeometry2,
  InstancedBufferAttribute as InstancedBufferAttribute2,
  Mesh as Mesh6,
  RawShaderMaterial,
  TextureLoader,
  Vector3 as Vector39,
  Color as Color5,
  Group as Group5,
  RepeatWrapping,
  NearestFilter,
  LinearFilter as LinearFilter2,
  DataTexture,
  RGBAFormat,
  UnsignedByteType,
  GLSL3,
  Vector2 as Vector22
} from "three";

// src/helpers/rivers.ts
function isRiverTile(tile) {
  return !!tile?.modifiers?.includes("river");
}
function isLakeTile(tile) {
  return !!tile?.modifiers?.includes("lake");
}
function isSeaOrCoastal(tile) {
  return tile?.type === "sea" /* sea */ || tile?.type === "coastal" /* coastal */;
}
var key2 = (p) => `${p.x},${p.y}`;
function tileHash(x, y, salt) {
  const v = Math.sin(x * 127.1 + y * 311.7 + salt * 74.7) * 43758.5453123;
  return v - Math.floor(v);
}
function buildRiverNetwork(map, options) {
  const { size } = options;
  const baseWidth = (options.riverWidth ?? 0.1) * size;
  const maxWidth = Math.max(options.riverMaxWidth ?? 0.25, options.riverWidth ?? 0.1) * size;
  const meander = options.riverMeander ?? 0.5;
  const tileAt = (p) => map.data[p.x]?.[p.y];
  const center = (p) => {
    const c = getHexCenter(p.x, p.y, size);
    return { x: c.x, z: c.y };
  };
  const lakeOf = /* @__PURE__ */ new Map();
  const lakeTiles = [];
  for (let x = 0; x < map.w; x++) {
    for (let y = 0; y < map.h; y++) {
      if (!isLakeTile(tileAt({ x, y })) || lakeOf.has(key2({ x, y }))) continue;
      const id = lakeTiles.length;
      const body = [];
      const stack = [{ x, y }];
      lakeOf.set(key2({ x, y }), id);
      while (stack.length) {
        const p = stack.pop();
        body.push(p);
        for (const n of getNeighbors(p.x, p.y)) {
          if (isLakeTile(tileAt(n)) && !lakeOf.has(key2(n))) {
            lakeOf.set(key2(n), id);
            stack.push({ x: n.x, y: n.y });
          }
        }
      }
      lakeTiles.push(body);
    }
  }
  const nodes = /* @__PURE__ */ new Map();
  const adjacency = /* @__PURE__ */ new Map();
  const nodeKey = (n) => n.kind === "river" ? key2(n.tile) : `lake:${n.lake}`;
  const link = (a, b) => {
    adjacency.get(a).add(b);
    adjacency.get(b).add(a);
  };
  lakeTiles.forEach((_, lake) => {
    const n = { kind: "lake", lake };
    nodes.set(nodeKey(n), n);
    adjacency.set(nodeKey(n), /* @__PURE__ */ new Set());
  });
  const mouths = /* @__PURE__ */ new Map();
  for (let x = 0; x < map.w; x++) {
    for (let y = 0; y < map.h; y++) {
      if (!isRiverTile(tileAt({ x, y })) || isLakeTile(tileAt({ x, y }))) continue;
      const n = { kind: "river", tile: { x, y } };
      nodes.set(key2({ x, y }), n);
      adjacency.set(key2({ x, y }), /* @__PURE__ */ new Set());
    }
  }
  for (const [k, n] of nodes) {
    if (n.kind !== "river") continue;
    for (const nb of getNeighbors(n.tile.x, n.tile.y)) {
      const t = tileAt(nb);
      if (isLakeTile(t)) link(k, `lake:${lakeOf.get(key2(nb))}`);
      else if (isRiverTile(t)) link(k, key2(nb));
      else if (isSeaOrCoastal(t) && !mouths.has(k)) mouths.set(k, { x: nb.x, y: nb.y });
    }
  }
  const dist = /* @__PURE__ */ new Map();
  const bfs = (sources2) => {
    const queue = [...sources2];
    sources2.forEach((s) => dist.set(s, 0));
    for (let i = 0; i < queue.length; i++) {
      const cur = queue[i];
      for (const nb of adjacency.get(cur)) {
        if (dist.has(nb)) continue;
        dist.set(nb, dist.get(cur) + 1);
        queue.push(nb);
      }
    }
  };
  bfs([...mouths.keys()]);
  for (; ; ) {
    const rest = [...nodes.keys()].filter((k) => !dist.has(k));
    if (rest.length === 0) break;
    const lake = rest.find((k) => nodes.get(k).kind === "lake");
    const leaf = rest.reduce((a, b) => adjacency.get(b).size < adjacency.get(a).size ? b : a);
    bfs([lake ?? leaf]);
  }
  const downstream = /* @__PURE__ */ new Map();
  for (const k of nodes.keys()) {
    let best;
    for (const nb of [...adjacency.get(k)].sort()) {
      if (dist.get(nb) < dist.get(k) && (best === void 0 || dist.get(nb) < dist.get(best))) best = nb;
    }
    downstream.set(k, best);
  }
  const accumulation = /* @__PURE__ */ new Map();
  const byDistDesc = [...nodes.keys()].sort((a, b) => dist.get(b) - dist.get(a));
  byDistDesc.forEach((k) => accumulation.set(k, nodes.get(k).kind === "river" ? 1 : 2));
  for (const k of byDistDesc) {
    const d = downstream.get(k);
    if (d) accumulation.set(d, accumulation.get(d) + accumulation.get(k));
  }
  const widthFor = (k) => Math.min(maxWidth, baseWidth * (1 + 0.3 * (accumulation.get(k) - 1)));
  const controlPoint = /* @__PURE__ */ new Map();
  for (const [k, n] of nodes) {
    if (n.kind !== "river") continue;
    const c = center(n.tile);
    const angle = tileHash(n.tile.x, n.tile.y, 1) * Math.PI * 2;
    const radius = meander * 0.4 * size * (0.4 + 0.6 * tileHash(n.tile.x, n.tile.y, 2));
    controlPoint.set(k, { x: c.x + Math.cos(angle) * radius, z: c.z + Math.sin(angle) * radius });
  }
  const lakeTileNextTo = (tile, lake) => {
    const nb = getNeighbors(tile.x, tile.y).find((p) => lakeOf.get(key2(p)) === lake);
    return center(nb);
  };
  const upstreamRivers = /* @__PURE__ */ new Map();
  for (const [k, d] of downstream) {
    if (d && nodes.get(k).kind === "river") upstreamRivers.set(d, (upstreamRivers.get(d) ?? 0) + 1);
  }
  const sources = [...nodes.keys()].filter((k) => nodes.get(k).kind === "river" && !upstreamRivers.has(k)).sort((a, b) => dist.get(b) - dist.get(a));
  const visited = /* @__PURE__ */ new Set();
  const rivers = [];
  for (const source of sources) {
    const control = [];
    const sourceTile = nodes.get(source).tile;
    const lakeNbs = [...adjacency.get(source)].filter((nb) => nodes.get(nb).kind === "lake" && nb !== downstream.get(source));
    const upLake = lakeNbs.find((nb) => downstream.get(nb) === source) ?? lakeNbs[0];
    if (upLake) {
      const c = lakeTileNextTo(sourceTile, nodes.get(upLake).lake);
      control.push({ ...c, width: widthFor(source) });
    }
    let cur = source;
    for (; ; ) {
      const tile = nodes.get(cur).tile;
      control.push({ ...controlPoint.get(cur), width: widthFor(cur) });
      if (visited.has(cur)) break;
      visited.add(cur);
      const down = downstream.get(cur);
      const mouth = mouths.get(cur);
      const c = center(tile);
      if (!down && mouth) {
        const s = center(mouth);
        const w = widthFor(cur);
        control.push({ x: (c.x + s.x) / 2, z: (c.z + s.z) / 2, width: w * 1.5 });
        control.push({ x: c.x + (s.x - c.x) * 0.85, z: c.z + (s.z - c.z) * 0.85, width: w * 2.2 });
        break;
      }
      if (!down) break;
      const dn = nodes.get(down);
      if (dn.kind === "lake") {
        control.push({ ...lakeTileNextTo(tile, dn.lake), width: widthFor(cur) });
        break;
      }
      const n = center(dn.tile);
      control.push({ x: (c.x + n.x) / 2, z: (c.z + n.z) / 2, width: (widthFor(cur) + widthFor(down)) / 2 });
      cur = down;
    }
    rivers.push({ points: smoothPath(control) });
  }
  for (const [k, n] of nodes) {
    const down = downstream.get(k);
    if (n.kind !== "lake" || !down || sources.includes(down)) continue;
    const tile = nodes.get(down).tile;
    rivers.push({ points: smoothPath([
      { ...lakeTileNextTo(tile, n.lake), width: widthFor(down) },
      { ...controlPoint.get(down), width: widthFor(down) }
    ]) });
  }
  const lakes = lakeTiles.map((body) => ({ centers: body.map(center) }));
  return { rivers, lakes };
}
function smoothPath(points) {
  if (points.length < 3) return points;
  const SAMPLES = 8;
  const out = [];
  const at = (i) => points[Math.min(Math.max(i, 0), points.length - 1)];
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = i === 0 ? extrapolate(points[1], points[0]) : at(i - 1);
    const p1 = at(i), p2 = at(i + 1);
    const p3 = i + 2 >= points.length ? extrapolate(points[points.length - 2], points[points.length - 1]) : at(i + 2);
    const t0 = 0;
    const t1 = t0 + Math.sqrt(Math.hypot(p1.x - p0.x, p1.z - p0.z)) + 1e-4;
    const t2 = t1 + Math.sqrt(Math.hypot(p2.x - p1.x, p2.z - p1.z)) + 1e-4;
    const t3 = t2 + Math.sqrt(Math.hypot(p3.x - p2.x, p3.z - p2.z)) + 1e-4;
    for (let s = 0; s < SAMPLES; s++) {
      const t = t1 + (t2 - t1) * (s / SAMPLES);
      const lerp = (a, b, ta, tb) => {
        const u = (t - ta) / (tb - ta);
        return { x: a.x + (b.x - a.x) * u, z: a.z + (b.z - a.z) * u };
      };
      const a1 = lerp(p0, p1, t0, t1), a2 = lerp(p1, p2, t1, t2), a3 = lerp(p2, p3, t2, t3);
      const b1 = { x: a1.x + (a2.x - a1.x) * (t - t0) / (t2 - t0), z: a1.z + (a2.z - a1.z) * (t - t0) / (t2 - t0) };
      const b2 = { x: a2.x + (a3.x - a2.x) * (t - t1) / (t3 - t1), z: a2.z + (a3.z - a2.z) * (t - t1) / (t3 - t1) };
      const c = { x: b1.x + (b2.x - b1.x) * (t - t1) / (t2 - t1), z: b1.z + (b2.z - b1.z) * (t - t1) / (t2 - t1) };
      out.push({ ...c, width: p1.width + (p2.width - p1.width) * (s / SAMPLES) });
    }
  }
  out.push(points[points.length - 1]);
  return out;
}
function extrapolate(from, to) {
  return { x: to.x * 2 - from.x, z: to.z * 2 - from.z, width: to.width };
}

// src/helpers/chunks.ts
import { Box3 as Box32, Sphere, Vector3 as Vector37 } from "three";
var CHUNK = 10;
function chunkKey(x, y) {
  return `${Math.floor(x / CHUNK)},${Math.floor(y / CHUNK)}`;
}
function groupByChunk(tiles) {
  const chunks = /* @__PURE__ */ new Map();
  for (const tile of tiles) {
    const key3 = chunkKey(tile.x, tile.y);
    let list = chunks.get(key3);
    if (!list) chunks.set(key3, list = []);
    list.push(tile);
  }
  return chunks;
}
function chunkBox(tiles, size, yMin, yMax) {
  const box = new Box32();
  const corner2 = new Vector37();
  for (const tile of tiles) {
    const c = getHexCenter(tile.x, tile.y, size);
    box.expandByPoint(corner2.set(c.x - size, yMin, c.y - size));
    box.expandByPoint(corner2.set(c.x + size, yMax, c.y + size));
  }
  return box;
}
function setBounds(geometry, box) {
  geometry.boundingBox = box.clone();
  geometry.boundingSphere = box.getBoundingSphere(new Sphere());
}

// src/styles/textured/hexagonGeometry.ts
import { Vector3 as Vector38, BufferGeometry as BufferGeometry4, BufferAttribute as BufferAttribute4 } from "three";
function subdivideTriangle(a, b, c, numSubdivisions) {
  if ((numSubdivisions || 0) <= 0) return [a, b, c];
  const ba = b.clone().sub(a);
  const ah = a.clone().add(ba.setLength(ba.length() / 2));
  const cb = c.clone().sub(b);
  const bh = b.clone().add(cb.setLength(cb.length() / 2));
  const ac = a.clone().sub(c);
  const ch = c.clone().add(ac.setLength(ac.length() / 2));
  return [].concat(
    subdivideTriangle(ah, bh, ch, numSubdivisions - 1),
    subdivideTriangle(ch, bh, c, numSubdivisions - 1),
    subdivideTriangle(ah, ch, a, numSubdivisions - 1),
    subdivideTriangle(bh, ah, b, numSubdivisions - 1)
  );
}
function createHexagonGeometry(radius, numSubdivisions = 0) {
  const numFaces = 6 * Math.pow(4, numSubdivisions);
  const positions = new Float32Array(numFaces * 3 * 3);
  const texcoords = new Float32Array(numFaces * 3 * 2);
  let p = 0, t = 0;
  const points = [0, 1, 2, 3, 4, 5].map((i) => {
    const angle = Math.PI / 180 * (60 * i);
    return new Vector38(radius * Math.cos(angle), 0, radius * Math.sin(angle));
  }).concat([new Vector38(0, 0, 0)]);
  const faces = [0, 6, 1, 1, 6, 2, 2, 6, 3, 3, 6, 4, 4, 6, 5, 5, 6, 0];
  let vertices = [];
  for (let i = 0; i < faces.length; i += 3) {
    const a = points[faces[i]], b = points[faces[i + 1]], c = points[faces[i + 2]];
    vertices = vertices.concat(subdivideTriangle(a, b, c, numSubdivisions));
  }
  for (let i = 0; i < vertices.length; i++) {
    positions[p++] = vertices[i].x;
    positions[p++] = vertices[i].y;
    positions[p++] = vertices[i].z;
    texcoords[t++] = 0.02 + 0.96 * ((vertices[i].x + radius) / (radius * 2));
    texcoords[t++] = 0.02 + 0.96 * ((vertices[i].z + radius) / (radius * 2));
  }
  const geometry = new BufferGeometry4();
  geometry.setAttribute("position", new BufferAttribute4(positions, 3));
  geometry.setAttribute("uv", new BufferAttribute4(texcoords, 2));
  return geometry;
}

// src/styles/textured/shaders/common.glsl.ts
var NOISE_GLSL = `
float hash21(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}

float valueNoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(
        mix(hash21(i), hash21(i + vec2(1.0, 0.0)), u.x),
        mix(hash21(i + vec2(0.0, 1.0)), hash21(i + vec2(1.0, 1.0)), u.x),
        u.y
    );
}
`;
var NOISE_TEXTURE_GLSL = `
uniform sampler2D noiseTex;

vec4 texNoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return texture(noiseTex, (i + u + 0.5) / 256.0);
}
`;
var COAST_FIELD_GLSL = `
uniform sampler2D coastField;
uniform vec2 coastFieldOrigin;
uniform vec2 coastFieldExtent;
uniform float coastCurvature;
uniform float beachWidth;

const float COAST_INSET = 0.15;
const float COAST_NOISE = 0.35;

// r = blurred signed distance to the land/water boundary (tile radii, + land),
// g = open-sea mask, b = coastline noise
vec4 coastSample(vec2 xz) {
    return texture(coastField, (xz - coastFieldOrigin) / coastFieldExtent);
}

// < 0 in the sea, 0 on the visible waterline, distance inland beyond it
float coastLine(vec4 c) {
    return c.r - COAST_INSET - c.b * coastCurvature * COAST_NOISE;
}

// how far the beach slope reaches inland from the tile boundary (tile radii)
float beachReach() {
    return 0.3 + clamp(beachWidth, 0.0, 1.0) * 0.4;
}

// Ground/water rest height around the coast, ONE function for both meshes:
// half way down to waterLevel exactly at the land/water boundary (sd = 0),
// rising to 0 inland over beachReach() and falling to waterLevel out at sea
// over 0.7 tile radii. Both layers evaluate it at their shared edge vertices
// from the same texture, so they meet at the same height whatever the field
// value there - no step, no crack, no z-fighting overlap.
float shoreHeight(float sd, float waterLevel) {
    if (sd >= 0.0) return waterLevel * 0.5 * (1.0 - smoothstep(0.0, beachReach(), sd));
    float t = smoothstep(-0.7, 0.0, sd);
    return waterLevel * (1.0 - 0.5 * t * t);
}
`;
var SEA_SHADING_GLSL = `
uniform vec3 waterColorShallow;
uniform vec3 waterColorDeep;
uniform float sparkleIntensity;
uniform float fresnelIntensity;
uniform float foamEnabled;
uniform vec3 foamColor;
uniform float foamCount;
uniform float foamSpeed;
uniform float foamWidth;
uniform float foamRange;
uniform float foamDistortion;
uniform float foamOpacity;

const vec3 sparkleColor = vec3(1.0, 0.97, 0.85);
const vec3 seaSkyTint = vec3(0.85, 0.95, 1.0);

// Unlit sea color at a point shoreDist (tile radii) out from the waterline:
// deep/shallow from the blurred open-sea mask, then turquoise shallows towards
// the shore, with a slow ripple so the near-shore water never looks frozen.
vec3 seaBaseColor(vec4 c, float shoreDist, vec2 xz, float t) {
    vec3 base = mix(waterColorShallow, waterColorDeep, smoothstep(0.15, 0.85, c.g));
    float shallow = 1.0 - smoothstep(0.0, 0.25 + beachWidth * 0.35, shoreDist);
    base = mix(base, mix(waterColorShallow, vec3(1.0), 0.45), shallow * 0.8);
    float ripple = valueNoise(xz * (6.0 / hexSize) + vec2(t * 0.35, t * 0.2));
    ripple = 0.5 * ripple + 0.5 * valueNoise(xz * (12.0 / hexSize) - vec2(t * 0.25, t * 0.4));
    return base * (1.0 + (ripple - 0.5) * 0.25 * shallow);
}

// Lambert in view space (like the land), sun glints and a sky fresnel in world
// space. worldN is the surface normal (waves), worldPos the fragment position.
vec3 shadeSea(vec3 base, vec3 worldN, vec3 worldPos) {
    vec3 nView = normalize(normalMatrix * worldN);
    float ndotl = max(dot(nView, normalize(lightDir)), 0.0);
    vec3 color = (lightAmbient + ndotl * lightDiffuse) * base;

    vec3 V = normalize(cameraPosition - worldPos);
    vec3 L = normalize(vec3(0.4, 1.0, 0.3));
    float spec = pow(max(dot(worldN, normalize(L + V)), 0.0), 60.0);
    color += spec * sparkleColor * sparkleIntensity;

    float fresnel = pow(1.0 - clamp(dot(worldN, V), 0.0, 1.0), 3.0);
    return mix(color, seaSkyTint, fresnel * 0.5 * fresnelIntensity);
}

// Coastal foam (after Harry Alisavakis' stylized water): noise-distorted bands
// travelling towards the shore plus a lapping strip at the waterline, keyed off
// shoreDist - the same field on both meshes, so the bands run continuously.
float coastalFoam(vec2 xz, float t, float shoreDist) {
    if (foamEnabled < 0.5 || shoreDist > foamRange + 0.1) return 0.0;
    float n = valueNoise(xz * (3.0 / hexSize) + vec2(0.0, t * 0.2));
    n = 0.5 * n + 0.5 * valueNoise(xz * (7.0 / hexSize) - vec2(t * 0.15, 0.0));
    float distort = (n - 0.5) * foamDistortion;

    float phase = fract(shoreDist * foamCount + t * foamSpeed + distort * 2.0);
    float halfW = clamp(foamWidth, 0.02, 1.0) * 0.5;
    float band = 1.0 - smoothstep(halfW * 0.35, halfW, abs(phase - 0.5));
    float fade = 1.0 - smoothstep(foamRange * 0.35, max(foamRange, 0.001), shoreDist);
    band *= fade * (0.55 + 0.45 * n);

    float edge = 1.0 - smoothstep(0.0, 0.1, shoreDist + distort * 0.3);
    return clamp(edge + band, 0.0, 1.0) * foamOpacity;
}
`;

// src/styles/textured/shaders/terrain.vertex.ts
var TERRAIN_VERTEX_SHADER = `
// GLSL ES 3.00 (RawShaderMaterial with glslVersion GLSL3): the fragment stage
// needs sampler2DArray for the terrain layers, and both stages of a program
// must share one version.
precision highp float;

uniform mat4 modelViewMatrix;
uniform mat4 projectionMatrix;
uniform mat3 normalMatrix;

uniform float hexSize; // tile circumradius, matches getHexCenter's "size" (world units)

// Beach slope: land sinks towards waterLevel (where the water plane sits, see
// water.vertex.ts) near the coast, from the shared coast field.
uniform float waterLevel;
${COAST_FIELD_GLSL}

// Mountains (tiles whose layer is mountainLayer): the whole tile rises to a
// craggy peak. The height is a pure function of the tile-local position
// (mountainHeightAt below) so the lighting normal can be derived from it by
// finite differences. mountainHeight is the peak height in world units.
uniform float mountainLayer;
uniform float mountainHeight;

// Rivers/lakes: a baked world-space distance field (see styles/textured/waterField.ts).
// The vertex stage only carves the bed - a smooth sink towards -riverDepth
// inside the water, starting riverBankWidth (tile radii) outside the waterline.
// World-space data, so the carve is identical on both sides of every tile
// border by construction (no per-tile masks that could disagree at an edge).
uniform sampler2D waterField;
uniform vec2 waterFieldOrigin;
uniform vec2 waterFieldExtent;
uniform float riverBankWidth;
uniform float riverDepth;

// World units one repeat of the war-fog texture spans. Fog UVs are computed
// from world position (not per-tile local UVs) so one copy of the texture
// flows continuously across every fogged tile.
uniform float fogTextureSize;

in vec3 position;

in vec2 offset;       // world-space (x,z) offset of this tile instance
in vec3 style;        // x = terrain layer index, y = 1 on river/lake tiles, z = unused here
in vec3 neighborsA;   // terrain layer of SE/S/SW neighbor (-1 = none/water)
in vec3 neighborsB;   // terrain layer of NW/N/NE neighbor
in vec3 neighborsKindA; // SE/S/SW: -1 no tile, 0 non-water, 1 sea, 2 coastal
in vec3 neighborsKindB; // NW/N/NE
in float fogState; // 0 = unseen, 1 = explored (darkened), 2 = visible - see FogOfWar.ts

out float vBorder;
flat out float vTerrain;
flat out float vFogState;
out vec3 vWorldNormal;
out vec3 vWorldPos;
out vec2 vFogUV;
out float vElevation;  // normalized mountain elevation (0 flat .. ~1 peak), for snowcap tinting

const vec2 DIR_SE = vec2(0.8660254, 0.5);
const vec2 DIR_S  = vec2(0.0, 1.0);
const vec2 DIR_SW = vec2(-0.8660254, 0.5);
const vec2 DIR_NW = vec2(-0.8660254, -0.5);
const vec2 DIR_N  = vec2(0.0, -1.0);
const vec2 DIR_NE = vec2(0.8660254, -0.5);

// World-space value noise - the mountain relief has to be world-space so
// adjacent mountain tiles' crags line up across the shared edge.
${NOISE_GLSL}

// Saddle corner taper - a ridge saddle's height at an edge CORNER must agree
// across all three tiles meeting there, or the surfaces crack open. If the
// corner's third tile is a mountain too, all three raise it to the same saddle
// height (no taper); if it isn't, the saddle fades to 0 towards that corner.
float saddleTaper(float adjIsMountain, float adjFactor) {
    return adjIsMountain > 0.5 ? 1.0 : 1.0 - smoothstep(0.6, 1.0, adjFactor);
}

// Normalized mountain height (0..~1.2) at a tile-local point p: a central
// peak, ridge saddles towards mountain neighbors (continuous ridgelines), shore
// flattening per water-adjacent direction, and world-space noise crags. Kept a
// pure, neighbor-symmetric function of p so the shared edges never crack.
float mountainHeightAt(vec2 p) {
    float apothem = hexSize * 0.8660254;
    vec3 efA = vec3(dot(p, DIR_SE), dot(p, DIR_S), dot(p, DIR_SW)) / apothem;
    vec3 efB = vec3(dot(p, DIR_NW), dot(p, DIR_N), dot(p, DIR_NE)) / apothem;
    float rim = max(max(max(efA.x, efA.y), max(efA.z, efB.x)), max(efB.y, efB.z));
    float h = pow(clamp(1.0 - rim, 0.0, 1.0), 1.2);

    float mSE = abs(neighborsA.x - mountainLayer) < 0.5 ? 1.0 : 0.0;
    float mS  = abs(neighborsA.y - mountainLayer) < 0.5 ? 1.0 : 0.0;
    float mSW = abs(neighborsA.z - mountainLayer) < 0.5 ? 1.0 : 0.0;
    float mNW = abs(neighborsB.x - mountainLayer) < 0.5 ? 1.0 : 0.0;
    float mN  = abs(neighborsB.y - mountainLayer) < 0.5 ? 1.0 : 0.0;
    float mNE = abs(neighborsB.z - mountainLayer) < 0.5 ? 1.0 : 0.0;

    // ring adjacency: SE-S-SW-NW-N-NE-SE (see the DIR_* constants' angles)
    float ridge = 0.0;
    if (mSE > 0.5) ridge = max(ridge, efA.x * saddleTaper(mNE, efB.z) * saddleTaper(mS,  efA.y));
    if (mS  > 0.5) ridge = max(ridge, efA.y * saddleTaper(mSE, efA.x) * saddleTaper(mSW, efA.z));
    if (mSW > 0.5) ridge = max(ridge, efA.z * saddleTaper(mS,  efA.y) * saddleTaper(mNW, efB.x));
    if (mNW > 0.5) ridge = max(ridge, efB.x * saddleTaper(mSW, efA.z) * saddleTaper(mN,  efB.y));
    if (mN  > 0.5) ridge = max(ridge, efB.y * saddleTaper(mNW, efB.x) * saddleTaper(mNE, efB.z));
    if (mNE > 0.5) ridge = max(ridge, efB.z * saddleTaper(mN,  efB.y) * saddleTaper(mSE, efA.x));
    h = max(h, clamp(ridge, 0.0, 1.0) * 0.55);

    if (neighborsKindA.x >= 0.5) h *= 1.0 - smoothstep(0.5, 0.95, efA.x);
    if (neighborsKindA.y >= 0.5) h *= 1.0 - smoothstep(0.5, 0.95, efA.y);
    if (neighborsKindA.z >= 0.5) h *= 1.0 - smoothstep(0.5, 0.95, efA.z);
    if (neighborsKindB.x >= 0.5) h *= 1.0 - smoothstep(0.5, 0.95, efB.x);
    if (neighborsKindB.y >= 0.5) h *= 1.0 - smoothstep(0.5, 0.95, efB.y);
    if (neighborsKindB.z >= 0.5) h *= 1.0 - smoothstep(0.5, 0.95, efB.z);

    vec2 w = offset + p;
    float n = valueNoise(w * (1.6 / hexSize));
    n = 0.65 * n + 0.35 * valueNoise(w * (4.0 / hexSize));
    return h * (0.72 + 1.1 * (n - 0.5));
}

// Beach sink at a world point (<= 0) - see shoreHeight() in common.glsl.ts.
float beachAt(vec2 worldXZ) {
    return shoreHeight(coastSample(worldXZ).r, waterLevel);
}

// River/lake bed depth 0..1 at a world point - the waterField texture's
// distance channel through a smoothstep that starts riverBankWidth outside the
// waterline and bottoms out a little inside it.
float riverBedAt(vec2 worldXZ) {
    float d = texture(waterField, (worldXZ - waterFieldOrigin) / waterFieldExtent).r;
    return 1.0 - smoothstep(-0.08, riverBankWidth, d);
}

void main() {
    vec2 local = position.xz;
    vec2 worldXZ = offset + local;

    // Unseen (fog of war): keep the tile perfectly flat - relief would betray
    // what's there.
    float fogVisible = fogState < 0.5 ? 0.0 : 1.0;

    // Land sinks half way to waterLevel at the tile boundary and the water
    // rises the other half (see water.vertex.ts): both read the same coast
    // field there, so the two meshes meet at exactly the same height - no
    // step for the background to show through, and no overlapping area that
    // could z-fight.
    float eps = hexSize * 0.1;
    float beach = beachAt(worldXZ);
    vec2 beachSlope = vec2(beachAt(worldXZ + vec2(eps, 0.0)) - beach, beachAt(worldXZ + vec2(0.0, eps)) - beach) / eps;
    float beachSink = beach * fogVisible;
    beachSlope *= fogVisible;

    // River/lake bed, plus its slope by finite differences for lighting. The
    // deeper of the two carves wins (a river mouth next to the sea).
    float bed = riverBedAt(worldXZ);
    vec2 bedSlope = vec2(riverBedAt(worldXZ + vec2(eps, 0.0)) - bed, riverBedAt(worldXZ + vec2(0.0, eps)) - bed) / eps;
    float riverSink = -riverDepth * bed * fogVisible;
    bool riverWins = riverSink < beachSink;
    float sinkY = riverWins ? riverSink : beachSink;

    // Mountain elevation - gated to 0 on river/lake tiles (the carved bed
    // wins) and under unseen fog.
    float raiseY = 0.0;
    vec2 mountainSlope = vec2(0.0);
    float elevation = 0.0;
    if (abs(style.x - mountainLayer) < 0.5) {
        float gate = fogVisible * (style.y > 0.5 ? 0.0 : 1.0);
        if (gate > 0.0) {
            float meps = hexSize * 0.08;
            float h0 = mountainHeightAt(local);
            float hx = mountainHeightAt(local + vec2(meps, 0.0));
            float hz = mountainHeightAt(local + vec2(0.0, meps));
            elevation = h0 * gate;
            raiseY = elevation * mountainHeight;
            mountainSlope = vec2(hx - h0, hz - h0) / meps * mountainHeight * gate;
        }
    }

    vec3 pos = vec3(worldXZ.x, position.y + sinkY + raiseY, worldXZ.y);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);

    vec2 slope = (riverWins ? bedSlope * -riverDepth * fogVisible : beachSlope) + mountainSlope;
    vWorldNormal = normalize(vec3(-slope.x, 1.0, -slope.y));

    // Rim distance for the grid line: the max edge factor is exactly 1.0
    // along each whole straight edge (radial distance would dip at midpoints).
    float apothem = hexSize * 0.8660254;
    vec3 ef = abs(vec3(dot(local, DIR_SE), dot(local, DIR_S), dot(local, DIR_SW))) / apothem;
    vBorder = clamp(max(ef.x, max(ef.y, ef.z)), 0.0, 1.0);
    vTerrain = style.x;
    vElevation = elevation;
    vFogState = fogState;
    vWorldPos = pos;
    // Axes swapped/negated so the fog image reads upright from this map's
    // camera (azimuth locked to ~90deg: screen-right = world -Z, screen-up =
    // world -X).
    vFogUV = vec2(-pos.z, -pos.x) / fogTextureSize;
}
`;

// src/styles/textured/shaders/terrain.fragment.ts
var TERRAIN_FRAGMENT_SHADER = `
// highp, not mediump: the noise hash (fract(sin(x) * 43758...)) is fed
// world-space coordinates in the thousands - at fp16 it collapses into
// structured streak garbage.
precision highp float;
precision highp sampler2DArray;

out vec4 fragColor;

// ---------------------------------------------------------------------------
// Terrain surface (see styles/textured/TerrainLayers.ts). One array layer per terrain
// type, sampled with WORLD-space UVs (layerScale world units per repeat), so a
// texture never restarts at hex borders. layerNormal: RG = normal XY, B = height.
// ---------------------------------------------------------------------------
uniform sampler2DArray layerAlbedo;
uniform sampler2DArray layerNormal;
uniform float layerScale;
uniform float normalStrength;
uniform float sandLayer;

// Every tile's terrain layer (R, 255 = none/water) and fog flag (G, 255 =
// unseen) in a W x H texture, so a pixel can look up ANY tile near it - not
// just its own instance's neighbor attributes. That is what lets the
// transitions below be organic shapes instead of bands along the hex edges.
uniform sampler2D tileMap;
uniform vec2 tileMapSize;

// Land-type transitions: landBlendWidth (0..1) is how far the height-based
// blend reaches from a border, landBlendCurvature (0..1) how strongly the
// border itself is pushed around by domain-warp noise.
uniform float landBlendWidth;
uniform float landBlendCurvature;

uniform sampler2D fogMap;        // war-fog.jpg, world-space UVs (vFogUV)
uniform float fogDarkenFactor;   // color multiplier for Explored (fogState 1) tiles

uniform float showGrid;
uniform vec3 gridColor;
uniform float gridWidth;
uniform float gridOpacity;

uniform vec3 lightDir;
uniform mat3 normalMatrix;
uniform vec3 cameraPosition;

uniform float hexSize;
uniform float uTime;

// Rivers/lakes - baked world-space field (see styles/textured/waterField.ts):
// R = signed distance to the waterline (tile radii), GB = flow direction,
// A = local half-width (depth scale).
uniform sampler2D waterField;
uniform vec2 waterFieldOrigin;
uniform vec2 waterFieldExtent;
uniform float riverBankWidth;   // wet-soil band beyond the waterline, tile radii
uniform float riverEdgeNoise;   // 0..1, how much noise roughens the banks
uniform float riverFlowSpeed;
uniform vec3 riverColorShallow;
uniform vec3 riverColorDeep;
uniform vec3 riverBankColor;    // wet soil tint the banks darken towards

in float vBorder;
flat in float vTerrain;
flat in float vFogState;
in vec3 vWorldNormal;
in vec3 vWorldPos;
in vec2 vFogUV;
in float vElevation;

const vec3 lightAmbient = vec3(0.55, 0.55, 0.55);
const vec3 lightDiffuse = vec3(0.55, 0.55, 0.55);
const vec3 skyTint = vec3(0.75, 0.85, 0.95);

${NOISE_GLSL}
${NOISE_TEXTURE_GLSL}
// The coast (sea waterline, sand, painted sea, foam) comes from the coast field
// shared with the water layer - see styles/textured/coastField.ts.
${COAST_FIELD_GLSL}
${SEA_SHADING_GLSL}

// value noise + its analytic gradient (xy) - the river ripple normals
vec3 valueNoiseD(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    vec2 du = 6.0 * f * (1.0 - f);
    float a = hash21(i), b = hash21(i + vec2(1.0, 0.0));
    float c = hash21(i + vec2(0.0, 1.0)), d = hash21(i + vec2(1.0, 1.0));
    return vec3(
        a + (b - a) * u.x + (c - a) * u.y + (a - b - c + d) * u.x * u.y,
        du * (vec2(b - a, c - a) + (a - b - c + d) * u.yx)
    );
}

// ---------------------------------------------------------------------------
// Hex lattice. Tile centers (getHexCenter: x = 1.5*size*col, z = sqrt3*size*
// row + half a row on even columns) form a triangular lattice. In axial
// coordinates (q = col, r = row - ceil(col/2), z measured from half a row down)
// every point lies in one lattice triangle whose 3 corners are the 3 nearest
// tile centers; its barycentric weights are the "how much does this point
// belong to each of them" - equal exactly on the hex edge between two tiles.
// ---------------------------------------------------------------------------

// Layer of the tile at axial qr; -1 for water, off-map and UNSEEN tiles - a
// fogged tile must not leak its terrain type into a visible neighbor.
float tileLayerAt(vec2 qr) {
    float x = qr.x;
    float y = qr.y + floor((qr.x + 1.0) * 0.5);
    if (x < 0.0 || y < 0.0 || x >= tileMapSize.x || y >= tileMapSize.y) return -1.0;
    vec2 t = texelFetch(tileMap, ivec2(int(x), int(y)), 0).rg;
    if (t.g > 0.5) return -1.0;
    float v = t.r * 255.0;
    return v > 254.5 ? -1.0 : floor(v + 0.5);
}

void latticeTriangle(vec2 w, out vec2 v0, out vec2 v1, out vec2 v2, out vec3 bary) {
    float q = w.x / (1.5 * hexSize);
    float r = (w.y - 0.8660254 * hexSize) / (1.7320508 * hexSize) - q * 0.5;
    vec2 base = floor(vec2(q, r));
    vec2 f = vec2(q, r) - base;
    if (f.x + f.y < 1.0) {
        v0 = base; v1 = base + vec2(1.0, 0.0); v2 = base + vec2(0.0, 1.0);
        bary = vec3(1.0 - f.x - f.y, f.x, f.y);
    } else {
        v0 = base + vec2(1.0, 1.0); v1 = base + vec2(0.0, 1.0); v2 = base + vec2(1.0, 0.0);
        bary = vec3(f.x + f.y - 1.0, 1.0 - f.x, 1.0 - f.y);
    }
}

// Anti-tiling (after Inigo Quilez, "texture repetition", technique 3): a
// low-frequency noise picks, per area, one of 8 random offsets into the
// texture; two neighboring offsets are crossfaded. The repeat grid vanishes.
// textureGrad with the UNOFFSET derivatives keeps the mip level stable across
// the offset jumps (plain texture() would pick a tiny mip there and draw
// seams). Where the crossfade weight is exactly 0 or 1 - most of the surface -
// only one lookup is made.
struct NoTile { vec2 offA; vec2 offB; float t; vec2 dx; vec2 dy; };

NoTile noTileSetup(vec2 uv) {
    NoTile n;
    float k = texNoise(uv * 1.3).r * 8.0;
    float i = floor(k);
    n.offA = sin(vec2(3.0, 7.0) * i);
    n.offB = sin(vec2(3.0, 7.0) * (i + 1.0));
    n.t = smoothstep(0.25, 0.75, fract(k));
    n.dx = dFdx(uv);
    n.dy = dFdy(uv);
    return n;
}

vec3 layerColor(float layer, vec2 uv, NoTile n) {
    if (n.t <= 0.0) return textureGrad(layerAlbedo, vec3(uv + n.offA, layer), n.dx, n.dy).rgb;
    if (n.t >= 1.0) return textureGrad(layerAlbedo, vec3(uv + n.offB, layer), n.dx, n.dy).rgb;
    vec3 a = textureGrad(layerAlbedo, vec3(uv + n.offA, layer), n.dx, n.dy).rgb;
    vec3 b = textureGrad(layerAlbedo, vec3(uv + n.offB, layer), n.dx, n.dy).rgb;
    return mix(a, b, n.t);
}

vec3 layerNormalH(float layer, vec2 uv, NoTile n) {
    if (n.t <= 0.0) return textureGrad(layerNormal, vec3(uv + n.offA, layer), n.dx, n.dy).rgb;
    if (n.t >= 1.0) return textureGrad(layerNormal, vec3(uv + n.offB, layer), n.dx, n.dy).rgb;
    vec3 a = textureGrad(layerNormal, vec3(uv + n.offA, layer), n.dx, n.dy).rgb;
    vec3 b = textureGrad(layerNormal, vec3(uv + n.offB, layer), n.dx, n.dy).rgb;
    return mix(a, b, n.t);
}

void main() {
    // Unseen: the war-fog texture only, nothing else.
    if (vFogState < 0.5) {
        fragColor = vec4(texture(fogMap, vFogUV).rgb, 1.0);
        return;
    }

    vec2 w = vWorldPos.xz;
    vec2 uv = w / layerScale;
    NoTile variation = noTileSetup(uv);

    // -----------------------------------------------------------------------
    // 1. Land-type transitions. Domain-warp the lookup point, find the 3
    //    nearest tiles, then blend their textures by HEIGHT: each layer's
    //    score is its barycentric weight plus its own height map, and only
    //    layers within a thin margin of the best score show. Near the middle
    //    of a tile one layer wins outright; at a border the taller detail of
    //    either texture wins pixel by pixel - grass growing between stones,
    //    sand filling the cracks - instead of a blurry crossfade.
    // -----------------------------------------------------------------------
    vec4 n1 = texNoise(w * (1.1 / hexSize));
    vec4 n2 = texNoise(w * (3.1 / hexSize) + vec2(37.0, 11.0));
    vec2 warp = 0.65 * n1.rg + 0.35 * n2.rg;
    vec2 wWarped = w + (warp - 0.5) * 2.0 * landBlendCurvature * 0.45 * hexSize;

    vec2 c0, c1, c2;
    vec3 bary;
    latticeTriangle(wWarped, c0, c1, c2, bary);
    float l0 = tileLayerAt(c0), l1 = tileLayerAt(c1), l2 = tileLayerAt(c2);
    // water/off-map/unseen corners take this tile's own layer (the coast
    // below draws the shoreline itself)
    if (l0 < 0.0) l0 = vTerrain;
    if (l1 < 0.0) l1 = vTerrain;
    if (l2 < 0.0) l2 = vTerrain;

    vec3 albedo;
    vec3 nrm; // xy = tangent normal, z = height
    if (l0 == l1 && l1 == l2) {
        albedo = layerColor(l0, uv, variation);
        nrm = layerNormalH(l0, uv, variation);
    } else {
        vec3 h0 = layerNormalH(l0, uv, variation);
        vec3 h1 = l1 == l0 ? h0 : layerNormalH(l1, uv, variation);
        vec3 h2 = l2 == l0 ? h0 : (l2 == l1 ? h1 : layerNormalH(l2, uv, variation));
        float depth = mix(0.08, 0.6, clamp(landBlendWidth, 0.0, 1.0));
        vec3 score = bary + vec3(h0.b, h1.b, h2.b) * depth;
        float top = max(score.x, max(score.y, score.z));
        vec3 wts = max(score - top + 0.06, 0.0);
        wts /= wts.x + wts.y + wts.z;

        // a layer whose weight is 0 needs no color lookup at all
        // (reuse only a color that was actually fetched)
        vec3 a0 = wts.x > 0.0 ? layerColor(l0, uv, variation) : vec3(0.0);
        vec3 a1 = wts.y > 0.0 ? ((l1 == l0 && wts.x > 0.0) ? a0 : layerColor(l1, uv, variation)) : vec3(0.0);
        vec3 a2 = wts.z > 0.0 ? ((l2 == l0 && wts.x > 0.0) ? a0 : ((l2 == l1 && wts.y > 0.0) ? a1 : layerColor(l2, uv, variation))) : vec3(0.0);
        albedo = a0 * wts.x + a1 * wts.y + a2 * wts.z;
        nrm = h0 * wts.x + h1 * wts.y + h2 * wts.z;
    }

    // large-scale tonal variation - real terrain is never one flat color
    float macro = texNoise(w * (0.18 / hexSize)).b * 0.6 + texNoise(w * (0.5 / hexSize)).a * 0.4;
    albedo *= 0.88 + 0.24 * macro;

    // -----------------------------------------------------------------------
    // 2. Rivers and lakes
    // -----------------------------------------------------------------------
    vec4 wf = texture(waterField, (w - waterFieldOrigin) / waterFieldExtent);
    float riverT = 0.0;
    vec3 riverColor = vec3(0.0);
    if (wf.r < riverBankWidth + 0.1) {
        // banks roughened by noise - lakes (no flow) get a much bolder
        // wobble than rivers, so their outline reads as a natural shore
        float bn = n1.b * 0.5 + n2.a * 0.3 + texNoise(w * (8.0 / hexSize)).r * 0.2;
        float lakeness = 1.0 - clamp(length(wf.gb), 0.0, 1.0);
        float d = wf.r + (bn - 0.5) * mix(0.12, 0.45, lakeness) * riverEdgeNoise;
        float aa = max(fwidth(d), 0.002);

        // wet soil hugging the water, darkest right at the waterline
        float wet = 1.0 - smoothstep(0.0, max(riverBankWidth, 0.001), d);
        albedo = mix(albedo, albedo * riverBankColor * 1.6, wet * (0.55 + 0.45 * nrm.b) * 0.8);

        riverT = 1.0 - smoothstep(-aa, aa, d);
        if (riverT > 0.0) {
            float depthT = smoothstep(0.0, 1.0, clamp(-d / max(wf.a, 0.04), 0.0, 1.0));
            vec2 flow = wf.gb;
            float flowAmount = clamp(length(flow), 0.0, 1.0);

            // flow map: two noise layers dragged along the flow, each reset
            // every cycle and crossfaded so the reset never shows
            float t = uTime * riverFlowSpeed;
            float ph0 = fract(t * 0.25);
            float ph1 = fract(t * 0.25 + 0.5);
            float fade = abs(ph0 - 0.5) * 2.0;
            vec2 p = w * (5.0 / hexSize);
            vec2 g0 = valueNoiseD(p - flow * ph0 * 2.5).yz;
            vec2 g1 = valueNoiseD(p + vec2(0.37, 0.71) - flow * ph1 * 2.5).yz;
            vec2 grad = mix(g0, g1, fade) * flowAmount;
            // lakes: slow drifting ripples instead
            grad += valueNoiseD(w * (4.0 / hexSize) + vec2(t * 0.06, -t * 0.05)).yz * (1.0 - flowAmount) * 0.6;
            grad += valueNoiseD(w * (11.0 / hexSize) - vec2(t * 0.1, t * 0.08)).yz * 0.3;
            vec3 wn = normalize(vec3(-grad.x * 0.18, 1.0, -grad.y * 0.18));

            // inland water stays lighter than the open sea's deep color
            riverColor = mix(riverColorShallow, mix(riverColorDeep, riverColorShallow, 0.3), depthT);
            // the river bed shows through the shallows
            riverColor = mix(riverColor, albedo * 0.6, (1.0 - depthT) * 0.35);

            vec3 V = normalize(cameraPosition - vWorldPos);
            vec3 L = normalize(vec3(0.4, 1.0, 0.3));
            float diffuse = 0.75 + 0.25 * max(dot(wn, L), 0.0);
            float fresnel = pow(1.0 - clamp(dot(wn, V), 0.0, 1.0), 4.0);
            float spec = pow(max(dot(wn, normalize(L + V)), 0.0), 90.0);
            riverColor = riverColor * diffuse;
            riverColor = mix(riverColor, skyTint, fresnel * 0.45);
            riverColor += spec * vec3(1.0, 0.97, 0.88) * 0.8;

            // a thin bright lip where water meets the bank
            float lip = 1.0 - smoothstep(0.0, 0.035, -d);
            riverColor = mix(riverColor, vec3(0.85, 0.9, 0.9), lip * 0.35);
        }
    }

    // -----------------------------------------------------------------------
    // 3. Sea coast: sand beach and the sea itself up to the waterline, all
    //    from the coast field the water layer reads too - the waterline,
    //    water color and foam continue across the mesh seam exactly.
    // -----------------------------------------------------------------------
    vec4 coast = coastSample(w);
    float line = coastLine(coast);
    float seaT = 0.0;
    vec3 seaColor = vec3(0.0);
    float beachBand = 0.06 + clamp(beachWidth, 0.0, 1.0) * 0.22;
    if (line < beachBand + 0.05) {
        float aa = max(fwidth(line), 0.002);

        // sand, height-blended into the land like the land transitions, and
        // darker where the waves keep it wet
        float beachT = (1.0 - smoothstep(beachBand * 0.5, beachBand, line)) * (1.0 - riverT);
        if (beachT > 0.0) {
            vec3 sand = layerColor(sandLayer, uv, variation);
            float sandH = layerNormalH(sandLayer, uv, variation).b;
            float sharp = smoothstep(0.0, 1.0, clamp(beachT * 1.6 - 0.3 + (sandH - nrm.b) * 0.5, 0.0, 1.0));
            sand *= mix(0.78, 1.0, smoothstep(0.0, 0.07, line));
            albedo = mix(albedo, sand, sharp);
            nrm = mix(nrm, vec3(0.5, 0.5, sandH), sharp);
        }

        seaT = 1.0 - smoothstep(-aa, aa, line);
        if (seaT > 0.0) {
            float shoreDist = max(-line, 0.0);
            seaColor = shadeSea(seaBaseColor(coast, shoreDist, w, uTime), vec3(0.0, 1.0, 0.0), vWorldPos);
            seaColor = mix(seaColor, foamColor, coastalFoam(w, uTime, shoreDist));
        }

        // a river reaching the sea fades into the sea's color
        riverColor = mix(riverColor, waterColorShallow, (1.0 - smoothstep(0.0, 0.3, line)) * 0.6);
    }
    float flatT = max(seaT, riverT); // no ground normal detail on water

    // Mountain snowcap
    if (vElevation > 0.0) {
        float snowT = smoothstep(0.55, 0.95, vElevation + (nrm.b - 0.5) * 0.15);
        albedo = mix(albedo, vec3(0.93, 0.95, 0.98), snowT * 0.85);
    }

    // -----------------------------------------------------------------------
    // 4. Lighting. The detail normal perturbs the world normal; lambert is
    //    done in view space, like the water layer's shadeSea().
    // -----------------------------------------------------------------------
    vec2 tn = (nrm.rg * 2.0 - 1.0) * normalStrength * (1.0 - flatT);
    vec3 worldN = normalize(vWorldNormal + vec3(tn.x, 0.0, -tn.y));
    vec3 normal = normalize(normalMatrix * worldN);
    float lambertian = max(dot(normalize(lightDir), normal), 0.0);
    // cavity darkening from the height map: crevices read deeper
    float cavity = mix(1.0, 0.88 + 0.18 * nrm.b, (1.0 - flatT));
    vec3 color = (lightAmbient + lambertian * lightDiffuse) * albedo * cavity;

    color = mix(color, riverColor, riverT);
    color = mix(color, seaColor, seaT);

    if (vFogState < 1.5) color *= fogDarkenFactor;

    fragColor = vec4(color, 1.0);

    if (showGrid > 0.0 && vBorder > 1.0 - gridWidth) {
        fragColor = mix(vec4(gridColor, 1.0), fragColor, 1.0 - gridOpacity);
    }
}
`;

// src/styles/textured/shaders/water.vertex.ts
var WATER_VERTEX_SHADER = `
precision highp float;

uniform mat4 modelViewMatrix;
uniform mat4 projectionMatrix;

uniform float hexSize; // tile circumradius, matches getHexCenter's "size" (world units)
uniform float uTime;   // seconds, animates the waves
uniform float waterLevel; // rest height of the water plane (usually negative, below land)

// Wave shape - see waveHeightAndSlope() below.
uniform float waveAmplitude;
uniform float waveFrequency;
uniform float waveSpeed;

uniform float fogTextureSize; // world units one repeat of the fog texture spans (see terrain.vertex.ts)

${COAST_FIELD_GLSL}

in vec3 position;
in vec2 offset;
in float fogState; // 0 = unseen, 1 = explored (darkened), 2 = visible - see FogOfWar.ts

out float vBorder;
out vec3 vWorldNormal;
out vec3 vWorldPos;
flat out float vFogState;
out vec2 vFogUV; // world-space fog texture coords, continuous across tiles

const vec2 DIR_SE = vec2(0.8660254, 0.5);
const vec2 DIR_S  = vec2(0.0, 1.0);
const vec2 DIR_SW = vec2(-0.8660254, 0.5);

const float GOLDEN_ANGLE = 2.399963; // ~137.5 deg, keeps summed waves from lining up

// Sum of sine waves (NVIDIA GPU Gems ocean approach): the derivative of a sine
// is a cosine of the same phase, so the slope comes out of the same loop.
// Returns (height, slope.x, slope.z).
vec3 waveHeightAndSlope(vec2 worldXZ, float t) {
    float height = 0.0;
    vec2 slope = vec2(0.0);

    float amp = waveAmplitude;
    float freq = waveFrequency;
    float speed = waveSpeed;
    float dirAngle = 0.4;

    for (int i = 0; i < 4; i++) {
        vec2 dir = vec2(cos(dirAngle), sin(dirAngle));
        float phase = dot(dir, worldXZ) * freq + t * speed;

        height += amp * sin(phase);
        slope += dir * (amp * freq * cos(phase));

        amp *= 0.55;
        freq *= 1.8;
        speed *= 1.3;
        dirAngle += GOLDEN_ANGLE;
    }

    return vec3(height, slope.x, slope.y);
}

void main() {
    vec2 worldXZ = offset + position.xz;

    // Rim vertices on a coastal edge are pushed 3% outwards, under the land
    // mesh, and sunk a little below it: the two meshes' shared edge vertices
    // are computed from different tile offsets, and float rounding opens
    // sub-pixel cracks between them - with this skirt those show water, not
    // the background. Only where the pushed point is on land (sd > 0), so
    // water-water edges never overlap.
    vec2 grown = offset + position.xz * 1.03;
    float sdGrown = coastSample(grown).r;
    float skirt = 0.0;
    if (sdGrown > 0.0) {
        worldXZ = grown;
        skirt = sdGrown * hexSize * 0.6;
    }

    vec3 hs = waveHeightAndSlope(worldXZ, uTime);

    // Unseen (fog of war): freeze the waves and raise the tile to land's rest
    // height (y=0) - animated or sunken water would betray the fog.
    float fogVisible = fogState < 0.5 ? 0.0 : 1.0;

    // Shore from the shared coast field (styles/textured/coastField.ts): 1 at the
    // land/water tile boundary, 0 from ~0.7 tile radii out. Waves die down
    // towards it, and the water rises half way up to land's rest height to
    // meet the land layer's beach slope (which sinks the other half - see
    // terrain.vertex.ts) at exactly the same height on the shared edge.
    float sd = coastSample(worldXZ).r;
    float damp = (1.0 - smoothstep(-0.7, 0.0, sd)) * fogVisible;
    float waveY = hs.x * damp;
    vec2 slope = hs.yz * damp;

    vec3 pos = vec3(worldXZ.x, mix(0.0, shoreHeight(sd, waterLevel) + waveY - skirt, fogVisible), worldXZ.y);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);

    vWorldNormal = normalize(vec3(-slope.x, 1.0, -slope.y));
    vWorldPos = pos;

    // Rim distance for the grid line: max over the 6 edge factors (|dot| covers
    // the opposite direction of each pair), exactly 1.0 along every edge.
    float apothem = hexSize * 0.8660254;
    vec2 local = position.xz;
    vec3 ef = abs(vec3(dot(local, DIR_SE), dot(local, DIR_S), dot(local, DIR_SW))) / apothem;
    vBorder = clamp(max(ef.x, max(ef.y, ef.z)), 0.0, 1.0);

    vFogState = fogState;
    // Same upright-for-the-camera mapping as terrain.vertex.ts's vFogUV.
    vFogUV = vec2(-worldXZ.y, -worldXZ.x) / fogTextureSize;
}
`;

// src/styles/textured/shaders/water.fragment.ts
var WATER_FRAGMENT_SHADER = `
// highp: the noise hash is fed world-space coordinates in the thousands.
precision highp float;

out vec4 fragColor;

uniform sampler2D fogMap;        // war-fog.jpg, world-space UVs (vFogUV)
uniform float fogDarkenFactor;   // color multiplier for Explored (fogState 1) tiles

uniform float showGrid;
uniform vec3 gridColor;
uniform float gridWidth;
uniform float gridOpacity;

uniform vec3 lightDir;
uniform mat3 normalMatrix;
uniform vec3 cameraPosition;
uniform float hexSize;
uniform float uTime;

in float vBorder;
in vec3 vWorldNormal;
in vec3 vWorldPos;
flat in float vFogState;
in vec2 vFogUV;

const vec3 lightAmbient = vec3(0.55, 0.55, 0.55);
const vec3 lightDiffuse = vec3(0.55, 0.55, 0.55);

${NOISE_GLSL}
${COAST_FIELD_GLSL}
${SEA_SHADING_GLSL}

void main() {
    // Unseen: the war-fog texture only (same short-circuit as the land layer).
    if (vFogState < 0.5) {
        fragColor = vec4(texture(fogMap, vFogUV).rgb, 1.0);
        return;
    }

    // Everything coast-related comes from the shared coast field, so it lines
    // up exactly with what the land layer paints on its side of the seam.
    vec2 xz = vWorldPos.xz;
    vec4 c = coastSample(xz);
    float shoreDist = max(-coastLine(c), 0.0);

    vec3 color = shadeSea(seaBaseColor(c, shoreDist, xz, uTime), normalize(vWorldNormal), vWorldPos);
    color = mix(color, foamColor, coastalFoam(xz, uTime, shoreDist));

    if (vFogState < 1.5) color *= fogDarkenFactor;

    fragColor = vec4(color, 1.0);

    if (showGrid > 0.0 && vBorder > 1.0 - gridWidth) {
        fragColor = mix(vec4(gridColor, 1.0), fragColor, 1.0 - gridOpacity);
    }
}
`;

// src/styles/textured/TerrainMesh.ts
var WATER_TYPES = ["sea" /* sea */, "coastal" /* coastal */];
var DIR_SE = { x: 0.8660254, y: 0.5 };
var DIR_S = { x: 0, y: 1 };
var DIR_SW = { x: -0.8660254, y: 0.5 };
var DIR_NW = { x: -0.8660254, y: -0.5 };
var DIR_N = { x: 0, y: -1 };
var DIR_NE = { x: 0.8660254, y: -0.5 };
var TERRAIN_DIRECTIONS = [DIR_SE, DIR_S, DIR_SW, DIR_NW, DIR_N, DIR_NE];
var TerrainMesh = class extends Group5 {
  constructor(map, options) {
    super();
    this.options = options;
    this.landMeshes = [];
    this.waterMeshes = [];
    //"x,y" -> its chunk's per-instance fog attribute + its index there
    this.landFog = /* @__PURE__ */ new Map();
    this.waterFog = /* @__PURE__ */ new Map();
    this.clock = 0;
    this.map = map;
    this.fogTexture = this.loadFogTexture();
    this.tileMap = this.buildTileMap();
    this.noiseTexture = this.buildNoiseTexture();
    this.shared = this.sharedUniforms();
    const allTiles = [];
    for (let x = 0; x < this.map.w; x++) {
      for (let y = 0; y < this.map.h; y++) {
        if (this.map.data[x]?.[y]) allTiles.push({ x, y });
      }
    }
    const isWater = (tile) => WATER_TYPES.includes(this.map.data[tile.x][tile.y].type);
    this.buildLandLayer(allTiles.filter((t) => !isWater(t)));
    this.buildWaterLayer(allTiles.filter(isWater));
  }
  //Terrain layer index (see TerrainLayers) of the tile at (x, y); -1 for
  //water types (no land layer) and for tiles off the map.
  layerFor(x, y) {
    const tile = this.map.data[x]?.[y];
    return tile ? this.options.layers.layerIndex(tile.type) : -1;
  }
  //Every tile's terrain layer in one W x H texture (R channel, 255 = none) -
  //the land shader looks up arbitrary tiles near a pixel with it, which is
  //what lets transitions ignore the hex outline (see terrain.fragment.ts).
  //G = 255 while the tile is Unseen (see setFogState()): the shader then
  //ignores it, so a fogged tile's terrain never bleeds into a visible one.
  buildTileMap() {
    const { w, h } = this.map;
    const data = new Uint8Array(w * h * 4);
    for (let x = 0; x < w; x++) {
      for (let y = 0; y < h; y++) {
        const layer = this.layerFor(x, y);
        data[(y * w + x) * 4] = layer < 0 ? 255 : layer;
        data[(y * w + x) * 4 + 3] = 255;
      }
    }
    const texture = new DataTexture(data, w, h, RGBAFormat, UnsignedByteType);
    texture.minFilter = texture.magFilter = NearestFilter;
    texture.needsUpdate = true;
    return texture;
  }
  //256x256 random values, 4 independent channels - the shaders' texNoise()
  //(see shaders/common.glsl.ts) reads value noise from it with one fetch
  //instead of four sin()-hashes. Seeded, so every load looks the same.
  buildNoiseTexture() {
    const data = new Uint8Array(256 * 256 * 4);
    let seed = 1234567;
    for (let i = 0; i < data.length; i++) {
      seed = seed * 1103515245 + 12345 >>> 0;
      data[i] = seed >>> 24;
    }
    const texture = new DataTexture(data, 256, 256, RGBAFormat, UnsignedByteType);
    texture.wrapS = texture.wrapT = RepeatWrapping;
    texture.minFilter = texture.magFilter = LinearFilter2;
    texture.needsUpdate = true;
    return texture;
  }
  //-1 no tile, 0 non-water, 1 sea, 2 coastal - drives the land layer's beach
  //slope and the water layer's edge-color resolution (see shaders).
  kindFor(x, y) {
    const row = this.map.data[x];
    const tile = row ? row[y] : void 0;
    if (!tile) return -1;
    const waterIndex = WATER_TYPES.indexOf(tile.type);
    return waterIndex === -1 ? 0 : waterIndex + 1;
  }
  //Builds the per-instance attribute arrays (offset/style/neighbors/neighbor
  //priorities/kinds) shared by every layer - land and water tiles are laid
  //out identically, only the geometry/shader differ.
  buildInstanceAttributes(tiles) {
    const { size } = this.options;
    const attrs = {
      offset: new Float32Array(tiles.length * 2),
      style: new Float32Array(tiles.length * 3),
      neighborsA: new Float32Array(tiles.length * 3),
      neighborsB: new Float32Array(tiles.length * 3),
      neighborsKindA: new Float32Array(tiles.length * 3),
      neighborsKindB: new Float32Array(tiles.length * 3),
      fogState: new Float32Array(tiles.length).fill(2)
      // default Visible - see FogOfWar.ts
    };
    tiles.forEach((tile, i) => {
      const info = this.map.data[tile.x][tile.y];
      const center = getHexCenter(tile.x, tile.y, size);
      attrs.offset[i * 2 + 0] = center.x;
      attrs.offset[i * 2 + 1] = center.y;
      attrs.style[i * 3 + 0] = this.layerFor(tile.x, tile.y);
      attrs.style[i * 3 + 1] = isRiverTile(info) || isLakeTile(info) ? 1 : 0;
      const se = getNeighborCoords(tile.x, tile.y, "SE");
      const s = getNeighborCoords(tile.x, tile.y, "S");
      const sw = getNeighborCoords(tile.x, tile.y, "SW");
      const nw = getNeighborCoords(tile.x, tile.y, "NW");
      const n = getNeighborCoords(tile.x, tile.y, "N");
      const ne = getNeighborCoords(tile.x, tile.y, "NE");
      attrs.neighborsA[i * 3 + 0] = this.layerFor(se.x, se.y);
      attrs.neighborsA[i * 3 + 1] = this.layerFor(s.x, s.y);
      attrs.neighborsA[i * 3 + 2] = this.layerFor(sw.x, sw.y);
      attrs.neighborsB[i * 3 + 0] = this.layerFor(nw.x, nw.y);
      attrs.neighborsB[i * 3 + 1] = this.layerFor(n.x, n.y);
      attrs.neighborsB[i * 3 + 2] = this.layerFor(ne.x, ne.y);
      attrs.neighborsKindA[i * 3 + 0] = this.kindFor(se.x, se.y);
      attrs.neighborsKindA[i * 3 + 1] = this.kindFor(s.x, s.y);
      attrs.neighborsKindA[i * 3 + 2] = this.kindFor(sw.x, sw.y);
      attrs.neighborsKindB[i * 3 + 0] = this.kindFor(nw.x, nw.y);
      attrs.neighborsKindB[i * 3 + 1] = this.kindFor(n.x, n.y);
      attrs.neighborsKindB[i * 3 + 2] = this.kindFor(ne.x, ne.y);
    });
    return attrs;
  }
  buildInstancedGeometry(tiles, hexagon) {
    const geometry = new InstancedBufferGeometry2();
    geometry.setAttribute("position", hexagon.getAttribute("position"));
    geometry.setAttribute("uv", hexagon.getAttribute("uv"));
    geometry.setIndex(hexagon.getIndex());
    geometry.instanceCount = tiles.length;
    const attrs = this.buildInstanceAttributes(tiles);
    geometry.setAttribute("offset", new InstancedBufferAttribute2(attrs.offset, 2));
    geometry.setAttribute("style", new InstancedBufferAttribute2(attrs.style, 3));
    geometry.setAttribute("neighborsA", new InstancedBufferAttribute2(attrs.neighborsA, 3));
    geometry.setAttribute("neighborsB", new InstancedBufferAttribute2(attrs.neighborsB, 3));
    geometry.setAttribute("neighborsKindA", new InstancedBufferAttribute2(attrs.neighborsKindA, 3));
    geometry.setAttribute("neighborsKindB", new InstancedBufferAttribute2(attrs.neighborsKindB, 3));
    geometry.setAttribute("fogState", new InstancedBufferAttribute2(attrs.fogState, 1));
    return geometry;
  }
  sharedUniforms() {
    const o = this.options;
    const size = o.size;
    const coast = o.coastField;
    return {
      hexSize: { value: size },
      uTime: { value: 0 },
      noiseTex: { value: this.noiseTexture },
      waterLevel: { value: -(o.waterDepth ?? size * 0.25) },
      beachWidth: { value: o.beachWidth ?? 0.35 },
      coastCurvature: { value: o.coastCurvature ?? 0.5 },
      coastField: { value: coast.texture },
      coastFieldOrigin: { value: new Vector22(coast.originX, coast.originZ) },
      coastFieldExtent: { value: new Vector22(coast.extentX, coast.extentZ) },
      waterColorShallow: { value: new Color5(o.waterColorShallow ?? LandColor["coastal" /* coastal */]) },
      waterColorDeep: { value: new Color5(o.waterColorDeep ?? LandColor["sea" /* sea */]) },
      sparkleIntensity: { value: o.waterSparkleIntensity ?? 1 },
      fresnelIntensity: { value: o.waterFresnelIntensity ?? 1 },
      foamEnabled: { value: o.coastalWavesEnabled ?? true ? 1 : 0 },
      foamColor: { value: new Color5(o.coastalWaveColor ?? 16777215) },
      foamCount: { value: o.coastalWaveCount ?? 3 },
      foamSpeed: { value: o.coastalWaveSpeed ?? 0.6 },
      foamWidth: { value: o.coastalWaveWidth ?? 0.3 },
      foamRange: { value: o.coastalWaveRange ?? 0.8 },
      foamDistortion: { value: o.coastalWaveDistortion ?? 0.5 },
      foamOpacity: { value: o.coastalWaveOpacity ?? 0.85 },
      fogMap: { value: this.fogTexture },
      fogDarkenFactor: { value: o.fogDarkenFactor ?? 0.45 },
      fogTextureSize: { value: o.fogTextureSize ?? size * 8 },
      lightDir: { value: new Vector39(0.4, 1, 0.3) },
      showGrid: { value: o.gridVisible === false ? 0 : 1 },
      gridColor: { value: new Color5(o.gridColor ?? 0) },
      gridWidth: { value: o.gridWidth ?? 0.04 },
      gridOpacity: { value: o.gridOpacity ?? 0.35 }
    };
  }
  //Value of a uniform shared by both materials (see `shared`).
  u(name) {
    return this.shared[name].value;
  }
  setU(name, value) {
    this.shared[name].value = value;
  }
  //war-fog.jpg (see FogOfWar.ts) - a single, non-atlased image sampled with
  //world-space UVs (see terrain/water vertex shaders' vFogUV), so one repeat
  //spans several tiles. RepeatWrapping is required for that (world UVs run
  //far past 0..1); mipmaps are fine here, unlike the atlas (a standalone
  //image has no neighboring cells to bleed into).
  loadFogTexture() {
    const loader = new TextureLoader().setPath(this.options.texturesBaseUrl);
    const texture = loader.load(this.options.fogTexture ?? "war-fog.jpg");
    texture.wrapS = texture.wrapT = RepeatWrapping;
    return texture;
  }
  //Subdivided (not a single flat triangle per wedge) so the beach slope and
  //landBlendWidth/beachWidth's smoothstep-based falloffs actually have interior
  //vertices to sample - with only the 2 outer corners + center (0 subdivisions),
  //the corners always saturate to fully-blended (edge factor is exactly 1 at
  //any hex corner) and the center is always 0, so the GPU only ever linearly
  //interpolates between those 2 fixed extremes no matter the configured width.
  buildLandLayer(tiles) {
    if (tiles.length === 0) return;
    const { layers, waterField, size } = this.options;
    this.landMaterial = new RawShaderMaterial({
      glslVersion: GLSL3,
      uniforms: {
        layerAlbedo: { value: layers.albedo },
        layerNormal: { value: layers.normal },
        layerScale: { value: this.options.layerScale ?? size * 1.6 },
        normalStrength: { value: this.options.normalStrength ?? 0.45 },
        sandLayer: { value: Math.max(layers.layerIndex("sand" /* sand */), 0) },
        tileMap: { value: this.tileMap },
        tileMapSize: { value: new Vector22(this.map.w, this.map.h) },
        waterField: { value: waterField.texture },
        waterFieldOrigin: { value: new Vector22(waterField.originX, waterField.originZ) },
        waterFieldExtent: { value: new Vector22(waterField.extentX, waterField.extentZ) },
        landBlendWidth: { value: this.options.landBlendWidth ?? 0.5 },
        landBlendCurvature: { value: this.options.landBlendCurvature ?? 0.5 },
        mountainLayer: { value: layers.layerIndex("mountain" /* mountain */) },
        mountainHeight: { value: this.options.mountainHeight ?? this.options.size * 0.6 },
        riverBankWidth: { value: this.options.riverBankWidth ?? 0.12 },
        riverEdgeNoise: { value: this.options.riverEdgeNoise ?? 0.5 },
        riverColorShallow: { value: new Color5(this.options.riverColorShallow ?? this.options.waterColorShallow ?? LandColor["coastal" /* coastal */]) },
        riverColorDeep: { value: new Color5(this.options.riverColorDeep ?? this.options.waterColorDeep ?? LandColor["sea" /* sea */]) },
        riverBankColor: { value: new Color5(this.options.riverBankColor ?? 7035454) },
        riverFlowSpeed: { value: this.options.riverFlowSpeed ?? 1 },
        riverDepth: { value: this.options.riverDepth ?? (this.options.waterDepth ?? this.options.size * 0.25) * 0.6 },
        ...this.shared
      },
      vertexShader: TERRAIN_VERTEX_SHADER,
      fragmentShader: TERRAIN_FRAGMENT_SHADER
    });
    this.landMeshes = this.buildChunks(tiles, this.landMaterial, this.landFog);
    this.updateBounds();
  }
  //One mesh per chunk of `tiles`, all on the same base hexagon (subdivision
  //3 for both layers: the mountain displacement needs the interior vertices,
  //and the vertices along a shared coastal edge must coincide or the two
  //layers open hairline cracks between them).
  buildChunks(tiles, material, fog) {
    const hexagon = createHexagonGeometry(this.options.size, 3);
    const meshes = [];
    for (const chunk of groupByChunk(tiles).values()) {
      const geometry = this.buildInstancedGeometry(chunk, hexagon);
      const attribute = geometry.getAttribute("fogState");
      chunk.forEach((tile, index) => fog.set(`${tile.x},${tile.y}`, { attribute, index }));
      const mesh = new Mesh6(geometry, material);
      mesh.userData.tiles = chunk;
      meshes.push(mesh);
      this.add(mesh);
    }
    return meshes;
  }
  //Chunk bounds for culling - vertices move in the shaders, so the boxes
  //cover the deepest river bed / wave trough up to the highest peak / crest
  //(recomputed when the mountain height or the wave amplitude changes).
  updateBounds() {
    const size = this.options.size;
    const deep = -((this.options.waterDepth ?? size * 0.25) * 2 + size * 0.5);
    const peak = Math.max(0, this.mountainHeight) * 1.5 + size * 0.5;
    const wave = Math.abs(this.waterWaveAmplitude) * 4 + size * 0.25;
    for (const mesh of this.landMeshes) {
      setBounds(mesh.geometry, chunkBox(mesh.userData.tiles, size, deep, peak).expandByScalar(size * 0.25));
    }
    for (const mesh of this.waterMeshes) {
      setBounds(mesh.geometry, chunkBox(mesh.userData.tiles, size, deep - wave, wave).expandByScalar(size * 0.25));
    }
  }
  //Water tiles: subdivided (see buildChunks) so the sum-of-sines wave
  //displacement in water.vertex.ts has enough resolution to look like a
  //smooth surface.
  buildWaterLayer(tiles) {
    if (tiles.length === 0) return;
    this.waterMaterial = new RawShaderMaterial({
      glslVersion: GLSL3,
      uniforms: {
        waveAmplitude: { value: this.options.waterWaveAmplitude ?? 1.6 },
        waveFrequency: { value: 0.045 * (this.options.waterWaveFrequency ?? 1) },
        waveSpeed: { value: this.options.waterWaveSpeed ?? 1 },
        ...this.shared
      },
      vertexShader: WATER_VERTEX_SHADER,
      fragmentShader: WATER_FRAGMENT_SHADER
    });
    this.waterMeshes = this.buildChunks(tiles, this.waterMaterial, this.waterFog);
    this.updateBounds();
  }
  //Advances the water/river animation. `dtS` is the elapsed time in seconds
  //since the previous frame - called once per frame by TexturedStyle.update.
  //One shared clock uniform drives waves, foam and river flow.
  update(dtS) {
    this.clock += dtS;
    this.setU("uTime", this.clock);
  }
  get gridVisible() {
    return this.u("showGrid") > 0;
  }
  set gridVisible(value) {
    this.setU("showGrid", value ? 1 : 0);
  }
  //-------------------------------------------------------------------------
  //Live shader-uniform tuning knobs, for a GUI to adjust without rebuilding
  //the map. Shared ones (see `shared`) reach both layers at once.
  //-------------------------------------------------------------------------
  landU(name, fallback) {
    return this.landMaterial?.uniforms[name]?.value ?? fallback;
  }
  setLandU(name, value) {
    if (this.landMaterial) this.landMaterial.uniforms[name].value = value;
  }
  waterU(name, fallback) {
    return this.waterMaterial?.uniforms[name]?.value ?? fallback;
  }
  setWaterU(name, value) {
    if (this.waterMaterial) this.waterMaterial.uniforms[name].value = value;
  }
  get landBlendWidth() {
    return this.landU("landBlendWidth", 0.5);
  }
  set landBlendWidth(value) {
    this.setLandU("landBlendWidth", value);
  }
  get landBlendCurvature() {
    return this.landU("landBlendCurvature", 0.5);
  }
  set landBlendCurvature(value) {
    this.setLandU("landBlendCurvature", value);
  }
  get mountainHeight() {
    return this.landU("mountainHeight", this.options.size * 0.6);
  }
  set mountainHeight(value) {
    this.setLandU("mountainHeight", value);
    this.updateBounds();
  }
  //River knobs - live uniforms on the land material (rivers are drawn by
  //the land layer's shaders from the baked WaterField).
  get riverBankWidth() {
    return this.landU("riverBankWidth", 0.12);
  }
  set riverBankWidth(value) {
    this.setLandU("riverBankWidth", value);
  }
  get riverEdgeNoise() {
    return this.landU("riverEdgeNoise", 0.5);
  }
  set riverEdgeNoise(value) {
    this.setLandU("riverEdgeNoise", value);
  }
  get riverColorShallow() {
    return this.landU("riverColorShallow", void 0)?.getHex() ?? 0;
  }
  set riverColorShallow(value) {
    this.landU("riverColorShallow", void 0)?.set(value);
  }
  get riverColorDeep() {
    return this.landU("riverColorDeep", void 0)?.getHex() ?? 0;
  }
  set riverColorDeep(value) {
    this.landU("riverColorDeep", void 0)?.set(value);
  }
  get riverBankColor() {
    return this.landU("riverBankColor", void 0)?.getHex() ?? 7035454;
  }
  set riverBankColor(value) {
    this.landU("riverBankColor", void 0)?.set(value);
  }
  get riverFlowSpeed() {
    return this.landU("riverFlowSpeed", 1);
  }
  set riverFlowSpeed(value) {
    this.setLandU("riverFlowSpeed", value);
  }
  get riverDepth() {
    return this.landU("riverDepth", this.options.size * 0.15);
  }
  set riverDepth(value) {
    this.setLandU("riverDepth", value);
  }
  //Coast (shared by both layers)
  get coastCurvature() {
    return this.u("coastCurvature");
  }
  set coastCurvature(value) {
    this.setU("coastCurvature", value);
  }
  get beachWidth() {
    return this.u("beachWidth");
  }
  set beachWidth(value) {
    this.setU("beachWidth", value);
  }
  //waterLevel uniform is negative (rest height below land); exposed here as
  //a positive "depth" to match the waterDepth constructor option's sign.
  get waterDepth() {
    return -this.u("waterLevel");
  }
  set waterDepth(value) {
    this.setU("waterLevel", -value);
  }
  //Waves (water layer only)
  get waterWaveAmplitude() {
    return this.waterU("waveAmplitude", 1.6);
  }
  set waterWaveAmplitude(value) {
    this.setWaterU("waveAmplitude", value);
    this.updateBounds();
  }
  //The stored uniform is pre-scaled by 0.045 (see buildWaterLayer) so the
  //raw shader frequency stays in a sane range - getter/setter work in the
  //same "multiplier" units as the constructor option.
  get waterWaveFrequency() {
    return this.waterU("waveFrequency", 0.045) / 0.045;
  }
  set waterWaveFrequency(value) {
    this.setWaterU("waveFrequency", 0.045 * value);
  }
  get waterWaveSpeed() {
    return this.waterU("waveSpeed", 1);
  }
  set waterWaveSpeed(value) {
    this.setWaterU("waveSpeed", value);
  }
  get waterSparkleIntensity() {
    return this.u("sparkleIntensity");
  }
  set waterSparkleIntensity(value) {
    this.setU("sparkleIntensity", value);
  }
  get waterFresnelIntensity() {
    return this.u("fresnelIntensity");
  }
  set waterFresnelIntensity(value) {
    this.setU("fresnelIntensity", value);
  }
  get waterColorShallow() {
    return this.u("waterColorShallow").getHex();
  }
  set waterColorShallow(value) {
    this.u("waterColorShallow").set(value);
  }
  get waterColorDeep() {
    return this.u("waterColorDeep").getHex();
  }
  set waterColorDeep(value) {
    this.u("waterColorDeep").set(value);
  }
  //Coastal foam waves (shared: water layer + the land layer's painted sea)
  get coastalWavesEnabled() {
    return this.u("foamEnabled") > 0.5;
  }
  set coastalWavesEnabled(value) {
    this.setU("foamEnabled", value ? 1 : 0);
  }
  get coastalWaveColor() {
    return this.u("foamColor").getHex();
  }
  set coastalWaveColor(value) {
    this.u("foamColor").set(value);
  }
  get coastalWaveCount() {
    return this.u("foamCount");
  }
  set coastalWaveCount(value) {
    this.setU("foamCount", value);
  }
  get coastalWaveSpeed() {
    return this.u("foamSpeed");
  }
  set coastalWaveSpeed(value) {
    this.setU("foamSpeed", value);
  }
  get coastalWaveWidth() {
    return this.u("foamWidth");
  }
  set coastalWaveWidth(value) {
    this.setU("foamWidth", value);
  }
  get coastalWaveRange() {
    return this.u("foamRange");
  }
  set coastalWaveRange(value) {
    this.setU("foamRange", value);
  }
  get coastalWaveDistortion() {
    return this.u("foamDistortion");
  }
  set coastalWaveDistortion(value) {
    this.setU("foamDistortion", value);
  }
  get coastalWaveOpacity() {
    return this.u("foamOpacity");
  }
  set coastalWaveOpacity(value) {
    this.setU("foamOpacity", value);
  }
  markerHeightForTile(x, y) {
    return this.terrainHeightAtTileLocal(x, y, 0, 0) + this.markerSurfaceOffset;
  }
  applyTerrainHeightToGeometry(geometry, x, y) {
    const position = geometry.getAttribute("position");
    if (!position) return;
    for (let i = 0; i < position.count; i++) {
      position.setY(i, this.terrainHeightAtTileLocal(x, y, position.getX(i), position.getZ(i)) + this.markerSurfaceOffset);
    }
    position.needsUpdate = true;
    geometry.computeVertexNormals();
    geometry.computeBoundingSphere();
  }
  terrainHeightAtTileLocal(x, y, localX, localZ) {
    const tile = this.map.data[x]?.[y];
    if (!tile) return 0;
    const center = getHexCenter(x, y, this.options.size);
    const worldX = center.x + localX, worldZ = center.y + localZ;
    const coastD = this.options.coastField.sample(worldX, worldZ)[0];
    if (WATER_TYPES.includes(tile.type)) return this.waterHeightAt(worldX, worldZ, coastD);
    let sinkY = this.shoreHeight(coastD);
    sinkY = Math.min(sinkY, this.riverSinkAt(worldX, worldZ));
    if (tile.type !== "mountain" /* mountain */ || isRiverTile(tile) || isLakeTile(tile)) return sinkY;
    return sinkY + this.mountainHeightAtTileLocal(x, y, localX, localZ) * this.mountainHeight;
  }
  get markerSurfaceOffset() {
    return Math.max(0.2, this.options.size * 0.01);
  }
  edgeFactors(localX, localZ) {
    const apothem = this.options.size * 0.8660254;
    return TERRAIN_DIRECTIONS.map((dir) => (localX * dir.x + localZ * dir.y) / apothem);
  }
  //CPU mirror of water.vertex.ts: waves dying down towards the shore, the
  //surface rising half way up to land's rest height at the tile boundary.
  waterHeightAt(worldX, worldZ, coastD) {
    const waveY = this.waterWaveHeight(worldX, worldZ) * (1 - this.smoothstep(-0.7, 0, coastD));
    return this.shoreHeight(coastD) + waveY;
  }
  //CPU mirror of shoreHeight() in shaders/common.glsl.ts.
  shoreHeight(sd) {
    const waterLevel = -this.waterDepth;
    if (sd >= 0) {
      const reach = 0.3 + Math.min(Math.max(this.beachWidth, 0), 1) * 0.4;
      return waterLevel * 0.5 * (1 - this.smoothstep(0, reach, sd));
    }
    const t = this.smoothstep(-0.7, 0, sd);
    return waterLevel * (1 - 0.5 * t * t);
  }
  waterWaveHeight(worldX, worldZ) {
    let height = 0;
    let amplitude = this.waterMaterial?.uniforms.waveAmplitude.value ?? 1.6;
    let frequency = this.waterMaterial?.uniforms.waveFrequency.value ?? 0.045;
    const speed = this.waterMaterial?.uniforms.waveSpeed.value ?? 1;
    let directionAngle = 0.4;
    const goldenAngle = 2.399963;
    for (let i = 0; i < 4; i++) {
      const dirX = Math.cos(directionAngle);
      const dirZ = Math.sin(directionAngle);
      const phase = (dirX * worldX + dirZ * worldZ) * frequency + this.clock * speed;
      height += amplitude * Math.sin(phase);
      amplitude *= 0.55;
      frequency *= 1.8;
      directionAngle += goldenAngle;
    }
    return height;
  }
  //CPU mirror of terrain.vertex.ts's riverBedAt() carve.
  riverSinkAt(worldX, worldZ) {
    const d = this.options.waterField.sample(worldX, worldZ)[0];
    return -this.riverDepth * (1 - this.smoothstep(-0.08, this.riverBankWidth, d));
  }
  mountainHeightAtTileLocal(x, y, localX, localZ) {
    const edge = this.edgeFactors(localX, localZ);
    let height = Math.pow(Math.min(Math.max(1 - Math.max(...edge), 0), 1), 1.2);
    const mSE = this.isMountainNeighbor(x, y, "SE"), mS = this.isMountainNeighbor(x, y, "S"), mSW = this.isMountainNeighbor(x, y, "SW");
    const mNW = this.isMountainNeighbor(x, y, "NW"), mN = this.isMountainNeighbor(x, y, "N"), mNE = this.isMountainNeighbor(x, y, "NE");
    let ridge = 0;
    if (mSE) ridge = Math.max(ridge, edge[0] * this.saddleTaper(mNE, edge[5]) * this.saddleTaper(mS, edge[1]));
    if (mS) ridge = Math.max(ridge, edge[1] * this.saddleTaper(mSE, edge[0]) * this.saddleTaper(mSW, edge[2]));
    if (mSW) ridge = Math.max(ridge, edge[2] * this.saddleTaper(mS, edge[1]) * this.saddleTaper(mNW, edge[3]));
    if (mNW) ridge = Math.max(ridge, edge[3] * this.saddleTaper(mSW, edge[2]) * this.saddleTaper(mN, edge[4]));
    if (mN) ridge = Math.max(ridge, edge[4] * this.saddleTaper(mNW, edge[3]) * this.saddleTaper(mNE, edge[5]));
    if (mNE) ridge = Math.max(ridge, edge[5] * this.saddleTaper(mN, edge[4]) * this.saddleTaper(mSE, edge[0]));
    height = Math.max(height, Math.min(Math.max(ridge, 0), 1) * 0.55);
    if (this.kindForNeighbor(x, y, "SE") >= 0.5) height *= 1 - this.smoothstep(0.5, 0.95, edge[0]);
    if (this.kindForNeighbor(x, y, "S") >= 0.5) height *= 1 - this.smoothstep(0.5, 0.95, edge[1]);
    if (this.kindForNeighbor(x, y, "SW") >= 0.5) height *= 1 - this.smoothstep(0.5, 0.95, edge[2]);
    if (this.kindForNeighbor(x, y, "NW") >= 0.5) height *= 1 - this.smoothstep(0.5, 0.95, edge[3]);
    if (this.kindForNeighbor(x, y, "N") >= 0.5) height *= 1 - this.smoothstep(0.5, 0.95, edge[4]);
    if (this.kindForNeighbor(x, y, "NE") >= 0.5) height *= 1 - this.smoothstep(0.5, 0.95, edge[5]);
    const center = getHexCenter(x, y, this.options.size);
    const worldX = center.x + localX;
    const worldZ = center.y + localZ;
    const noise = 0.65 * this.valueNoise(worldX * (1.6 / this.options.size), worldZ * (1.6 / this.options.size)) + 0.35 * this.valueNoise(worldX * (4 / this.options.size), worldZ * (4 / this.options.size));
    return height * (0.72 + 1.1 * (noise - 0.5));
  }
  kindForNeighbor(x, y, direction) {
    const point = getNeighborCoords(x, y, direction);
    return this.kindFor(point.x, point.y);
  }
  isMountainNeighbor(x, y, direction) {
    const point = getNeighborCoords(x, y, direction);
    return this.map.data[point.x]?.[point.y]?.type === "mountain" /* mountain */;
  }
  saddleTaper(adjIsMountain, adjFactor) {
    return adjIsMountain ? 1 : 1 - this.smoothstep(0.6, 1, adjFactor);
  }
  smoothstep(edge0, edge1, value) {
    const t = Math.min(Math.max((value - edge0) / (edge1 - edge0), 0), 1);
    return t * t * (3 - 2 * t);
  }
  valueNoise(x, y) {
    const ix = Math.floor(x);
    const iy = Math.floor(y);
    const fx = x - ix;
    const fy = y - iy;
    const ux = fx * fx * (3 - 2 * fx);
    const uy = fy * fy * (3 - 2 * fy);
    const a = this.mix(this.hash21(ix, iy), this.hash21(ix + 1, iy), ux);
    const b = this.mix(this.hash21(ix, iy + 1), this.hash21(ix + 1, iy + 1), ux);
    return this.mix(a, b, uy);
  }
  hash21(x, y) {
    const value = Math.sin(x * 127.1 + y * 311.7) * 43758.5453123;
    return value - Math.floor(value);
  }
  mix(a, b, t) {
    return a * (1 - t) + b * t;
  }
  //-------------------------------------------------------------------------
  //Fog of war (see FogOfWar.ts) - updates one tile's terrain (land or water,
  //whichever layer it's actually on) to the given state. Plain per-instance attribute writes, no rebuild.
  //-------------------------------------------------------------------------
  setFogState(x, y, state) {
    const key3 = `${x},${y}`;
    const at = this.landFog.get(key3) ?? this.waterFog.get(key3);
    if (at) {
      at.attribute.setX(at.index, state);
      at.attribute.needsUpdate = true;
    }
    if (x >= 0 && y >= 0 && x < this.map.w && y < this.map.h) {
      const data = this.tileMap.image.data;
      const g = state < 0.5 ? 255 : 0;
      if (data[(y * this.map.w + x) * 4 + 1] !== g) {
        data[(y * this.map.w + x) * 4 + 1] = g;
        this.tileMap.needsUpdate = true;
      }
    }
  }
  //Releases the land/water geometries, materials and atlas texture.
  dispose() {
    for (const mesh of [...this.landMeshes, ...this.waterMeshes]) mesh.geometry.dispose();
    this.landMaterial?.dispose();
    this.waterMaterial?.dispose();
    this.tileMap.dispose();
    this.noiseTexture.dispose();
    this.fogTexture.dispose();
  }
};

// src/styles/textured/TerrainLayers.ts
import { DataArrayTexture, ImageLoader, LinearFilter as LinearFilter3, LinearMipmapLinearFilter as LinearMipmapLinearFilter2, RepeatWrapping as RepeatWrapping2 } from "three";
async function loadArray(url, size, depth, anisotropy) {
  const image = await new ImageLoader().loadAsync(url);
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size * depth;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(image, 0, 0, size, size * depth);
  const data = new Uint8Array(ctx.getImageData(0, 0, size, size * depth).data.buffer);
  const texture = new DataArrayTexture(data, size, size, depth);
  texture.wrapS = texture.wrapT = RepeatWrapping2;
  texture.magFilter = LinearFilter3;
  texture.minFilter = LinearMipmapLinearFilter2;
  texture.generateMipmaps = true;
  texture.anisotropy = anisotropy;
  texture.needsUpdate = true;
  return texture;
}
async function loadTerrainLayers(baseUrl, anisotropy = 8) {
  const base = new URL(baseUrl, window.location.href);
  const info = await fetch(new URL("terrain-layers.json", base).href).then((r) => r.json());
  const [albedo, normal] = await Promise.all([
    loadArray(new URL(info.albedo, base).href, info.layerSize, info.layers.length, anisotropy),
    loadArray(new URL(info.normal, base).href, info.layerSize, info.layers.length, anisotropy)
  ]);
  return { info, albedo, normal, layerIndex: (type) => info.layers.indexOf(type) };
}

// src/styles/textured/waterField.ts
import { DataTexture as DataTexture2, RGBAFormat as RGBAFormat2, HalfFloatType, LinearFilter as LinearFilter4, ClampToEdgeWrapping, DataUtils } from "three";
var FAR = 2;
var TEXELS_PER_RADIUS = 10;
var LAKE_BLEND = 0.45;
var RIVER_LAKE_BLEND = 0.12;
var LAKE_DEPTH_SCALE = 0.2;
var WaterField = class {
  constructor(map, options) {
    this.map = map;
    this.options = options;
    this.network = { rivers: [], lakes: [] };
    const { size } = options;
    const margin = size * 1.5;
    this.texel = size / TEXELS_PER_RADIUS;
    this.originX = -margin;
    this.originZ = -margin;
    const maxX = (map.w - 1) * size * 1.5 + margin;
    const maxZ = map.h * size * Math.sqrt(3) + margin;
    this.width = Math.ceil((maxX - this.originX) / this.texel);
    this.height = Math.ceil((maxZ - this.originZ) / this.texel);
    this.data = new Float32Array(this.width * this.height * 4);
    this.halfs = new Uint16Array(this.width * this.height * 4);
    this.texture = new DataTexture2(this.halfs, this.width, this.height, RGBAFormat2, HalfFloatType);
    this.texture.minFilter = this.texture.magFilter = LinearFilter4;
    this.texture.wrapS = this.texture.wrapT = ClampToEdgeWrapping;
    this.texture.generateMipmaps = false;
    this.bake();
  }
  //World-space extent covered by the texture - shader uv = (xz - origin) / extent.
  get extentX() {
    return this.width * this.texel;
  }
  get extentZ() {
    return this.height * this.texel;
  }
  //Re-bakes with changed river options (width/meander) - live GUI tuning.
  update(options) {
    Object.assign(this.options, options);
    this.bake();
  }
  bake() {
    const { size } = this.options;
    const { width, height, texel, data } = this;
    this.network = buildRiverNetwork(this.map, this.options);
    for (let i = 0; i < width * height; i++) {
      data[i * 4] = FAR;
      data[i * 4 + 1] = 0;
      data[i * 4 + 2] = 0;
      data[i * 4 + 3] = 0;
    }
    const reach = size;
    for (const river of this.network.rivers) {
      const pts = river.points;
      const segments = pts.length === 1 ? [[pts[0], pts[0]]] : pts.slice(1).map((p, i) => [pts[i], p]);
      for (const [a, b] of segments) {
        const dx = b.x - a.x, dz = b.z - a.z;
        const len2 = dx * dx + dz * dz;
        const len = Math.sqrt(len2) || 1;
        const pad = Math.max(a.width, b.width) + reach;
        const i0 = Math.max(0, Math.floor((Math.min(a.x, b.x) - pad - this.originX) / texel));
        const i1 = Math.min(width - 1, Math.ceil((Math.max(a.x, b.x) + pad - this.originX) / texel));
        const j0 = Math.max(0, Math.floor((Math.min(a.z, b.z) - pad - this.originZ) / texel));
        const j1 = Math.min(height - 1, Math.ceil((Math.max(a.z, b.z) + pad - this.originZ) / texel));
        for (let j = j0; j <= j1; j++) {
          const pz = this.originZ + (j + 0.5) * texel;
          for (let i = i0; i <= i1; i++) {
            const px = this.originX + (i + 0.5) * texel;
            const t = len2 > 0 ? Math.min(Math.max(((px - a.x) * dx + (pz - a.z) * dz) / len2, 0), 1) : 0;
            const w = a.width + (b.width - a.width) * t;
            const d = (Math.hypot(px - a.x - dx * t, pz - a.z - dz * t) - w) / size;
            const o = (j * width + i) * 4;
            if (d < data[o]) {
              data[o] = d;
              data[o + 1] = dx / len;
              data[o + 2] = dz / len;
              data[o + 3] = w / size;
            }
          }
        }
      }
    }
    const radius = this.options.lakeRadius ?? 0.75;
    for (const lake of this.network.lakes) {
      let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
      lake.centers.forEach((c) => {
        minX = Math.min(minX, c.x);
        maxX = Math.max(maxX, c.x);
        minZ = Math.min(minZ, c.z);
        maxZ = Math.max(maxZ, c.z);
      });
      const pad = size * (radius + 1);
      const i0 = Math.max(0, Math.floor((minX - pad - this.originX) / texel));
      const i1 = Math.min(width - 1, Math.ceil((maxX + pad - this.originX) / texel));
      const j0 = Math.max(0, Math.floor((minZ - pad - this.originZ) / texel));
      const j1 = Math.min(height - 1, Math.ceil((maxZ + pad - this.originZ) / texel));
      for (let j = j0; j <= j1; j++) {
        const pz = this.originZ + (j + 0.5) * texel;
        for (let i = i0; i <= i1; i++) {
          const px = this.originX + (i + 0.5) * texel;
          let sum = 0;
          for (const c of lake.centers) sum += Math.exp(-(Math.hypot(px - c.x, pz - c.z) / size - radius) / LAKE_BLEND);
          const lakeD = -LAKE_BLEND * Math.log(sum);
          const o = (j * width + i) * 4;
          const riverD = data[o];
          const h = Math.min(Math.max(0.5 + 0.5 * (lakeD - riverD) / RIVER_LAKE_BLEND, 0), 1);
          const d = lakeD + (riverD - lakeD) * h - RIVER_LAKE_BLEND * h * (1 - h);
          data[o] = Math.min(d, FAR);
          data[o + 1] *= h;
          data[o + 2] *= h;
          data[o + 3] = LAKE_DEPTH_SCALE + (data[o + 3] - LAKE_DEPTH_SCALE) * h;
        }
      }
    }
    for (let i = 0; i < data.length; i++) this.halfs[i] = DataUtils.toHalfFloat(data[i]);
    this.texture.needsUpdate = true;
  }
  //Bilinear sample matching the GPU's LinearFilter lookup: [distance, flowX, flowZ, halfWidth].
  sample(worldX, worldZ) {
    const fx = Math.min(Math.max((worldX - this.originX) / this.texel - 0.5, 0), this.width - 1);
    const fz = Math.min(Math.max((worldZ - this.originZ) / this.texel - 0.5, 0), this.height - 1);
    const i = Math.min(Math.floor(fx), this.width - 2), j = Math.min(Math.floor(fz), this.height - 2);
    const u = fx - i, v = fz - j;
    const out = [0, 0, 0, 0];
    for (let c = 0; c < 4; c++) {
      const a = this.data[(j * this.width + i) * 4 + c], b = this.data[(j * this.width + i + 1) * 4 + c];
      const d = this.data[((j + 1) * this.width + i) * 4 + c], e = this.data[((j + 1) * this.width + i + 1) * 4 + c];
      out[c] = (a * (1 - u) + b * u) * (1 - v) + (d * (1 - u) + e * u) * v;
    }
    return out;
  }
  //True when a world point lies in river/lake water, or within `margin` tile
  //radii of it (the shader's waterline noise wobbles rivers by up to ~0.04,
  //lakes by up to ~0.12 - pass a margin covering that).
  isWater(worldX, worldZ, margin = 0.06) {
    return this.sample(worldX, worldZ)[0] < margin;
  }
  dispose() {
    this.texture.dispose();
  }
};

// src/styles/textured/coastField.ts
import { DataTexture as DataTexture3, RGBAFormat as RGBAFormat3, HalfFloatType as HalfFloatType2, LinearFilter as LinearFilter5, ClampToEdgeWrapping as ClampToEdgeWrapping2, DataUtils as DataUtils2 } from "three";
var TEXELS_PER_RADIUS2 = 8;
var COAST_BLUR = 3;
var SEA_BLUR = 5;
var COAST_INSET = 0.15;
var COAST_NOISE = 0.35;
function isWaterType(tile) {
  return !tile || tile.type === "sea" /* sea */ || tile.type === "coastal" /* coastal */;
}
function hash21(x, y) {
  const v = Math.sin(x * 127.1 + y * 311.7) * 43758.5453123;
  return v - Math.floor(v);
}
function valueNoise(x, y) {
  const ix = Math.floor(x), iy = Math.floor(y);
  const fx = x - ix, fy = y - iy;
  const ux = fx * fx * (3 - 2 * fx), uy = fy * fy * (3 - 2 * fy);
  const a = hash21(ix, iy) + (hash21(ix + 1, iy) - hash21(ix, iy)) * ux;
  const b = hash21(ix, iy + 1) + (hash21(ix + 1, iy + 1) - hash21(ix, iy + 1)) * ux;
  return a + (b - a) * uy;
}
function edt1d(f, n, d, v, z) {
  let k = 0;
  v[0] = 0;
  z[0] = -Infinity;
  z[1] = Infinity;
  for (let q = 1; q < n; q++) {
    let s = (f[q] + q * q - (f[v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k]);
    while (s <= z[k]) {
      k--;
      s = (f[q] + q * q - (f[v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k]);
    }
    k++;
    v[k] = q;
    z[k] = s;
    z[k + 1] = Infinity;
  }
  k = 0;
  for (let q = 0; q < n; q++) {
    while (z[k + 1] < q) k++;
    d[q] = (q - v[k]) * (q - v[k]) + f[v[k]];
  }
}
function distanceTo(inside, w, h) {
  const INF = 1e20;
  const grid = new Float32Array(w * h);
  for (let i = 0; i < w * h; i++) grid[i] = inside[i] ? 0 : INF;
  const n = Math.max(w, h);
  const f = new Float32Array(n), d = new Float32Array(n), z = new Float32Array(n + 1);
  const v = new Int32Array(n);
  for (let x = 0; x < w; x++) {
    for (let y = 0; y < h; y++) f[y] = grid[y * w + x];
    edt1d(f, h, d, v, z);
    for (let y = 0; y < h; y++) grid[y * w + x] = d[y];
  }
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) f[x] = grid[y * w + x];
    edt1d(f, w, d, v, z);
    for (let x = 0; x < w; x++) grid[y * w + x] = Math.sqrt(d[x]);
  }
  return grid;
}
function boxBlur(src, w, h, r) {
  const tmp = new Float32Array(Math.max(w, h));
  const span = 2 * r + 1;
  for (let y = 0; y < h; y++) {
    let acc = 0;
    for (let i = -r; i <= r; i++) acc += src[y * w + Math.min(Math.max(i, 0), w - 1)];
    for (let x = 0; x < w; x++) {
      tmp[x] = acc / span;
      acc += src[y * w + Math.min(x + r + 1, w - 1)] - src[y * w + Math.max(x - r, 0)];
    }
    for (let x = 0; x < w; x++) src[y * w + x] = tmp[x];
  }
  for (let x = 0; x < w; x++) {
    let acc = 0;
    for (let i = -r; i <= r; i++) acc += src[Math.min(Math.max(i, 0), h - 1) * w + x];
    for (let y = 0; y < h; y++) {
      tmp[y] = acc / span;
      acc += src[Math.min(y + r + 1, h - 1) * w + x] - src[Math.max(y - r, 0) * w + x];
    }
    for (let y = 0; y < h; y++) src[y * w + x] = tmp[y];
  }
}
var CoastField = class {
  constructor(map, size) {
    this.size = size;
    const margin = size * 2;
    this.texel = size / TEXELS_PER_RADIUS2;
    this.originX = -margin;
    this.originZ = -margin;
    this.width = Math.ceil(((map.w - 1) * size * 1.5 + 2 * margin) / this.texel);
    this.height = Math.ceil((map.h * size * Math.sqrt(3) + 2 * margin) / this.texel);
    const { width: w, height: h, texel } = this;
    const n = w * h;
    const land = new Uint8Array(n);
    const water = new Uint8Array(n);
    const sea = new Float32Array(n);
    const noise = new Float32Array(n);
    for (let j = 0; j < h; j++) {
      const pz = this.originZ + (j + 0.5) * texel;
      for (let i = 0; i < w; i++) {
        const px = this.originX + (i + 0.5) * texel;
        const t = worldToTile(px, pz, size);
        const tile = map.data[t.x]?.[t.y];
        const k = j * w + i;
        if (isWaterType(tile)) water[k] = 1;
        else land[k] = 1;
        sea[k] = !tile || tile.type === "sea" /* sea */ ? 1 : 0;
        noise[k] = 0.6 * valueNoise(px * (1.3 / size), pz * (1.3 / size)) + 0.4 * valueNoise(px * (3.2 / size), pz * (3.2 / size));
      }
    }
    const toWater = distanceTo(water, w, h);
    const toLand = distanceTo(land, w, h);
    const sd = new Float32Array(n);
    for (let k = 0; k < n; k++) {
      sd[k] = (land[k] ? toWater[k] - 0.5 : -(toLand[k] - 0.5)) * texel / size;
    }
    boxBlur(sd, w, h, COAST_BLUR);
    boxBlur(sd, w, h, COAST_BLUR);
    boxBlur(sea, w, h, SEA_BLUR);
    boxBlur(sea, w, h, SEA_BLUR);
    this.data = new Float32Array(n * 4);
    const halfs = new Uint16Array(n * 4);
    for (let k = 0; k < n; k++) {
      this.data[k * 4] = sd[k];
      this.data[k * 4 + 1] = sea[k];
      this.data[k * 4 + 2] = noise[k];
    }
    for (let i = 0; i < n * 4; i++) halfs[i] = DataUtils2.toHalfFloat(this.data[i]);
    this.texture = new DataTexture3(halfs, w, h, RGBAFormat3, HalfFloatType2);
    this.texture.minFilter = this.texture.magFilter = LinearFilter5;
    this.texture.wrapS = this.texture.wrapT = ClampToEdgeWrapping2;
    this.texture.generateMipmaps = false;
    this.texture.needsUpdate = true;
  }
  get extentX() {
    return this.width * this.texel;
  }
  get extentZ() {
    return this.height * this.texel;
  }
  //Bilinear sample matching the GPU lookup: [distance, sea, noise].
  sample(worldX, worldZ) {
    const fx = Math.min(Math.max((worldX - this.originX) / this.texel - 0.5, 0), this.width - 1.001);
    const fz = Math.min(Math.max((worldZ - this.originZ) / this.texel - 0.5, 0), this.height - 1.001);
    const i = Math.floor(fx), j = Math.floor(fz);
    const u = fx - i, v = fz - j;
    const out = [0, 0, 0];
    for (let c = 0; c < 3; c++) {
      const a = this.data[(j * this.width + i) * 4 + c], b = this.data[(j * this.width + i + 1) * 4 + c];
      const d = this.data[((j + 1) * this.width + i) * 4 + c], e = this.data[((j + 1) * this.width + i + 1) * 4 + c];
      out[c] = (a * (1 - u) + b * u) * (1 - v) + (d * (1 - u) + e * u) * v;
    }
    return out;
  }
  //CPU mirror of coast.glsl.ts's coastLine(): < 0 in the sea, 0 on the
  //visible waterline, tile radii inland beyond it.
  coastLine(worldX, worldZ, coastCurvature) {
    const [d, , n] = this.sample(worldX, worldZ);
    return d - COAST_INSET - n * coastCurvature * COAST_NOISE;
  }
  //True on the beach or in the sea - where the land shader paints sand or
  //water (see the beachBand in terrain.fragment.ts), plus `margin` tile radii.
  isShore(worldX, worldZ, coastCurvature, beachWidth, margin = 0.05) {
    const beachBand = 0.06 + Math.min(Math.max(beachWidth, 0), 1) * 0.22;
    return this.coastLine(worldX, worldZ, coastCurvature) < beachBand + margin;
  }
  dispose() {
    this.texture.dispose();
  }
};

// src/styles/textured/Forest.ts
import {
  InstancedMesh,
  InstancedBufferAttribute as InstancedBufferAttribute3,
  Matrix4 as Matrix43,
  Group as Group6,
  DynamicDrawUsage,
  Vector3 as Vector310
} from "three";
var ForestField = class extends Group6 {
  constructor(tileRanges, fogDarkenFactor) {
    super();
    this.tileRanges = tileRanges;
    this.fogDarkenFactor = fogDarkenFactor;
    this.hiddenMatrix = new Matrix43().makeScale(0, 0, 0);
  }
  setFogState(x, y, state) {
    const range = this.tileRanges.get(`${x},${y}`);
    if (!range) return;
    const hidden = state < 0.5;
    const shade = state < 1.5 ? this.fogDarkenFactor : 1;
    for (const instancedMesh of range.instancedMeshes) {
      for (let i = 0; i < range.count; i++) {
        const idx = range.start + i;
        instancedMesh.setMatrixAt(idx, hidden ? this.hiddenMatrix : range.originalMatrices[i]);
        instancedMesh.instanceColor?.setXYZ(idx, shade, shade, shade);
      }
      instancedMesh.instanceMatrix.needsUpdate = true;
      if (instancedMesh.instanceColor) instancedMesh.instanceColor.needsUpdate = true;
    }
  }
};
async function createForest(map, options) {
  const { size } = options;
  const treesPerTile = options.treesPerTile ?? 20;
  const defaultModel = options.treeModel ?? "Assets/models/pinia";
  const treeScale = options.treeScale ?? 1;
  const fogDarkenFactor = options.fogDarkenFactor ?? 0.45;
  const tilesByModel = /* @__PURE__ */ new Map();
  for (let x = 0; x < map.w; x++) {
    for (let y = 0; y < map.h; y++) {
      const tile = map.data[x]?.[y];
      if (!tile?.modifiers?.includes("wood") || isLakeTile(tile) || options.cleared?.has(`${x},${y}`)) continue;
      const modelPath = tile.treeModel ?? defaultModel;
      const tiles = tilesByModel.get(modelPath) ?? [];
      tiles.push({ x, y });
      tilesByModel.set(modelPath, tiles);
    }
  }
  if (tilesByModel.size === 0) return null;
  const treeFootprint = Math.max(1, Math.round(size / 10));
  const polygon = HEXPolygon({ x: 0, y: 0 }, size - treeFootprint).map((p) => [p.x, p.y]);
  const waterField = options.waterField;
  const coastField = options.coastField;
  const beachWidth = options.beachWidth ?? 0.35;
  const coastCurvature = options.coastCurvature ?? 0.5;
  const tileRanges = /* @__PURE__ */ new Map();
  const group = new ForestField(tileRanges, fogDarkenFactor);
  for (const [modelPath, tiles] of tilesByModel) {
    const { scene, fixup } = await loadModel(modelPath);
    const meshes = [];
    scene.traverse((o) => {
      if (o.isMesh) meshes.push(o);
    });
    if (meshes.length === 0) continue;
    const parts = meshes.map((mesh) => {
      const geometry = mesh.geometry.clone();
      geometry.applyMatrix4(mesh.matrixWorld);
      geometry.applyMatrix4(fixup);
      return { geometry, material: mesh.material };
    });
    for (const chunk of groupByChunk(tiles).values()) {
      const totalInstances = chunk.length * treesPerTile;
      const instancedMeshes = parts.map(({ geometry, material }) => {
        const instancedMesh = new InstancedMesh(geometry, material, totalInstances);
        instancedMesh.instanceMatrix.setUsage(DynamicDrawUsage);
        instancedMesh.instanceColor = new InstancedBufferAttribute3(new Float32Array(totalInstances * 3).fill(1), 3);
        group.add(instancedMesh);
        return instancedMesh;
      });
      const matrix = new Matrix43();
      const scaleVector = new Vector310();
      let instance = 0;
      for (const tile of chunk) {
        const center = getHexCenter(tile.x, tile.y, size);
        const placed = [];
        const tileStart = instance;
        const originalMatrices = [];
        let attempts = 0;
        while (placed.length < treesPerTile && attempts < treesPerTile * 20) {
          attempts++;
          const lx = getRandomInt(-size, size);
          const ly = getRandomInt(-size, size);
          if (!insideConvex(polygon, lx, ly)) continue;
          if (waterField?.isWater(center.x + lx, center.y + ly, 0.18)) continue;
          if (coastField?.isShore(center.x + lx, center.y + ly, coastCurvature, beachWidth, 0.08)) continue;
          const overlaps = placed.some((p) => Math.abs(p.x - lx) < treeFootprint && Math.abs(p.y - ly) < treeFootprint);
          if (overlaps) continue;
          placed.push({ x: lx, y: ly });
          const scale = treeScale * (0.8 + Math.random() * 0.4);
          matrix.makeRotationY(Math.random() * Math.PI * 2);
          matrix.scale(scaleVector.set(scale, scale, scale));
          matrix.setPosition(center.x + lx, 0, center.y + ly);
          for (const instancedMesh of instancedMeshes) instancedMesh.setMatrixAt(instance, matrix);
          originalMatrices.push(matrix.clone());
          instance++;
        }
        tileRanges.set(`${tile.x},${tile.y}`, { instancedMeshes, start: tileStart, count: instance - tileStart, originalMatrices });
      }
      for (const instancedMesh of instancedMeshes) {
        instancedMesh.count = instance;
        instancedMesh.instanceMatrix.needsUpdate = true;
        instancedMesh.computeBoundingSphere();
      }
    }
  }
  return group;
}

// src/styles/textured/Grass.ts
import {
  BufferGeometry as BufferGeometry6,
  Float32BufferAttribute,
  InstancedBufferGeometry as InstancedBufferGeometry3,
  InstancedBufferAttribute as InstancedBufferAttribute4,
  Mesh as Mesh8,
  RawShaderMaterial as RawShaderMaterial2,
  Color as Color6,
  DoubleSide as DoubleSide3,
  Group as Group7
} from "three";

// src/styles/textured/shaders/grass.vertex.ts
var GRASS_VERTEX_SHADER = `
precision mediump float;

uniform mat4 modelViewMatrix;
uniform mat4 projectionMatrix;

uniform float uTime;
uniform float windStrength;
uniform float windSpeed;

// Blade shape authored once in local space (see Grass.ts buildBladeGeometry):
// x spans [-0.5, 0.5] at the root and tapers to 0 at the tip, y is a plain
// [0, 1] height factor (0 = root, 1 = tip) - not a world-unit height, that's
// what the per-instance "scale" attribute is for.
attribute vec3 position;

attribute vec2 offset;  // world XZ position of this blade's root
attribute float angle;  // random Y rotation, radians - so blades don't all face the same way
attribute vec2 scale;   // x = width multiplier, y = height multiplier (world units)
attribute float phase;  // random wind phase offset, see wave below
attribute float shade;  // random per-blade brightness multiplier (clump variation)
attribute float fogState; // 0 = unseen (blade hidden), 1 = explored (darkened), 2 = visible - see FogOfWar.ts

varying float vHeightFactor;
varying float vShade;
varying float vFogState;

void main() {
    float heightFactor = position.y;
    vec3 p = vec3(position.x * scale.x, position.y * scale.y, position.z * scale.x);

    float s = sin(angle);
    float c = cos(angle);
    vec3 rotated = vec3(p.x * c - p.z * s, p.y, p.x * s + p.z * c);

    // Wind bends the blade towards its tip only (heightFactor^2 keeps the root
    // planted) - phase is offset by world position so a gust visibly travels
    // across the field instead of every blade swaying in lockstep.
    float wave = sin(uTime * windSpeed + phase + (offset.x + offset.y) * 0.015);
    float bend = wave * windStrength * heightFactor * heightFactor;
    rotated.x += bend;
    rotated.z += bend * 0.4;

    vec3 worldPos = vec3(offset.x + rotated.x, rotated.y, offset.y + rotated.z);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(worldPos, 1.0);

    vHeightFactor = heightFactor;
    vShade = shade;
    vFogState = fogState;
}
`;

// src/styles/textured/shaders/grass.fragment.ts
var GRASS_FRAGMENT_SHADER = `
precision mediump float;

uniform vec3 colorBase;
uniform vec3 colorTip;
uniform float fogDarkenFactor;

varying float vHeightFactor;
varying float vShade;
varying float vFogState;

void main() {
    // Unseen: no feature should show at all under the war-fog tile.
    if (vFogState < 0.5) discard;

    vec3 color = mix(colorBase, colorTip, vHeightFactor) * vShade;

    // Explored: keep the blade visible, just darker (mirrors terrain.fragment.ts).
    if (vFogState < 1.5) color *= fogDarkenFactor;

    gl_FragColor = vec4(color, 1.0);
}
`;

// src/styles/textured/Grass.ts
var GrassField = class extends Group7 {
  constructor(chunks, material, tileRanges) {
    super();
    this.tileRanges = tileRanges;
    this.clock = 0;
    this.grassMaterial = material;
    for (const chunk of chunks) this.add(chunk);
  }
  //Updates every blade belonging to (x, y) to the given fog state (see
  //FogOfWar.ts) - a plain attribute-slice fill + needsUpdate, no rebuild.
  //No-op for tiles with no grass (city tiles, non-"land" terrain).
  setFogState(x, y, state) {
    const range = this.tileRanges.get(`${x},${y}`);
    if (!range) return;
    for (let i = 0; i < range.count; i++) range.attribute.setX(range.start + i, state);
    range.attribute.needsUpdate = true;
  }
  //Advances the wind animation. `dtS` is the elapsed time in seconds since
  //the previous frame - call this once per frame (TexturedStyle.update).
  update(dtS) {
    this.clock += dtS;
    this.grassMaterial.uniforms.uTime.value = this.clock;
  }
  get windStrength() {
    return this.grassMaterial.uniforms.windStrength.value;
  }
  set windStrength(value) {
    this.grassMaterial.uniforms.windStrength.value = value;
  }
  get windSpeed() {
    return this.grassMaterial.uniforms.windSpeed.value;
  }
  set windSpeed(value) {
    this.grassMaterial.uniforms.windSpeed.value = value;
  }
  dispose() {
    for (const chunk of this.children) chunk.geometry.dispose();
    this.grassMaterial.dispose();
  }
};
function buildBladeGeometry() {
  const positions = new Float32Array([
    -0.5,
    0,
    0,
    0.5,
    0,
    0,
    -0.25,
    0.5,
    0,
    0.25,
    0.5,
    0,
    0,
    1,
    0
  ]);
  const index = [0, 1, 2, 1, 3, 2, 2, 3, 4];
  const geometry = new BufferGeometry6();
  geometry.setAttribute("position", new Float32BufferAttribute(positions, 3));
  geometry.setIndex(index);
  return geometry;
}
function createGrassField(map, options) {
  const { size } = options;
  const density = options.density ?? 60;
  if (density <= 0) return null;
  const bladeWidth = options.bladeWidth ?? size * 0.03;
  const bladeHeight = options.bladeHeight ?? size * 0.18;
  const heightVariation = options.heightVariation ?? 0.4;
  const windStrength = options.windStrength ?? bladeHeight * 0.35;
  const windSpeed = options.windSpeed ?? 1.2;
  const tiles = [];
  for (let x = 0; x < map.w; x++) {
    for (let y = 0; y < map.h; y++) {
      const tile = map.data[x]?.[y];
      if (tile?.type === "land" /* land */ && !isLakeTile(tile) && !options.cleared?.has(`${x},${y}`)) tiles.push({ x, y });
    }
  }
  if (tiles.length === 0) return null;
  const polygon = HEXPolygon({ x: 0, y: 0 }, size * 0.8).map((p) => [p.x, p.y]);
  const waterField = options.waterField;
  const coastField = options.coastField;
  const beachWidth = options.beachWidth ?? 0.35;
  const coastCurvature = options.coastCurvature ?? 0.5;
  const tileRanges = /* @__PURE__ */ new Map();
  const blade = buildBladeGeometry();
  const geometries = [];
  for (const chunk of groupByChunk(tiles).values()) {
    const count = chunk.length * density;
    const offsets = new Float32Array(count * 2);
    const angles = new Float32Array(count);
    const scales = new Float32Array(count * 2);
    const phases = new Float32Array(count);
    const shades = new Float32Array(count);
    const fogStates = new Float32Array(count).fill(2);
    const fogAttribute = new InstancedBufferAttribute4(fogStates, 1);
    let instance = 0;
    for (const tile of chunk) {
      const center = getHexCenter(tile.x, tile.y, size);
      const tileStart = instance;
      for (let i = 0; i < density; i++) {
        let lx = 0, ly = 0, attempts = 0, valid = false;
        while (!valid && attempts < 20) {
          lx = getRandomInt(-size, size);
          ly = getRandomInt(-size, size);
          valid = insideConvex(polygon, lx, ly) && !waterField?.isWater(center.x + lx, center.y + ly, 0.15) && !coastField?.isShore(center.x + lx, center.y + ly, coastCurvature, beachWidth, 0.02);
          attempts++;
        }
        if (!valid) continue;
        offsets[instance * 2 + 0] = center.x + lx;
        offsets[instance * 2 + 1] = center.y + ly;
        angles[instance] = Math.random() * Math.PI * 2;
        const heightJitter = 1 - heightVariation * 0.5 + Math.random() * heightVariation;
        scales[instance * 2 + 0] = bladeWidth * (0.8 + Math.random() * 0.4);
        scales[instance * 2 + 1] = bladeHeight * heightJitter;
        phases[instance] = Math.random() * Math.PI * 2;
        shades[instance] = 0.75 + Math.random() * 0.35;
        instance++;
      }
      tileRanges.set(`${tile.x},${tile.y}`, { attribute: fogAttribute, start: tileStart, count: instance - tileStart });
    }
    const geometry = new InstancedBufferGeometry3();
    geometry.setAttribute("position", blade.getAttribute("position"));
    geometry.setIndex(blade.getIndex());
    geometry.instanceCount = instance;
    geometry.setAttribute("offset", new InstancedBufferAttribute4(offsets, 2));
    geometry.setAttribute("angle", new InstancedBufferAttribute4(angles, 1));
    geometry.setAttribute("scale", new InstancedBufferAttribute4(scales, 2));
    geometry.setAttribute("phase", new InstancedBufferAttribute4(phases, 1));
    geometry.setAttribute("shade", new InstancedBufferAttribute4(shades, 1));
    geometry.setAttribute("fogState", fogAttribute);
    setBounds(geometry, chunkBox(chunk, size, -size * 0.25, bladeHeight * 2 + windStrength + size * 0.25));
    geometries.push(geometry);
  }
  const material = new RawShaderMaterial2({
    uniforms: {
      uTime: { value: 0 },
      windStrength: { value: windStrength },
      windSpeed: { value: windSpeed },
      colorBase: { value: new Color6(options.colorBase ?? 3960366) },
      colorTip: { value: new Color6(options.colorTip ?? 9424474) },
      fogDarkenFactor: { value: options.fogDarkenFactor ?? 0.45 }
    },
    vertexShader: GRASS_VERTEX_SHADER,
    fragmentShader: GRASS_FRAGMENT_SHADER,
    side: DoubleSide3
  });
  return new GrassField(geometries.map((g) => new Mesh8(g, material)), material, tileRanges);
}

// src/styles/textured/TexturedStyle.ts
var DEFAULT_OPTIONS2 = {
  texturesBaseUrl: "textures/",
  gridColor: 4338219,
  gridWidth: 0.04,
  gridOpacity: 0.35,
  treesPerTile: 20,
  waterColorShallow: LandColor["coastal" /* coastal */],
  waterColorDeep: LandColor["sea" /* sea */],
  waterWaveAmplitude: 1.6,
  waterWaveFrequency: 1,
  waterWaveSpeed: 1,
  waterSparkleIntensity: 1,
  waterFresnelIntensity: 1,
  coastalWavesEnabled: true,
  coastalWaveColor: 16777215,
  coastalWaveCount: 3,
  coastalWaveSpeed: 0.6,
  coastalWaveWidth: 0.3,
  coastalWaveRange: 0.8,
  coastalWaveDistortion: 0.5,
  coastalWaveOpacity: 0.85,
  beachWidth: 0.35,
  landBlendWidth: 0.5,
  coastCurvature: 0.5,
  landBlendCurvature: 0.5,
  riverWidth: 0.1,
  riverMaxWidth: 0.25,
  riverCurvature: 0.5,
  riverBankWidth: 0.12,
  riverEdgeNoise: 0.5,
  riverBankColor: 7035454,
  riverFlowSpeed: 1,
  treeModel: "Assets/models/pinia",
  treeScale: 1,
  grassEnabled: true,
  grassDensity: 60,
  grassBladeWidth: 1.2,
  grassBladeHeight: 7.2,
  grassWindStrength: 2.5,
  grassWindSpeed: 1.2,
  fogTexture: "war-fog.jpg"
};
var TexturedStyle = class {
  constructor(options = {}) {
    //"x,y" tiles with no grass/trees (setTileCleared) and a pending rebuild
    this.clearedTiles = /* @__PURE__ */ new Set();
    this.clearedDirty = false;
    this.forestGeneration = 0;
    this.input = options;
  }
  attach(ctx) {
    this.ctx = ctx;
    const o = this.input, size = ctx.size;
    const waterDepth = o.waterDepth ?? size * 0.25;
    this.options = {
      ...DEFAULT_OPTIONS2,
      ...o,
      size,
      gridVisible: this.options?.gridVisible ?? true,
      fogDarkenFactor: ctx.fogDarkenFactor,
      waterDepth,
      fogTextureSize: o.fogTextureSize ?? size * 8,
      riverColorShallow: o.riverColorShallow ?? o.waterColorShallow ?? DEFAULT_OPTIONS2.waterColorShallow,
      riverColorDeep: o.riverColorDeep ?? o.waterColorDeep ?? DEFAULT_OPTIONS2.waterColorDeep,
      riverDepth: o.riverDepth ?? waterDepth * 0.6,
      mountainHeight: o.mountainHeight ?? size * 0.6
    };
  }
  //Loads the terrain textures (terrain-layers.json, once - see
  //TerrainLayers.ts), bakes the map's river/lake and coast fields, builds
  //the terrain, trees and grass.
  async load(mapData) {
    this.mapData = mapData;
    this.clearedTiles.clear();
    this.layers ?? (this.layers = await loadTerrainLayers(this.options.texturesBaseUrl, this.ctx.renderer.capabilities.getMaxAnisotropy()));
    this.waterField?.dispose();
    this.waterField = new WaterField(mapData, this.waterFieldOptions());
    this.coastField?.dispose();
    this.coastField = new CoastField(mapData, this.options.size);
    await this.rebuildTerrain();
    await this.rebuildForest();
    this.rebuildGrass();
  }
  update(dt) {
    this.terrain?.update(dt);
    this.grass?.update(dt);
    if (this.clearedDirty) {
      this.clearedDirty = false;
      this.rebuildGrass();
      void this.rebuildForest();
    }
  }
  heightAt(worldX, worldZ) {
    if (!this.terrain) return 0;
    const tile = worldToTile(worldX, worldZ, this.options.size);
    const center = getHexCenter(tile.x, tile.y, this.options.size);
    return this.terrain.terrainHeightAtTileLocal(tile.x, tile.y, worldX - center.x, worldZ - center.y);
  }
  //On water above the highest waves (overlays don't move with them).
  overlayHeight(worldX, worldZ) {
    const tile = worldToTile(worldX, worldZ, this.options.size);
    const type = this.mapData?.data[tile.x]?.[tile.y]?.type;
    const water = type === "sea" /* sea */ || type === "coastal" /* coastal */;
    return this.heightAt(worldX, worldZ) + (water ? this.waterWaveAmplitude : 0);
  }
  setFog(x, y, state) {
    this.terrain?.setFogState(x, y, state);
    this.grass?.setFogState(x, y, state);
    this.forest?.setFogState(x, y, state);
  }
  //Grass and trees rebuild once on the next frame, however many tiles changed.
  setTileCleared(x, y, cleared) {
    const key3 = `${x},${y}`;
    if (this.clearedTiles.has(key3) === cleared) return;
    if (cleared) this.clearedTiles.add(key3);
    else this.clearedTiles.delete(key3);
    this.clearedDirty = true;
  }
  get gridVisible() {
    return this.terrain?.gridVisible ?? this.options?.gridVisible ?? true;
  }
  set gridVisible(value) {
    if (this.options) this.options.gridVisible = value;
    if (this.terrain) this.terrain.gridVisible = value;
  }
  dispose() {
    if (this.terrain) {
      this.ctx.scene.remove(this.terrain);
      this.terrain.dispose();
    }
    if (this.grass) {
      this.ctx.scene.remove(this.grass);
      this.grass.dispose();
    }
    if (this.forest) {
      this.ctx.scene.remove(this.forest);
      this.forest.traverse((o) => o.geometry?.dispose());
    }
    this.waterField?.dispose();
    this.coastField?.dispose();
    this.terrain = this.grass = this.forest = void 0;
  }
  waterFieldOptions() {
    return {
      size: this.options.size,
      riverWidth: this.options.riverWidth,
      riverMaxWidth: this.options.riverMaxWidth,
      riverMeander: this.options.riverCurvature
    };
  }
  //Tears down and recreates the terrain (land/water layers + city models) from
  //the current options against the already-loaded textures/map data. Only needed
  //when the map itself changes (see load()) - everything water/blend-related
  //is a live uniform, see TerrainMesh's own getters/setters, forwarded below
  //(waterWaveAmplitude, beachWidth, etc.)
  async rebuildTerrain() {
    if (this.terrain) {
      this.ctx.scene.remove(this.terrain);
      this.terrain.dispose();
    }
    this.terrain = new TerrainMesh(this.mapData, {
      size: this.options.size,
      texturesBaseUrl: this.options.texturesBaseUrl,
      layers: this.layers,
      waterField: this.waterField,
      coastField: this.coastField,
      layerScale: this.options.layerScale,
      normalStrength: this.options.normalStrength,
      gridVisible: this.options.gridVisible,
      gridColor: this.options.gridColor,
      gridWidth: this.options.gridWidth,
      gridOpacity: this.options.gridOpacity,
      waterColorShallow: this.options.waterColorShallow,
      waterColorDeep: this.options.waterColorDeep,
      waterWaveAmplitude: this.options.waterWaveAmplitude,
      waterWaveFrequency: this.options.waterWaveFrequency,
      waterWaveSpeed: this.options.waterWaveSpeed,
      waterSparkleIntensity: this.options.waterSparkleIntensity,
      waterFresnelIntensity: this.options.waterFresnelIntensity,
      coastalWavesEnabled: this.options.coastalWavesEnabled,
      coastalWaveColor: this.options.coastalWaveColor,
      coastalWaveCount: this.options.coastalWaveCount,
      coastalWaveSpeed: this.options.coastalWaveSpeed,
      coastalWaveWidth: this.options.coastalWaveWidth,
      coastalWaveRange: this.options.coastalWaveRange,
      coastalWaveDistortion: this.options.coastalWaveDistortion,
      coastalWaveOpacity: this.options.coastalWaveOpacity,
      waterDepth: this.options.waterDepth,
      beachWidth: this.options.beachWidth,
      landBlendWidth: this.options.landBlendWidth,
      coastCurvature: this.options.coastCurvature,
      landBlendCurvature: this.options.landBlendCurvature,
      mountainHeight: this.options.mountainHeight,
      riverBankWidth: this.options.riverBankWidth,
      riverEdgeNoise: this.options.riverEdgeNoise,
      riverColorShallow: this.options.riverColorShallow,
      riverColorDeep: this.options.riverColorDeep,
      riverBankColor: this.options.riverBankColor,
      riverFlowSpeed: this.options.riverFlowSpeed,
      riverDepth: this.options.riverDepth,
      fogTexture: this.options.fogTexture,
      fogDarkenFactor: this.options.fogDarkenFactor,
      fogTextureSize: this.options.fogTextureSize
    });
    this.ctx.scene.add(this.terrain);
    this.ctx.reapplyFog();
    this.ctx?.groundChanged();
  }
  //Tears down and recreates the tree instances from the current tree*
  //options. treesPerTile/treeScale are baked into the instanced geometry's
  //instance count/matrices at build time, so - like grass - there's no live
  //uniform for them, only a rebuild. Model files are cached (see
  //helpers/models.ts), so repeated rebuilds don't re-fetch the glTF.
  async rebuildForest() {
    if (!this.mapData) return;
    const generation = ++this.forestGeneration;
    const forest = await createForest(this.mapData, {
      size: this.options.size,
      treesPerTile: this.options.treesPerTile,
      treeModel: this.options.treeModel,
      treeScale: this.options.treeScale,
      fogDarkenFactor: this.options.fogDarkenFactor,
      waterField: this.waterField,
      coastField: this.coastField,
      beachWidth: this.options.beachWidth,
      coastCurvature: this.options.coastCurvature,
      cleared: this.clearedTiles
    }) ?? void 0;
    if (generation !== this.forestGeneration) {
      forest?.traverse((o) => o.geometry?.dispose());
      return;
    }
    if (this.forest) {
      this.ctx.scene.remove(this.forest);
      this.forest.traverse((o) => o.geometry?.dispose());
    }
    this.forest = forest;
    if (this.forest) {
      this.ctx.scene.add(this.forest);
      this.ctx.reapplyFog();
    }
  }
  //Tears down and recreates the grass field from the current grass* options
  //against the already-loaded map data. Grass is purely procedural (no
  //textures/models to load), so this is synchronous and cheap enough to call
  //directly from a live GUI slider (see grassDensity/grassBladeWidth/
  //grassBladeHeight setters below) - a rebuild replaces the whole instanced
  //geometry, there's no partial/incremental update.
  rebuildGrass() {
    if (this.grass) {
      this.ctx.scene.remove(this.grass);
      this.grass.dispose();
      this.grass = void 0;
    }
    if (!this.mapData) return;
    this.grass = createGrassField(this.mapData, {
      size: this.options.size,
      density: this.options.grassDensity,
      bladeWidth: this.options.grassBladeWidth,
      bladeHeight: this.options.grassBladeHeight,
      windStrength: this.options.grassWindStrength,
      windSpeed: this.options.grassWindSpeed,
      fogDarkenFactor: this.options.fogDarkenFactor,
      waterField: this.waterField,
      coastField: this.coastField,
      beachWidth: this.options.beachWidth,
      coastCurvature: this.options.coastCurvature,
      cleared: this.clearedTiles
    }) ?? void 0;
    if (this.grass) {
      this.grass.visible = this.options.grassEnabled;
      this.ctx.scene.add(this.grass);
      this.ctx.reapplyFog();
    }
  }
  //-------------------------------------------------------------------------
  //Water - live shader uniforms forwarded straight through to TerrainMesh,
  //no rebuild needed.
  //-------------------------------------------------------------------------
  get waterWaveAmplitude() {
    return this.terrain?.waterWaveAmplitude ?? this.options.waterWaveAmplitude;
  }
  set waterWaveAmplitude(value) {
    this.options.waterWaveAmplitude = value;
    if (this.terrain) this.terrain.waterWaveAmplitude = value;
  }
  get waterWaveFrequency() {
    return this.terrain?.waterWaveFrequency ?? this.options.waterWaveFrequency;
  }
  set waterWaveFrequency(value) {
    this.options.waterWaveFrequency = value;
    if (this.terrain) this.terrain.waterWaveFrequency = value;
  }
  get waterWaveSpeed() {
    return this.terrain?.waterWaveSpeed ?? this.options.waterWaveSpeed;
  }
  set waterWaveSpeed(value) {
    this.options.waterWaveSpeed = value;
    if (this.terrain) this.terrain.waterWaveSpeed = value;
  }
  get waterSparkleIntensity() {
    return this.terrain?.waterSparkleIntensity ?? this.options.waterSparkleIntensity;
  }
  set waterSparkleIntensity(value) {
    this.options.waterSparkleIntensity = value;
    if (this.terrain) this.terrain.waterSparkleIntensity = value;
  }
  get waterFresnelIntensity() {
    return this.terrain?.waterFresnelIntensity ?? this.options.waterFresnelIntensity;
  }
  set waterFresnelIntensity(value) {
    this.options.waterFresnelIntensity = value;
    if (this.terrain) this.terrain.waterFresnelIntensity = value;
  }
  get waterColorShallow() {
    return this.terrain?.waterColorShallow ?? this.options.waterColorShallow;
  }
  set waterColorShallow(value) {
    this.options.waterColorShallow = value;
    if (this.terrain) this.terrain.waterColorShallow = value;
  }
  get waterColorDeep() {
    return this.terrain?.waterColorDeep ?? this.options.waterColorDeep;
  }
  set waterColorDeep(value) {
    this.options.waterColorDeep = value;
    if (this.terrain) this.terrain.waterColorDeep = value;
  }
  //-------------------------------------------------------------------------
  //Coastal foam waves - all live shader uniforms forwarded to TerrainMesh,
  //no rebuild (the enable flag included: it's a uniform gate in the water
  //fragment shader).
  //-------------------------------------------------------------------------
  get coastalWavesEnabled() {
    return this.terrain?.coastalWavesEnabled ?? this.options.coastalWavesEnabled;
  }
  set coastalWavesEnabled(value) {
    this.options.coastalWavesEnabled = value;
    if (this.terrain) this.terrain.coastalWavesEnabled = value;
  }
  get coastalWaveColor() {
    return this.terrain?.coastalWaveColor ?? this.options.coastalWaveColor;
  }
  set coastalWaveColor(value) {
    this.options.coastalWaveColor = value;
    if (this.terrain) this.terrain.coastalWaveColor = value;
  }
  get coastalWaveCount() {
    return this.terrain?.coastalWaveCount ?? this.options.coastalWaveCount;
  }
  set coastalWaveCount(value) {
    this.options.coastalWaveCount = value;
    if (this.terrain) this.terrain.coastalWaveCount = value;
  }
  get coastalWaveSpeed() {
    return this.terrain?.coastalWaveSpeed ?? this.options.coastalWaveSpeed;
  }
  set coastalWaveSpeed(value) {
    this.options.coastalWaveSpeed = value;
    if (this.terrain) this.terrain.coastalWaveSpeed = value;
  }
  get coastalWaveWidth() {
    return this.terrain?.coastalWaveWidth ?? this.options.coastalWaveWidth;
  }
  set coastalWaveWidth(value) {
    this.options.coastalWaveWidth = value;
    if (this.terrain) this.terrain.coastalWaveWidth = value;
  }
  get coastalWaveRange() {
    return this.terrain?.coastalWaveRange ?? this.options.coastalWaveRange;
  }
  set coastalWaveRange(value) {
    this.options.coastalWaveRange = value;
    if (this.terrain) this.terrain.coastalWaveRange = value;
  }
  get coastalWaveDistortion() {
    return this.terrain?.coastalWaveDistortion ?? this.options.coastalWaveDistortion;
  }
  set coastalWaveDistortion(value) {
    this.options.coastalWaveDistortion = value;
    if (this.terrain) this.terrain.coastalWaveDistortion = value;
  }
  get coastalWaveOpacity() {
    return this.terrain?.coastalWaveOpacity ?? this.options.coastalWaveOpacity;
  }
  set coastalWaveOpacity(value) {
    this.options.coastalWaveOpacity = value;
    if (this.terrain) this.terrain.coastalWaveOpacity = value;
  }
  //-------------------------------------------------------------------------
  //Land/coastal blending + beach height - all live shader uniforms, no rebuild.
  //-------------------------------------------------------------------------
  get landBlendWidth() {
    return this.terrain?.landBlendWidth ?? this.options.landBlendWidth;
  }
  set landBlendWidth(value) {
    this.options.landBlendWidth = value;
    if (this.terrain) this.terrain.landBlendWidth = value;
  }
  get coastCurvature() {
    return this.terrain?.coastCurvature ?? this.options.coastCurvature;
  }
  set coastCurvature(value) {
    this.options.coastCurvature = value;
    if (this.terrain) this.terrain.coastCurvature = value;
  }
  get landBlendCurvature() {
    return this.terrain?.landBlendCurvature ?? this.options.landBlendCurvature;
  }
  set landBlendCurvature(value) {
    this.options.landBlendCurvature = value;
    if (this.terrain) this.terrain.landBlendCurvature = value;
  }
  get mountainHeight() {
    return this.terrain?.mountainHeight ?? this.options.mountainHeight;
  }
  set mountainHeight(value) {
    this.options.mountainHeight = value;
    if (this.terrain) this.terrain.mountainHeight = value;
    this.ctx?.groundChanged();
  }
  get beachWidth() {
    return this.terrain?.beachWidth ?? this.options.beachWidth;
  }
  set beachWidth(value) {
    this.options.beachWidth = value;
    if (this.terrain) this.terrain.beachWidth = value;
  }
  get waterDepth() {
    return this.terrain?.waterDepth ?? this.options.waterDepth;
  }
  set waterDepth(value) {
    this.options.waterDepth = value;
    if (this.terrain) this.terrain.waterDepth = value;
  }
  //-------------------------------------------------------------------------
  //Rivers. Which tiles carry a river is map data (the "river"/"lake"
  //modifiers), not an option. Shape knobs (width, meander) re-bake the
  //water field in place - the shaders pick it up next frame; grass/trees
  //keep their placement until the next rebuild. Everything else is a live
  //uniform forwarded to TerrainMesh.
  //-------------------------------------------------------------------------
  get riverWidth() {
    return this.options.riverWidth;
  }
  set riverWidth(value) {
    this.options.riverWidth = value;
    this.waterField?.update(this.waterFieldOptions());
  }
  get riverMaxWidth() {
    return this.options.riverMaxWidth;
  }
  set riverMaxWidth(value) {
    this.options.riverMaxWidth = value;
    this.waterField?.update(this.waterFieldOptions());
  }
  get riverCurvature() {
    return this.options.riverCurvature;
  }
  set riverCurvature(value) {
    this.options.riverCurvature = value;
    this.waterField?.update(this.waterFieldOptions());
  }
  get riverEdgeNoise() {
    return this.terrain?.riverEdgeNoise ?? this.options.riverEdgeNoise;
  }
  set riverEdgeNoise(value) {
    this.options.riverEdgeNoise = value;
    if (this.terrain) this.terrain.riverEdgeNoise = value;
  }
  get riverBankWidth() {
    return this.terrain?.riverBankWidth ?? this.options.riverBankWidth;
  }
  set riverBankWidth(value) {
    this.options.riverBankWidth = value;
    if (this.terrain) this.terrain.riverBankWidth = value;
  }
  get riverColorShallow() {
    return this.terrain?.riverColorShallow ?? this.options.riverColorShallow;
  }
  set riverColorShallow(value) {
    this.options.riverColorShallow = value;
    if (this.terrain) this.terrain.riverColorShallow = value;
  }
  get riverColorDeep() {
    return this.terrain?.riverColorDeep ?? this.options.riverColorDeep;
  }
  set riverColorDeep(value) {
    this.options.riverColorDeep = value;
    if (this.terrain) this.terrain.riverColorDeep = value;
  }
  get riverBankColor() {
    return this.terrain?.riverBankColor ?? this.options.riverBankColor;
  }
  set riverBankColor(value) {
    this.options.riverBankColor = value;
    if (this.terrain) this.terrain.riverBankColor = value;
  }
  get riverFlowSpeed() {
    return this.terrain?.riverFlowSpeed ?? this.options.riverFlowSpeed;
  }
  set riverFlowSpeed(value) {
    this.options.riverFlowSpeed = value;
    if (this.terrain) this.terrain.riverFlowSpeed = value;
  }
  get riverDepth() {
    return this.terrain?.riverDepth ?? this.options.riverDepth;
  }
  set riverDepth(value) {
    this.options.riverDepth = value;
    if (this.terrain) this.terrain.riverDepth = value;
  }
  //-------------------------------------------------------------------------
  //Tree density/size - baked into the instanced geometry at build time (like
  //grass), so both rebuild the forest rather than touching a uniform.
  //-------------------------------------------------------------------------
  get treesPerTile() {
    return this.options.treesPerTile;
  }
  set treesPerTile(value) {
    this.options.treesPerTile = value;
    void this.rebuildForest();
  }
  get treeScale() {
    return this.options.treeScale;
  }
  set treeScale(value) {
    this.options.treeScale = value;
    void this.rebuildForest();
  }
  //Toggling visibility just flips the mesh's own `visible` flag (grass is
  //still generated even when disabled) - the terrain's own grass texture
  //keeps rendering underneath either way, so disabling this is purely
  //"remove the blade overlay", not "regenerate as flat grass".
  get grassVisible() {
    return this.grass?.visible ?? this.options.grassEnabled;
  }
  set grassVisible(value) {
    this.options.grassEnabled = value;
    if (this.grass) this.grass.visible = value;
  }
  //Wind uniforms are cheap to update live - no rebuild needed.
  get grassWindStrength() {
    return this.grass?.windStrength ?? this.options.grassWindStrength;
  }
  set grassWindStrength(value) {
    this.options.grassWindStrength = value;
    if (this.grass) this.grass.windStrength = value;
  }
  get grassWindSpeed() {
    return this.grass?.windSpeed ?? this.options.grassWindSpeed;
  }
  set grassWindSpeed(value) {
    this.options.grassWindSpeed = value;
    if (this.grass) this.grass.windSpeed = value;
  }
  //Blade count/size is baked into the instanced geometry at build time, so
  //changing any of these rebuilds the whole grass field (see rebuildGrass()).
  get grassDensity() {
    return this.options.grassDensity;
  }
  set grassDensity(value) {
    this.options.grassDensity = value;
    this.rebuildGrass();
  }
  get grassBladeWidth() {
    return this.options.grassBladeWidth;
  }
  set grassBladeWidth(value) {
    this.options.grassBladeWidth = value;
    this.rebuildGrass();
  }
  get grassBladeHeight() {
    return this.options.grassBladeHeight;
  }
  set grassBladeHeight(value) {
    this.options.grassBladeHeight = value;
    this.rebuildGrass();
  }
};
export {
  Building,
  City,
  EventEmitter,
  FogOfWar,
  FogState,
  GameEngine,
  HEXPolygon,
  HexMap,
  Land,
  LandColor,
  LandPriority,
  NEIGHBOR_DIRECTIONS,
  PathFinder,
  Player,
  ResourceRules,
  Storage,
  TexturedStyle,
  Unit,
  UnitActions,
  addYields,
  deposit,
  getHexCenter,
  getNeighborCoords,
  getNeighbors,
  hexDistance,
  missingFor,
  registerUnitBehavior,
  routeTurns,
  spend,
  stepTurns
};
//# sourceMappingURL=textured.mjs.map